/**
 * ⭐ DANCER PROFILE COMPONENT - STAR RANKING LOGIC COMPLIANT
 * 
 * 🔄 Star Ranking Updates: Only when competitions are uploaded or results become >365 days old (~15x/year)
 * 📊 Profile Overview Data Sources:
 *   • Active Points: From Star Ranking (updates ~15x/year)
 *   • Current Rank: From Star Ranking (updates ~15x/year) 
 *   • Current Days: Updates daily (days holding current rank)
 *   • Best Rank: Historical best ever achieved
 *   • Best Days: Updates daily (days at best rank without being overtaken)
 * 
 * 🚫 Invalid Results: Completely excluded from ALL calculations (Star Rank, Eternal Cup, Profile Overview)
 * 🧓 Age Group Transitions: For verified dancers, Best Rank/Days calculated only up to Dec 31 of previous year
 * 📅 Daily Updates: Only Current Days and Best Days require daily recalculation
 * 🏆 Historical Categories: Shows all categories ever participated in, marked as "Historical" if no active points
 */

import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  doc,
  updateDoc,
  arrayRemove,
  setDoc,
  getDoc,
  deleteDoc,
  writeBatch
} from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { db, storage } from "./firebase";
import { useAdmin } from "./hooks/useAdmin";
import { useDancerAuth } from "./hooks/useDancerAuth";
import { useProfileClaimed } from "./hooks/useProfileClaimed";
import SponsorshipDisplay from "./components/SponsorshipDisplay";
import { ClickableStudioName } from "./utils/ClickableNames";
import { shouldAutoExcludeResult, didDancerChangeAgeCategoryThisYear, calculateAgeCategory, calculateAgeCategoryFromAge, shouldResultCountForRankings, getDancerCurrentAgeCategory, calculateAgeCategoryForYear, shouldResultCountForRankingsInYear } from "./utils/ageCategoryUtils";
import ClaimProfile from "./ClaimProfile";
import SimpleProfileEditor from "./SimpleProfileEditor";
import LoginPrompt from "./LoginPrompt";
import { 
  getCachedDancerRankings,
  saveCachedDancerRankings
} from "./utils/rankingCalculations";
import {
  getCachedDancerRankings as getGlobalCachedDancerRankings,
  getCacheMetadata,
  shouldRefreshCache
} from "./utils/globalCache";
import { getFunctions, httpsCallable } from "firebase/functions";
import ShareResultModal from "./components/ShareResultModal";
import FollowButton from "./components/FollowButton";
import PredictionPoints from "./components/PredictionPoints";
import "./DancerProfile.css";

// Helper function to normalize Facebook URLs
const normalizeFacebookUrl = (facebookValue) => {
  if (!facebookValue) return '';
  
  // If it's already a complete URL, return as is
  if (facebookValue.startsWith('http')) {
    return facebookValue;
  }
  
  // Remove any existing facebook.com or www.facebook.com prefixes
  let cleanValue = facebookValue
    .replace(/^(https?:\/\/)?(www\.)?facebook\.com\//, '')
    .replace(/^facebook\.com\//, '');
  
  // Return properly formatted URL
  return `https://www.facebook.com/${cleanValue}`;
};

export default function DancerProfile() {
  const { name: encodedName } = useParams();
  const dancerName = decodeURIComponent(encodedName);
  const name = decodeURIComponent(encodedName || ''); // Decode URL-encoded name
  
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();
  const { user } = useDancerAuth();
  const { isProfileClaimed, claimedBy, loading: claimLoading } = useProfileClaimed(name);
  
  const [dancerData, setDancerData] = useState(null);
  const [cachedRankings, setCachedRankings] = useState(null); // Store cached rankings for fast overview display
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedDiscipline, setSelectedDiscipline] = useState("");
  const [selectedAgeCategory, setSelectedAgeCategory] = useState("");
  const [selectedDance, setSelectedDance] = useState("");
  const [compNameMap, setCompNameMap] = useState({});
  const [profileData, setProfileData] = useState(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const [profilePictureLoading, setProfilePictureLoading] = useState(false);
  // 🚀 PERFORMANCE: Add studio loading state
  const [studioLoading, setStudioLoading] = useState(true);
  const [ageCategory, setAgeCategory] = useState(null);
  const [isSponsored, setIsSponsored] = useState(false);
  const [sponsorshipLoading, setSponsorshipLoading] = useState(true);
  const [transferHistory, setTransferHistory] = useState([]);
  const [currentStudio, setCurrentStudio] = useState(null);
  const [disciplineStudios, setDisciplineStudios] = useState({});
  
  // 🎂 Manual Year of Birth System
  const [manualYearOfBirth, setManualYearOfBirth] = useState(null);
  const [editingYearOfBirth, setEditingYearOfBirth] = useState(false);
  const [yearOfBirthLoading, setYearOfBirthLoading] = useState(false);
  
  // 🔓 Unclaim Profile System (Admin only)
  const [unclaimConfirmStep, setUnclaimConfirmStep] = useState(0); // 0 = not started, 1 = first click, 2 = confirmed
  const [unclaimLoading, setUnclaimLoading] = useState(false);

  // ✏️ Rename Dancer (Admin only)
  const [editingDancerName, setEditingDancerName] = useState(false);
  const [newDancerNameInput, setNewDancerNameInput] = useState('');
  const [renameLoading, setRenameLoading] = useState(false);
  const [renameStatus, setRenameStatus] = useState('');
  
  // 📊 Best Days Visibility Setting
  const [hideBestDaysFromPublic, setHideBestDaysFromPublic] = useState(false);
  const [bestDaysSettingLoaded, setBestDaysSettingLoaded] = useState(false);

  // 👁️ Visitor Count
  const [visitorCount, setVisitorCount] = useState(null);
  
  // 🏆 Achievement Badges
  const [achievementBadges, setAchievementBadges] = useState([]);
  const [activeBadgeTooltip, setActiveBadgeTooltip] = useState(null);

  // 🏆 Eternal Cup Rankings for this dancer (current year)
  const [eternalCupRankings, setEternalCupRankings] = useState(null);
  const [eternalCupLoading, setEternalCupLoading] = useState(false);
  const [showEternalCupTable, setShowEternalCupTable] = useState(false);
  const [ecSelectedYear, setEcSelectedYear] = useState(new Date().getFullYear());
  const [ecSelectedCategory, setEcSelectedCategory] = useState("");
  const [ecYearRankings, setEcYearRankings] = useState(null);
  const [ecYearRankingsLoading, setEcYearRankingsLoading] = useState(false);
  
  // 🔧 Admin Section Toggle (default hidden)
  const [showAdminControls, setShowAdminControls] = useState(false);
  
  // 🏆 Competition Details Modal
  const [competitionModal, setCompetitionModal] = useState({ isOpen: false, competitionId: null, competitionData: null, loading: false });
  const [cmHoveredJudge, setCmHoveredJudge] = useState(null);
  const [cmSelectedJudges, setCmSelectedJudges] = useState(new Set());
  const toggleCmJudge = (jId) => setCmSelectedJudges(prev => { const s = new Set(prev); s.has(jId) ? s.delete(jId) : s.add(jId); return s; });

  // 📤 Share Result Modal
  const [shareModalOpen, setShareModalOpen] = useState(false);
  
  // Track if auto-exclusions have been calculated to prevent redundant calls
  const autoExclusionsCalculated = useRef(false);
  
  // 🚀 PERFORMANCE: Shared competitions data to avoid duplicate queries
  const sharedCompetitionsData = useRef(null);
  
  // 🚀 PERFORMANCE: Store loaded profile data immediately so loadTransferHistory can use it without re-reading
  const loadedProfileDataRef = useRef(null);

  // 🎯 Priority Logic: Get effective birth year
  // Priority: 1. Claimed profile dateOfBirth, 2. Admin manual yearOfBirth, 3. null
  const getEffectiveBirthYear = () => {
    // Highest priority: Claimed profile dateOfBirth
    if (profileData?.dateOfBirth) {
      try {
        const birthDate = new Date(profileData.dateOfBirth);
        const birthYear = birthDate.getFullYear();
        return birthYear;
      } catch (error) {
        console.warn('Error parsing claimed dateOfBirth:', error);
      }
    }
    
    // Fallback: Admin manual yearOfBirth
    if (manualYearOfBirth) {
      return manualYearOfBirth;
    }
    
    return null;
  };

  // 🎂 Calculate age category from birth date
  const calculateAgeCategory = (birthDate) => {
    if (!birthDate) {
      return null;
    }
    
    try {
      let day, month, year;
      
      // Handle both dd/mm/yyyy and yyyy-mm-dd formats
      if (birthDate.includes('-')) {
        // yyyy-mm-dd format (from database)
        const parts = birthDate.split('-');
        if (parts.length !== 3) {
          return null;
        }
        [year, month, day] = parts.map(num => parseInt(num));
      } else if (birthDate.includes('/')) {
        // dd/mm/yyyy format (from form input)
        const parts = birthDate.split('/');
        if (parts.length !== 3) {
          return null;
        }
        [day, month, year] = parts.map(num => parseInt(num));
      } else {
        return null;
      }
      
      if (isNaN(day) || isNaN(month) || isNaN(year)) {
        return null;
      }
      
      const birthYear = year;
      const currentYear = new Date().getFullYear();
      
      // Calculate age based on year only (as of January 1st of current year)
      const age = currentYear - birthYear;
      
      if (age >= 7 && age <= 9) return 'Juvenile I';
      if (age >= 10 && age <= 11) return 'Juvenile II';
      if (age >= 12 && age <= 13) return 'Junior I';
      if (age >= 14 && age <= 15) return 'Junior II';
      if (age >= 16 && age <= 18) return 'Youth';
      if (age >= 19) return 'Adult';
      
      // For ages below 7, return null (no category)
      return null;
    } catch (error) {
      return null;
    }
  };

  // 🎯 Calculate age category using priority system
  const calculateAgeCategoryFromPriority = () => {
    const effectiveBirthYear = getEffectiveBirthYear();
    if (!effectiveBirthYear) {
      return null;
    }
    
    const currentYear = new Date().getFullYear();
    const age = currentYear - effectiveBirthYear;
    
    if (age >= 7 && age <= 9) return 'Juvenile I';
    if (age >= 10 && age <= 11) return 'Juvenile II';
    if (age >= 12 && age <= 13) return 'Junior I';
    if (age >= 14 && age <= 15) return 'Junior II';
    if (age >= 16 && age <= 18) return 'Youth';
    if (age >= 19) return 'Adult';
    
    return null;
  };

  // 🗓️ Friendly date formatter
  function formatDate(ts) {
    if (!ts) return "Unknown Date";
    
    const dateObj =
      ts instanceof Timestamp
        ? ts.toDate()
        : typeof ts === "string"
        ? new Date(ts)
        : ts;

    if (!dateObj || isNaN(dateObj.getTime())) return "Unknown Date";

    return dateObj.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  }

  // Check sponsorship status
  // 🎂 Cache for birth dates - loaded once per profile view
  const birthDateCache = useRef(null);
  
  // Pre-load all birth dates once (claimed profiles + manual years)
  const preloadBirthDates = async () => {
    if (birthDateCache.current) {
      return birthDateCache.current; // Already loaded
    }
    
    const birthDates = new Map();
    
    try {
      // Load all birth dates from dancerProfiles (publicly readable)
      const profilesQuery = query(collection(db, 'dancerProfiles'));
      const profilesSnapshot = await getDocs(profilesQuery);
      
      profilesSnapshot.forEach(doc => {
        const data = doc.data();
        const dancerName = doc.id;
        
        // Priority 1: dateOfBirth from claimed profile (most accurate)
        if (data.dateOfBirth) {
          birthDates.set(dancerName, data.dateOfBirth);
        }
        // Priority 2: manualYearOfBirth (less accurate, year only)
        else if (data.manualYearOfBirth && !birthDates.has(dancerName)) {
          const manualBirthDate = new Date(data.manualYearOfBirth, 0, 1);
          birthDates.set(dancerName, manualBirthDate);
        }
      });
      
      // Fallback: Try to load from profileClaims (for backwards compatibility)
      // This may fail for non-authenticated users, but we'll catch the error
      try {
        const claimsQuery = query(
          collection(db, 'profileClaims'),
          where('status', '==', 'approved')
        );
        const claimsSnapshot = await getDocs(claimsQuery);
        
        claimsSnapshot.forEach(doc => {
          const data = doc.data();
          if (data.dancerName && data.birthDate && !birthDates.has(data.dancerName)) {
            birthDates.set(data.dancerName, data.birthDate);
          }
        });
      } catch (claimsError) {
        console.log('ℹ️ Could not load from profileClaims (may require authentication):', claimsError.message);
      }
      
      console.log(`🎂 Pre-loaded ${birthDates.size} birth dates (claimed + manual)`);
      birthDateCache.current = birthDates;
      return birthDates;
      
    } catch (error) {
      console.error('Error preloading birth dates:', error);
      birthDateCache.current = new Map();
      return birthDateCache.current;
    }
  };
  
  // Helper function to get dancer's birth date from cache
  const getDancerBirthDate = (dancerName, birthDatesMap) => {
    return birthDatesMap.get(dancerName) || null;
  };

  const checkSponsorshipStatus = async (dancerName) => {
    setSponsorshipLoading(true);
    try {
      const sponsorshipDoc = await getDoc(doc(db, "sponsorships", dancerName));
      if (sponsorshipDoc.exists() && sponsorshipDoc.data().sponsor === 'prima') {
        setIsSponsored(true);
      } else {
        setIsSponsored(false);
      }
    } catch (error) {
      console.error('Error checking sponsorship status:', error);
      setIsSponsored(false);
    }
    setSponsorshipLoading(false);
  };

  // 🎭 Format category with dance information
  function formatCategoryDisplay(category, dance, discipline) {
    if (!category) return "-";
    
    // For Latin discipline, show dance in parentheses
    if (discipline === "Latin" && dance) {
      return `${category} (${dance})`;
    }
    
    // For other disciplines, show discipline in parentheses if it's not Latin
    if (discipline && discipline !== "Latin") {
      return `${category} (${discipline})`;
    }
    
    // Just return category if no additional info
    return category;
  }

  // 🏆 Format place with total participants and withdrawal status
  function formatPlaceDisplay(place, participants, withdrawn = false) {
    if (!place) return "-";
    
  const basePlace = (!participants || participants === 0) ? place.toString() : `${place} (of ${participants})`;
    
    if (withdrawn) {
      // Return JSX for styled "W" with tooltip
      return (
        <span>
          {basePlace}{" "}
          <span 
            style={{ 
              color: "#d32f2f", 
              fontWeight: "bold",
              cursor: "help"
            }}
            title="Withdrawn - Dancer did not finish the competition"
          >
            W
          </span>
        </span>
      );
    }
    
    return basePlace;
  }

  // 🔗 Handle competition name click to open modal with competition details
  const handleCompetitionClick = async (competitionId) => {
    setCompetitionModal({ isOpen: true, competitionId, competitionData: null, loading: true });
    
    try {
      // Try to load from old structure first
      const oldDocRef = doc(db, "competitions", competitionId);
      const oldDoc = await getDoc(oldDocRef);
      
      let competition = null;
      
      if (oldDoc.exists()) {
        competition = oldDoc.data();
      } else {
        // Try to find in new structure
        const disciplinesSnapshot = await getDocs(collection(db, "disciplines"));
        
        for (const disciplineDoc of disciplinesSnapshot.docs) {
          const categoriesSnapshot = await getDocs(collection(db, "disciplines", disciplineDoc.id, "categories"));
          
          for (const categoryDoc of categoriesSnapshot.docs) {
            // Check direct competitions
            try {
              const compDoc = await getDoc(doc(db, "disciplines", disciplineDoc.id, "categories", categoryDoc.id, "competitions", competitionId));
              if (compDoc.exists()) {
                competition = compDoc.data();
                break;
              }
            } catch (error) {
              // Continue searching
            }
            
            // Check dance-specific competitions
            try {
              const dancesSnapshot = await getDocs(collection(db, "disciplines", disciplineDoc.id, "categories", categoryDoc.id, "dances"));
              for (const danceDoc of dancesSnapshot.docs) {
                try {
                  const compDoc = await getDoc(doc(db, "disciplines", disciplineDoc.id, "categories", categoryDoc.id, "dances", danceDoc.id, "competitions", competitionId));
                  if (compDoc.exists()) {
                    competition = compDoc.data();
                    break;
                  }
                } catch (error) {
                  // Continue searching
                }
              }
              if (competition) break;
            } catch (error) {
              // Continue searching
            }
          }
          if (competition) break;
        }
      }
      
      if (competition) {
        // Merge roundData (full judge marks) into results for Contaste-uploaded competitions
        if (competition.roundData && competition.results) {
          const roundDataByName = {};
          for (const rd of competition.roundData) {
            if (rd.dancerName) roundDataByName[rd.dancerName] = rd;
          }
          competition.results = competition.results.map(result => ({
            ...result,
            number: roundDataByName[result.dancerName]?.number ?? result.number ?? null,
            highestRound: roundDataByName[result.dancerName]?.highestRound ?? result.highestRound ?? null,
            rounds: roundDataByName[result.dancerName]?.rounds ?? result.rounds ?? [],
          }));
        }

        // Sort results by place
        if (competition.results) {
          competition.results.sort((a, b) => a.place - b.place);
        }

        setCompetitionModal({ isOpen: true, competitionId, competitionData: competition, loading: false });
      } else {
        setCompetitionModal({ isOpen: true, competitionId, competitionData: null, loading: false, error: "Competition not found" });
      }
    } catch (error) {
      console.error("Error loading competition:", error);
      setCompetitionModal({ isOpen: true, competitionId, competitionData: null, loading: false, error: "Failed to load competition" });
    }
  };
  
  // Calculate points using the same formula as in UploadCompetition
  const calculatePointsForPlace = (place, participants) => {
    const raw = 321 * Math.exp(-0.077 * place) * (0.39 * Math.log(participants) + 0.13);
    return Math.round(raw);
  };
  
  // Close competition modal
  const closeCompetitionModal = () => {
    setCompetitionModal({ isOpen: false, competitionId: null, competitionData: null, loading: false });
    setCmSelectedJudges(new Set());
    setCmHoveredJudge(null);
  };

  // PERFORMANCE OPTIMIZATION: Calculate auto-exclusions asynchronously
  const calculateAutoExclusionsAsync = useCallback(async (results, dancerName) => {
    try {
      console.log(`🔍 Starting auto-exclusion check for ${dancerName}...`);
      
      // Check if any results actually need auto-exclusion check
      const needsCheck = results.some(result => result.needsAutoExclusionCheck);
      if (!needsCheck) {
        console.log(`✅ No auto-exclusion checks needed for ${dancerName}`);
        return;
      }
      
      const updated = await Promise.all(results.map(async (result) => {
        if (result.needsAutoExclusionCheck) {
          try {
            // Convert timestamp to date for auto-exclusion check
            const resultDate = result.timestamp instanceof Timestamp 
              ? result.timestamp.toDate() 
              : new Date(result.timestamp);
            
            // Use category field from competition result
            const autoExclusionResult = await shouldAutoExcludeResult(
              dancerName, 
              resultDate, 
              result.category // Use the category field from the result
            );
            
            const { shouldExclude, isHistorical, excludeFromBestMetrics, competitionYear } = autoExclusionResult;
            
            if (shouldExclude) {
              console.log(`⚠️ Auto-excluding result for ${dancerName} in ${result.category} from ${resultDate.toDateString()}`);
              console.log(`   Historical: ${isHistorical}, Exclude from Best metrics: ${excludeFromBestMetrics}`);
            }
            
            // CRITICAL: Invalid results are excluded from EVERYTHING
            const isInvalid = result.isInvalid === true || result.invalid === true;
            
            // Auto-exclusion overrides manual exclusion
            // Invalid results override everything
            const finalExcluded = isInvalid ? true : (shouldExclude ? true : result.excluded);
            const finalExclusionReason = isInvalid ? "invalid" : (shouldExclude 
              ? (isHistorical ? "historical" : "Age category changed")
              : result.exclusionReason);
            
            return {
              ...result,
              autoExcluded: shouldExclude,
              excluded: finalExcluded,
              isInvalid: isInvalid,
              exclusionReason: finalExclusionReason,
              isHistorical: isHistorical,
              excludeFromBestMetrics: excludeFromBestMetrics,
              competitionYear: competitionYear,
              countsForRanking: result.isWithin365Days && !finalExcluded && !result.isInvalid,
              countsForBestMetrics: !excludeFromBestMetrics && !result.isInvalid,
              needsAutoExclusionCheck: false
            };
          } catch (error) {
            console.warn(`⚠️ Error checking auto-exclusion for result:`, error);
            return {
              ...result,
              needsAutoExclusionCheck: false
            };
          }
        }
        return result;
      }));
      
      console.log(`✅ Auto-exclusion check completed for ${dancerName}`);
      
      // Update dancer data with calculated exclusions
      setDancerData(prevData => ({
        ...prevData,
        results: updated
      }));

      // 🔄 Recalculate rankings after auto-exclusions are applied
      setTimeout(async () => {
        try {
          // Recalculate discipline overview with updated exclusions
          const groupedResults = {};
          updated.forEach(result => {
            const key = result.discipline === "Latin" 
              ? `${result.discipline}-${result.category}-${result.dance}`
              : `${result.discipline}-${result.category}`;
            
            if (!groupedResults[key]) {
              groupedResults[key] = {
                discipline: result.discipline,
                category: result.category,
                dance: result.dance,
                results: []
              };
            }
            groupedResults[key].results.push(result);
          });

          // Calculate points for each group with updated exclusions
          const disciplineOverview = Object.values(groupedResults).map(group => {
            const rankingGroupResults = group.results.filter(r => r.countsForRanking);
            const sortedResults = rankingGroupResults.sort((a, b) => b.points - a.points);
            const top6Results = sortedResults.slice(0, 6);
            const totalPoints = top6Results.reduce((sum, r) => sum + r.points, 0);
            
            return {
              discipline: group.discipline,
              category: group.category,
              dance: group.dance,
              totalPoints,
              competitionCount: group.results.length,
              rankingCompetitionCount: rankingGroupResults.length,
              hasResults: group.results.length > 0
            };
          });

          // Recalculate real rankings with updated exclusions
          const disciplineOverviewWithRankings = await calculateRealRankings(disciplineOverview, dancerName, sharedCompetitionsData.current);
          
          // Update the state with recalculated rankings
          setDancerData(prevData => ({
            ...prevData,
            disciplineOverview: disciplineOverviewWithRankings
          }));

          console.log(`🔄 Rankings recalculated after auto-exclusions for ${dancerName}`);
        } catch (error) {
          console.error('❌ Error recalculating rankings after auto-exclusions:', error);
        }
      }, 50); // Small delay to ensure state update is complete
    } catch (error) {
      console.error('❌ Error calculating auto-exclusions:', error);
    }
  }, []);

  // 🛠️ Toggle result exclusion (admin only)
  const toggleResultExclusion = async (resultToToggle, currentlyExcluded) => {
    if (!isAdmin) return;
    
    try {
      console.log(`🔄 Toggling exclusion for result:`, {
        compId: resultToToggle.compId,
        discipline: resultToToggle.discipline,
        category: resultToToggle.category,
        dance: resultToToggle.dance,
        currentlyExcluded
      });
      
      // Update the local state immediately for better UX
      setDancerData(prev => {
        const updatedResults = prev.results.map(result => {
          // Match by all identifying fields to ensure we get the exact result
          if (result.compId === resultToToggle.compId &&
              result.discipline === resultToToggle.discipline &&
              result.category === resultToToggle.category &&
              result.dance === resultToToggle.dance) {
            const newExcludedState = !currentlyExcluded;
            return {
              ...result, 
              excluded: newExcludedState,
              countsForRanking: result.isWithin365Days && !newExcludedState && !result.autoExcluded && !result.isInvalid
            };
          }
          return result;
        });

        // Recalculate disciplineOverview with updated exclusions
        const groupedResults = {};
        updatedResults.forEach(result => {
          const key = `${result.discipline}-${result.category}${result.dance ? `-${result.dance}` : ''}`;
          if (!groupedResults[key]) {
            groupedResults[key] = {
              discipline: result.discipline,
              category: result.category,
              dance: result.dance,
              results: []
            };
          }
          groupedResults[key].results.push(result);
        });

        // Calculate points for each group and create discipline overview
        const updatedDisciplineOverview = Object.values(groupedResults).map(group => {
          // Only use results from last 365 days for ranking calculation
          const rankingGroupResults = group.results.filter(r => r.countsForRanking);
          const sortedResults = rankingGroupResults.sort((a, b) => b.points - a.points);
          const top6Results = sortedResults.slice(0, 6);
          const totalPoints = top6Results.reduce((sum, r) => sum + r.points, 0);
          
          // Preserve ranking data if available, but mark as needing recalculation if totalPoints changed significantly
          const existingOverview = prev.disciplineOverview?.find(d => 
            d.discipline === group.discipline && 
            d.category === group.category && 
            d.dance === group.dance
          );
          
          // If totalPoints changed significantly, rankings need recalculation
          const pointsChanged = existingOverview && Math.abs(existingOverview.totalPoints - totalPoints) > 0;
          
          return {
            discipline: group.discipline,
            category: group.category,
            dance: group.dance,
            totalPoints, // Points from last 365 days only
            competitionCount: group.results.length, // Total competitions (all time)
            rankingCompetitionCount: rankingGroupResults.length, // Competitions counting for ranking
            hasResults: group.results.length > 0,
            currentRank: pointsChanged ? "-" : (existingOverview?.currentRank || null),
            bestRank: existingOverview?.bestRank || null,
            daysHeld: pointsChanged ? "-" : (existingOverview?.daysHeld || 0),
            bestDaysHeld: existingOverview?.bestDaysHeld || 0,
            hasCurrentRank: totalPoints > 0
          };
        }).filter(overview => overview.hasResults)
        .sort((a, b) => {
          // Sort by age category first (highest to lowest)
          const ageCategoryOrder = ['Adult', 'Youth', 'Junior II', 'Junior I', 'Juvenile II', 'Juvenile I'];
          const aIndex = ageCategoryOrder.indexOf(a.category);
          const bIndex = ageCategoryOrder.indexOf(b.category);
          
          if (aIndex !== bIndex) {
            if (aIndex === -1) return 1;
            if (bIndex === -1) return -1;
            return aIndex - bIndex;
          }
          
          if (a.category === b.category && a.discipline === 'Latin' && b.discipline === 'Latin') {
            const latinDanceOrder = ['Samba', 'ChaChaCha', 'Rumba', 'Pasodoble', 'Jive'];
            const aDanceIndex = latinDanceOrder.indexOf(a.dance);
            const bDanceIndex = latinDanceOrder.indexOf(b.dance);
            
            if (aDanceIndex !== bDanceIndex) {
              if (aDanceIndex === -1) return 1;
              if (bDanceIndex === -1) return -1;
              return aDanceIndex - bDanceIndex;
            }
          }
          
          return 0;
        });

        return {
          ...prev,
          results: updatedResults,
          disciplineOverview: updatedDisciplineOverview
        };
      });
      
      // Update the database - find the competition document by ID
      
      // Try direct document reference first
      const compDocRef = doc(db, 'competitions', resultToToggle.compId);
      const compDoc = await getDoc(compDocRef);
      
      if (compDoc.exists()) {
        const compData = compDoc.data();
        
        console.log(`🔍 Found competition document with ${compData.results?.length || 0} results`);
        console.log(`🔍 Looking for result with:`, {
          dancerName: name,
          discipline: resultToToggle.discipline,
          category: resultToToggle.category,
          dance: resultToToggle.dance
        });
        
        // 🐛 DEBUG: Show all results for this dancer to see what's available
        const dancerResults = compData.results.filter(r => r.dancerName === name);
        console.log(`👤 Found ${dancerResults.length} results for ${name} in this competition:`, 
          dancerResults.map(r => ({
            discipline: r.discipline,
            category: r.category,
            dance: r.dance,
            excluded: r.excluded,
            invalid: r.invalid
          }))
        );
        
        // Update the specific result for this dancer by matching all result properties
        const updatedResults = compData.results.map(result => {
          // Since the database stores discipline/category/dance as undefined,
          // we need to match only by dancer name for this specific competition
          const nameMatch = result.dancerName === name;
          
          // For competitions with multiple results per dancer, we need additional logic
          // But for now, match by name since this competition seems to have 1 result per dancer
          const isTargetResult = nameMatch;
          
          if (result.dancerName === name) {
            console.log(`🔍 Checking result:`);
            console.log(`   Result:`, result.dancerName, '|', result.discipline, '|', result.category, '|', result.dance);
            console.log(`   Target:`, name, '|', resultToToggle.discipline, '|', resultToToggle.category, '|', resultToToggle.dance);
            console.log(`   Matches:`, { nameMatch });
            console.log(`   isTargetResult:`, isTargetResult);
            console.log(`   🚨 NOTE: Using name-only matching due to undefined discipline/category/dance in database`);
          }
          
          if (isTargetResult) {
            console.log(`✅ Found matching result to update:`, result);
          }
          
          if (isTargetResult) {
            return {
              ...result,
              excluded: !currentlyExcluded
            };
          }
          return result;
        });
        
        const matchCount = updatedResults.filter(r => 
          r.dancerName === name && 
          r.discipline === resultToToggle.discipline &&
          r.category === resultToToggle.category &&
          (resultToToggle.dance ? r.dance === resultToToggle.dance : !r.dance)
        ).length;
        console.log(`📊 Found ${matchCount} matching results to update`);
        
        // Save back to database
        await updateDoc(compDocRef, {
          results: updatedResults
        });
        
        console.log(`💾 Exclusion status saved to database for ${name} in competition ${resultToToggle.compId}`);
        
        // 🔍 VERIFICATION: Read back the data to confirm it was saved correctly
        const verificationDoc = await getDoc(compDocRef);
        if (verificationDoc.exists()) {
          const verificationData = verificationDoc.data();
          const savedResult = verificationData.results.find(r => 
            r.dancerName === name
            // NOTE: Using name-only matching since discipline/category/dance are undefined in database
          );
          if (savedResult) {
            console.log(`✅ VERIFICATION: Database now contains excluded=${savedResult.excluded} for this result`);
          } else {
            console.error(`❌ VERIFICATION FAILED: Could not find saved result in database`);
          }
        }
      } else {
        // Fallback: try querying by competitionId field
        const compQuery = query(
          collection(db, 'competitions'), 
          where('competitionId', '==', resultToToggle.compId)
        );
        
        const compSnapshot = await getDocs(compQuery);
        if (!compSnapshot.empty) {
          const compDoc = compSnapshot.docs[0];
          const compData = compDoc.data();
          
          console.log(`🔍 Found competition via query with ${compData.results?.length || 0} results`);
          
          // Update the specific result for this dancer by matching all result properties
          const updatedResults = compData.results.map(result => {
            // Match by dancer name, discipline, category, and dance (if applicable)
            const isTargetResult = result.dancerName === name && 
                                  result.discipline === resultToToggle.discipline &&
                                  result.category === resultToToggle.category &&
                                  (resultToToggle.dance ? result.dance === resultToToggle.dance : !result.dance);
            
            if (isTargetResult) {
              console.log(`✅ Found matching result to update via query:`, result);
            }
            
            if (isTargetResult) {
              return {
                ...result,
                excluded: !currentlyExcluded
              };
            }
            return result;
          });
          
          // Save back to database
          await updateDoc(compDoc.ref, {
            results: updatedResults
          });
          
          console.log(`💾 Exclusion status saved to database for ${name} in competition ${resultToToggle.compId} (via query)`);
        } else {
          console.error('Competition not found in database:', resultToToggle.compId);
        }
      }

      // Force a complete refresh of profile data to ensure proper age category validation
      console.log('🔄 Triggering profile refresh to ensure proper validation...');
      setTimeout(() => {
        loadProfileData();
      }, 100);

      // Note: Rankings will be updated in the next automatic cache refresh  
      console.log('📅 Result exclusion updated - rankings will be refreshed automatically');
      
    } catch (error) {
      console.error("Error toggling result exclusion:", error);
      // Revert the local state change on error
      setDancerData(prev => ({
        ...prev,
        results: prev.results.map(result => 
          result.compId === resultToToggle.compId &&
          result.discipline === resultToToggle.discipline &&
          result.category === resultToToggle.category &&
          result.dance === resultToToggle.dance
            ? { ...result, excluded: currentlyExcluded }
            : result
        )
      }));
    }
  };

  // 🚫 Toggle result invalid status (admin only) - excludes from ALL calculations
  const toggleResultInvalid = async (resultToToggle, currentlyInvalid) => {
    if (!isAdmin) return;
    
    try {
      console.log(`🔄 Toggling invalid status for result:`, {
        compId: resultToToggle.compId,
        discipline: resultToToggle.discipline,
        category: resultToToggle.category,
        dance: resultToToggle.dance,
        currentlyInvalid
      });
      
        // Update the local state immediately for better UX
        setDancerData(prev => {
          const updatedResults = prev.results.map(result => {
            // Match by all identifying fields to ensure we get the exact result
            if (result.compId === resultToToggle.compId &&
                result.discipline === resultToToggle.discipline &&
                result.category === resultToToggle.category &&
                result.dance === resultToToggle.dance) {
              const newInvalidStatus = !currentlyInvalid;
              return { 
                ...result, 
                invalid: newInvalidStatus,
                isInvalid: newInvalidStatus, // Keep both for consistency
                // Note: countsForRanking will be recalculated properly in the overview recalculation below
                // For now, use basic logic (full validation happens in overview calculation)
                countsForRanking: result.isWithin365Days && !result.excluded && !result.autoExcluded && !newInvalidStatus
              };
            }
            return result;
          });        // Recalculate disciplineOverview with updated invalid status
        // Filter out invalid results from ALL calculations
        const validResults = updatedResults.filter(r => !r.invalid);
        
        const groupedResults = {};
        validResults.forEach(result => {
          const key = `${result.discipline}-${result.category}${result.dance ? `-${result.dance}` : ''}`;
          if (!groupedResults[key]) {
            groupedResults[key] = {
              discipline: result.discipline,
              category: result.category,
              dance: result.dance,
              results: []
            };
          }
          groupedResults[key].results.push(result);
        });

        // Calculate points for each group using only valid results
        const updatedDisciplineOverview = Object.values(groupedResults).map(group => {
          // Only use valid results from last 365 days for ranking calculation
          const rankingGroupResults = group.results.filter(r => r.countsForRanking);
          const sortedResults = rankingGroupResults.sort((a, b) => b.points - a.points);
          const top6Results = sortedResults.slice(0, 6);
          const totalPoints = top6Results.reduce((sum, r) => sum + r.points, 0);
          
          // Find existing overview data
          const existingOverview = prev.disciplineOverview?.find(d => 
            d.discipline === group.discipline && 
            d.category === group.category && 
            d.dance === group.dance
          );
          
          // If totalPoints changed, rankings need recalculation
          const pointsChanged = existingOverview && Math.abs(existingOverview.totalPoints - totalPoints) > 0;
          
          return {
            discipline: group.discipline,
            category: group.category,
            dance: group.dance,
            totalPoints, // Points from valid results in last 365 days only
            competitionCount: group.results.length, // Valid competitions count
            rankingCompetitionCount: rankingGroupResults.length, // Valid competitions counting for ranking
            hasResults: group.results.length > 0,
            currentRank: pointsChanged ? "-" : (existingOverview?.currentRank || null),
            bestRank: existingOverview?.bestRank || null,
            daysHeld: pointsChanged ? "-" : (existingOverview?.daysHeld || 0),
            bestDaysHeld: existingOverview?.bestDaysHeld || 0,
            hasCurrentRank: totalPoints > 0
          };
        }).filter(overview => overview.hasResults)
        .sort((a, b) => {
          // Sort by age category first (highest to lowest)
          const ageCategoryOrder = ['Adult', 'Youth', 'Junior II', 'Junior I', 'Juvenile II', 'Juvenile I'];
          const aIndex = ageCategoryOrder.indexOf(a.category);
          const bIndex = ageCategoryOrder.indexOf(b.category);
          
          if (aIndex !== bIndex) {
            if (aIndex === -1) return 1;
            if (bIndex === -1) return -1;
            return aIndex - bIndex;
          }
          
          if (a.category === b.category && a.discipline === 'Latin' && b.discipline === 'Latin') {
            const latinDanceOrder = ['Samba', 'ChaChaCha', 'Rumba', 'Pasodoble', 'Jive'];
            const aDanceIndex = latinDanceOrder.indexOf(a.dance);
            const bDanceIndex = latinDanceOrder.indexOf(b.dance);
            
            if (aDanceIndex !== bDanceIndex) {
              if (aDanceIndex === -1) return 1;
              if (bDanceIndex === -1) return -1;
              return aDanceIndex - bDanceIndex;
            }
          }
          
          return 0;
        });

        return {
          ...prev,
          results: updatedResults,
          disciplineOverview: updatedDisciplineOverview
        };
      });
      
      // Update the database - find the competition document by ID
      const compDocRef = doc(db, 'competitions', resultToToggle.compId);
      const compDoc = await getDoc(compDocRef);
      
      if (compDoc.exists()) {
        const compData = compDoc.data();
        
        console.log(`🔍 Found competition document with ${compData.results?.length || 0} results`);
        console.log(`🎯 Looking for result matching:`, {
          dancerName: name,
          discipline: resultToToggle.discipline,
          category: resultToToggle.category,
          dance: resultToToggle.dance
        });
        
        // 🐛 DEBUG: Show all results for this dancer to see what's available
        const dancerResults = compData.results.filter(r => r.dancerName === name);
        console.log(`👤 Found ${dancerResults.length} results for ${name} in this competition:`, 
          dancerResults.map(r => ({
            discipline: r.discipline,
            category: r.category,
            dance: r.dance,
            excluded: r.excluded,
            invalid: r.invalid
          }))
        );
        
        // Update the specific result in the competition by matching all result properties
        const updatedResults = compData.results.map(result => {
          // Since the database stores discipline/category/dance as undefined,
          // we need to match only by dancer name for this specific competition
          const nameMatch = result.dancerName === name;
          
          // For competitions with multiple results per dancer, we need additional logic
          // But for now, match by name since this competition seems to have 1 result per dancer
          const isTargetResult = nameMatch;
          
          if (result.dancerName === name) {
            console.log(`🔍 Checking invalid result:`);
            console.log(`   Result:`, result.dancerName, '|', result.discipline, '|', result.category, '|', result.dance);
            console.log(`   Target:`, name, '|', resultToToggle.discipline, '|', resultToToggle.category, '|', resultToToggle.dance);
            console.log(`   Matches:`, { nameMatch });
            console.log(`   isTargetResult:`, isTargetResult);
            console.log(`   🚨 NOTE: Using name-only matching due to undefined discipline/category/dance in database`);
          }
          
          if (isTargetResult) {
            console.log(`✅ Found matching invalid result to update:`, result);
            return {
              ...result,
              invalid: !currentlyInvalid
            };
          }
          return result;
        });
        
        // Save back to database
        await updateDoc(compDoc.ref, {
          results: updatedResults
        });
        
        console.log(`💾 Invalid status saved to database for ${name} in competition ${resultToToggle.compId}`);
        
        // 🔍 VERIFICATION: Read back the data to confirm it was saved correctly
        const verificationDoc = await getDoc(doc(db, 'competitions', resultToToggle.compId));
        if (verificationDoc.exists()) {
          const verificationData = verificationDoc.data();
          const savedResult = verificationData.results.find(r => 
            r.dancerName === name
            // NOTE: Using name-only matching since discipline/category/dance are undefined in database
          );
          if (savedResult) {
            console.log(`✅ VERIFICATION: Database now contains invalid=${savedResult.invalid} for this result`);
          } else {
            console.error(`❌ VERIFICATION FAILED: Could not find saved result in database`);
          }
        }
      } else {
        console.error('Competition not found in database:', resultToToggle.compId);
      }

      // Force a complete refresh of profile data to ensure proper age category validation
      console.log('🔄 Triggering profile refresh to ensure proper validation...');
      setTimeout(() => {
        loadProfileData();
      }, 100);

      // Note: Rankings will be updated in the next automatic cache refresh
      console.log('📅 Result invalid status updated - rankings will be refreshed automatically');
      
    } catch (error) {
      console.error("Error toggling result invalid status:", error);
      // Revert the local state change on error
      setDancerData(prev => ({
        ...prev,
        results: prev.results.map(result => 
          result.compId === resultToToggle.compId &&
          result.discipline === resultToToggle.discipline &&
          result.category === resultToToggle.category &&
          result.dance === resultToToggle.dance
            ? { ...result, invalid: currentlyInvalid }
            : result
        )
      }));
    }
  };

  // 🎯 Calculate real rankings based on actual competition data
  async function calculateRealRankings(disciplineOverview, currentDancerName, competitionsData = null) {
    const enhancedOverview = [];
    
    console.log(`🔍 Starting Star Ranking calculations for ${currentDancerName}`);
    console.log(`📊 Processing ${disciplineOverview.length} discipline/category combinations`);
    console.log(`📊 Input overview categories:`, disciplineOverview.map(o => `${o.category} (${o.dance}) - Points: ${o.totalPoints}, Rank: ${o.currentRank}`).join(', '));
    
    for (const overview of disciplineOverview) {
      const categoryLabel = `${overview.discipline} - ${overview.category}${overview.dance ? ` (${overview.dance})` : ''}`;
      console.log(`\n🎯 Calculating rankings for: ${categoryLabel}`);
      
      // 🎂 Check if this category is valid for dancers with manual birth year
      let shouldSkipRankingCalculation = false;
      if (profileData?.manualYearOfBirth) {
        const currentYear = new Date().getFullYear();
        const currentAgeCategory = calculateAgeCategoryForYear(`${profileData.manualYearOfBirth}-01-01`, currentYear);
        const categoryAgeGroup = overview.category.replace(/^(Solo |Couple )?/, '');
        
        const ageCategoryHierarchy = ['Junior I', 'Junior II', 'Youth', 'Adult'];
        const currentIndex = ageCategoryHierarchy.indexOf(currentAgeCategory);
        const categoryIndex = ageCategoryHierarchy.indexOf(categoryAgeGroup);
        
        const isCurrentCategoryValid = categoryAgeGroup === currentAgeCategory;
        const isOneCategoryHigher = categoryIndex === currentIndex + 1;
        const isCategoryAllowed = isCurrentCategoryValid || isOneCategoryHigher;
        
        if (!isCategoryAllowed) {
          console.log(`🎂 Skipping ranking calculation for invalid category: ${overview.category} (current: ${currentAgeCategory})`);
          shouldSkipRankingCalculation = true;
        }
      }
      
      if (shouldSkipRankingCalculation) {
        // For invalid categories, preserve historical best rank/days but reset current data
        console.log(`🎂 Preserving historical data for invalid category: bestRank=${overview.bestRank}, bestDaysHeld=${overview.bestDaysHeld}`);
        enhancedOverview.push({
          ...overview,
          totalPoints: 0,
          rankingCompetitionCount: 0,
          currentRank: null,
          daysHeld: 0,
          // Preserve historical bestRank and bestDaysHeld from the overview calculation
          bestRank: overview.bestRank,
          bestDaysHeld: overview.bestDaysHeld,
          totalDancers: 0,
          hasCurrentRank: false
        });
        continue;
      }
      
      try {
        // Get all dancers in this category to calculate rankings
        const allDancersData = await getAllDancersInCategory(overview.discipline, overview.category, overview.dance, competitionsData);
        console.log(`👥 Found ${allDancersData.length} dancers in category`);
        
        // Calculate current ranking based on total points from last 365 days
        const rankingData = calculateCurrentRanking(allDancersData, currentDancerName);
        console.log(`🏆 Current Star Ranking: #${rankingData.currentRank || 'Unranked'}`);
        
        // Calculate historical rankings and best rank with real rank tracking
        const historicalData = await calculateHistoricalRankings(overview.discipline, overview.category, overview.dance, currentDancerName, competitionsData);
        console.log(`⭐ Best Rank Ever: #${historicalData.bestRank || 'None'}`);
        console.log(`📅 Current Days: ${historicalData.currentDaysHeld} | Best Days: ${historicalData.bestDaysHeld}`);
        
        enhancedOverview.push({
          ...overview,
          currentRank: rankingData.currentRank,
          daysHeld: historicalData.currentDaysHeld, // Days since acquiring current rank
          bestRank: historicalData.bestRank,
          bestDaysHeld: historicalData.bestDaysHeld, // Days held at best position
          totalDancers: allDancersData.filter(d => d.totalPoints > 0).length, // Only count dancers with active points
          hasCurrentRank: rankingData.currentRank !== null && overview.totalPoints > 0
        });
        
      } catch (error) {
        console.error(`❌ Error calculating rankings for ${categoryLabel}:`, error);
        // Keep original data if calculation fails
        enhancedOverview.push({
          ...overview,
          currentRank: null,
          daysHeld: 0,
          bestRank: null,
          bestDaysHeld: 0,
          totalDancers: 0,
          hasCurrentRank: false
        });
      }
    }
    
    console.log(`✅ Completed Star Ranking calculations for ${currentDancerName}`);
    console.log(`📊 Output overview categories:`, enhancedOverview.map(o => `${o.category} (${o.dance}) - Points: ${o.totalPoints}, Rank: ${o.currentRank}`).join(', '));
    return enhancedOverview;
  }

  // 📊 Get all dancers in a specific category for ranking comparison
  async function getAllDancersInCategory(discipline, category, dance, competitionsData = null) {
    const dancersMap = new Map();
    const oneYearAgo = new Date();
    oneYearAgo.setDate(oneYearAgo.getDate() - 365);
    
    try {
      // Use shared competitions data if available, otherwise query database
      let competitionsToProcess = [];
      
      if (competitionsData) {
        console.log("🚀 Using shared competitions data for getAllDancersInCategory");
        competitionsToProcess = competitionsData;
      } else {
        console.log("⚠️ No shared data, querying database for getAllDancersInCategory");
        const oldQuery = query(collection(db, "competitions"));
        const oldSnapshot = await getDocs(oldQuery);
        competitionsToProcess = oldSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data()
        }));
      }
      
      competitionsToProcess.forEach((competition) => {
        const comp = competition.data || competition;
        const docId = competition.id;
        
        if (comp.discipline === discipline && comp.category === category && 
            (!dance || comp.dance === dance) && comp.results) {
          
          comp.results.forEach((result) => {
            const dancerName = result.dancerName;
            if (!dancersMap.has(dancerName)) {
              dancersMap.set(dancerName, []);
            }
            
            // Only include results from last 365 days for ranking
            const resultDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
            if (resultDate >= oneYearAgo) {
              const score = Math.round((result.points || 0) * (comp.multiplier || 1));
              dancersMap.get(dancerName).push({
                points: score,
                timestamp: comp.timestamp,
                compId: docId
              });
            }
          });
        }
      });

    } catch (error) {
      console.error("❌ Error fetching all dancers data:", error);
    }

    // Calculate total points (best 6 results) for each dancer
    const dancersWithPoints = Array.from(dancersMap.entries()).map(([name, results]) => {
      const sortedResults = results.sort((a, b) => b.points - a.points);
      const top6Results = sortedResults.slice(0, 6);
      const totalPoints = top6Results.reduce((sum, r) => sum + r.points, 0);
      
      return {
        name,
        totalPoints,
        resultCount: results.length,
        top6Results
      };
    });
    
    // 🎂 Filter out dancers who should be in higher age categories (verified/manual birth year only)
    // This ensures ranks are accurate - a Youth dancer with points in Junior II should be excluded from Junior II rankings
    const filteredDancers = [];
    // Extract age category from full category name (e.g., "Solo Hip Hop Junior II" -> "Junior II")
    // Remove "Solo " or "Couple " prefix, then remove discipline names (Hip Hop, Contemporary, Commercial, Ballet, etc.)
    let categoryAgeGroup = category.replace(/^(Solo |Couple )?/, ''); // Remove prefix
    categoryAgeGroup = categoryAgeGroup.replace(/^(Hip Hop |Contemporary |Commercial |Ballet |Latin |Ballroom |Jazz |Tap )/i, ''); // Remove discipline
    categoryAgeGroup = categoryAgeGroup.trim();
    
    // Pre-load all birth dates once (no database queries per dancer)
    const birthDatesMap = await preloadBirthDates();
    
    for (const dancer of dancersWithPoints) {
      try {
        // Check if dancer has verified profile or manual year of birth
        const birthDate = getDancerBirthDate(dancer.name, birthDatesMap);
        
        if (birthDate) {
          // Dancer has verified/manual birth date - check if category is valid
          const currentYear = new Date().getFullYear();
          // Convert Date object to yyyy-mm-dd string format for calculateAgeCategoryForYear
          const birthDateString = birthDate instanceof Date 
            ? birthDate.toISOString().split('T')[0] 
            : birthDate;
          const currentAgeCategory = calculateAgeCategoryForYear(birthDateString, currentYear);
          
          const ageCategoryHierarchy = ['Juvenile I', 'Juvenile II', 'Junior I', 'Junior II', 'Youth', 'Adult'];
          const currentIndex = ageCategoryHierarchy.indexOf(currentAgeCategory);
          const categoryIndex = ageCategoryHierarchy.indexOf(categoryAgeGroup);
          
          // Allow current category and one category higher
          const isCurrentCategory = categoryAgeGroup === currentAgeCategory;
          const isOneCategoryHigher = categoryIndex === currentIndex + 1;
          
          if (isCurrentCategory || isOneCategoryHigher) {
            // Valid category for this dancer
            filteredDancers.push(dancer);
          } else if (categoryIndex < currentIndex) {
            // This is a lower/historical category - EXCLUDE from active rankings
            console.log(`🚫 Excluding ${dancer.name} from ${category} rankings (current age category: ${currentAgeCategory})`);
          } else {
            // Higher category (future) - include for now
            filteredDancers.push(dancer);
          }
        } else {
          // No birth date - include dancer by default (unverified)
          filteredDancers.push(dancer);
        }
      } catch (error) {
        console.error(`⚠️ Error checking age category for ${dancer.name}:`, error);
        // On error, include dancer to avoid data loss
        filteredDancers.push(dancer);
      }
    }
    
    console.log(`📊 ${category}: Included ${filteredDancers.length}/${dancersWithPoints.length} dancers after age category filtering`);
    
    // Sort by total points to determine rankings
    return filteredDancers.sort((a, b) => b.totalPoints - a.totalPoints);
  }

  // 🥇 Calculate current ranking based on points comparison
  function calculateCurrentRanking(allDancersData, currentDancerName) {
    const currentDancerData = allDancersData.find(d => d.name === currentDancerName);
    
    if (!currentDancerData || currentDancerData.totalPoints === 0) {
      return { currentRank: null, daysHeld: 0 };
    }
    
    // Find the rank (1-based)
    const currentRank = allDancersData.findIndex(d => d.name === currentDancerName) + 1;
    
    return { currentRank, daysHeld: 0 }; // Will be calculated later with real rank tracking
  }

  // 📈 Calculate historical rankings and best rank achieved with real rank tracking
  // ⚠️ For verified dancers in higher age groups: Best Rank/Days calculated only up to Dec 31 of previous year
  async function calculateHistoricalRankings(discipline, category, dance, currentDancerName, competitionsData = null) {
    try {
      // Get all competitions in chronological order to track rank changes
      const allCompetitions = await getChronologicalCompetitions(discipline, category, dance, competitionsData);
      
      if (allCompetitions.length === 0) {
        return { bestRank: null, bestDaysHeld: 0, currentDaysHeld: 0 };
      }

      // 📅 Determine cutoff date for verified dancers who changed age categories
      const isVerified = profileData?.dateOfBirth;
      const currentYear = new Date().getFullYear();
      const dec31PreviousYear = new Date(currentYear - 1, 11, 31, 23, 59, 59); // Dec 31 of previous year
      
      let bestRankCutoffDate = null;
      
      // 🚫 Check for excluded results and find the earliest exclusion date
      let earliestExclusionCutoff = null;
      if (dancerData?.results) {
        const excludedResults = dancerData.results.filter(r => 
          r.excluded || r.autoExcluded
        );
        
        if (excludedResults.length > 0) {
          // Find the earliest excluded result's competition year
          const earliestExcludedDate = excludedResults.reduce((earliest, result) => {
            const resultDate = result.timestamp instanceof Timestamp 
              ? result.timestamp.toDate() 
              : new Date(result.timestamp);
            return !earliest || resultDate < earliest ? resultDate : earliest;
          }, null);
          
          if (earliestExcludedDate) {
            // Set cutoff to Dec 31 of the competition year of the earliest excluded result
            const competitionYear = earliestExcludedDate.getFullYear();
            earliestExclusionCutoff = new Date(competitionYear, 11, 31, 23, 59, 59); // Dec 31 of competition year
            console.log(`🚫 Found excluded results - Best rank cutoff set to Dec 31, ${competitionYear}`);
          }
        }
      }
      
      // Age category verification logic (get birth date for consistency)
      const birthDatesMap = await preloadBirthDates();
      const dancerBirthDate = getDancerBirthDate(currentDancerName, birthDatesMap);
      if (isVerified && dancerBirthDate) {
        // Determine if and when the dancer changed age categories
        const currentYear = new Date().getFullYear();
        const currentAgeCategory = await getDancerCurrentAgeCategory(currentDancerName);
        
        // Convert Date object to yyyy-mm-dd string format
        const birthDateString = dancerBirthDate instanceof Date 
          ? dancerBirthDate.toISOString().split('T')[0] 
          : dancerBirthDate;
        
        if (currentAgeCategory) {
          // Extract the age category from the category parameter (e.g., "Solo Hip Hop Juvenile I" -> "Juvenile I")
          const categoryAgeGroup = category.match(/\b(Juvenile I|Juvenile II|Junior I|Junior II|Youth|Adult)\b/)?.[0];
          
          // Check what age category they had in previous years
          let ageCategoryChangeYear = null;
          let previousAgeCategory = null;
          
          // Look back through years to find when they changed categories
          for (let year = currentYear - 1; year >= currentYear - 5; year--) {
            const ageCategoryInYear = calculateAgeCategoryForYear(birthDateString, year);
            if (ageCategoryInYear !== currentAgeCategory) {
              // Found the last year they were in a different category
              ageCategoryChangeYear = year + 1; // They changed in the year after
              previousAgeCategory = ageCategoryInYear;
              break;
            }
          }
          
          if (ageCategoryChangeYear && ageCategoryChangeYear <= currentYear) {
            // Only apply cutoff if calculating best rank for the OLD age category they aged out of
            // Don't apply cutoff to current or one-category-up rankings
            const isOldAgeCategory = categoryAgeGroup === previousAgeCategory;
            
            if (isOldAgeCategory) {
              // They aged out of this category - set cutoff to Dec 31 of the last year in this category
              const lastYearInPreviousCategory = ageCategoryChangeYear - 1;
              const ageCategoryCutoff = new Date(lastYearInPreviousCategory, 11, 31, 23, 59, 59);
              console.log(`🔄 This is the old age category (${previousAgeCategory}) - Best rank cutoff: Dec 31, ${lastYearInPreviousCategory}`);
              
              // Use the most restrictive cutoff (earliest date)
              if (earliestExclusionCutoff && earliestExclusionCutoff < ageCategoryCutoff) {
                bestRankCutoffDate = earliestExclusionCutoff;
                console.log(`📅 Using exclusion cutoff (earlier than age category cutoff)`);
              } else {
                bestRankCutoffDate = ageCategoryCutoff;
                console.log(`📅 Using age category cutoff`);
              }
            } else {
              console.log(`✅ Still competing in ${categoryAgeGroup} - No age category cutoff applied`);
              if (earliestExclusionCutoff) {
                bestRankCutoffDate = earliestExclusionCutoff;
                console.log(`📅 Using exclusion cutoff only`);
              }
            }
          } else if (earliestExclusionCutoff) {
            bestRankCutoffDate = earliestExclusionCutoff;
            console.log(`📅 Using exclusion cutoff only (no age category change detected)`);
          }
        } else if (earliestExclusionCutoff) {
          bestRankCutoffDate = earliestExclusionCutoff;
          console.log(`📅 Using exclusion cutoff (no current age category found)`);
        }
      } else if (profileData?.dateOfBirth) {
        // Fallback to manual birth date if no claimed profile
        const birthYear = new Date(profileData.dateOfBirth).getFullYear();
        const currentAge = currentYear - birthYear;
        const previousYearAge = currentAge - 1;
        
        const currentCategory = calculateAgeCategoryFromAge(currentAge);
        const previousYearCategory = calculateAgeCategoryFromAge(previousYearAge);
        
        // Age category order (higher = later in career)
        const ageCategoryOrder = ['Juvenile I', 'Juvenile II', 'Junior I', 'Junior II', 'Youth', 'Adult'];
        const currentCategoryIndex = ageCategoryOrder.indexOf(currentCategory);
        const previousCategoryIndex = ageCategoryOrder.indexOf(previousYearCategory);
        
        // If moved to higher age group, use Dec 31 cutoff for best rank calculations
        if (currentCategoryIndex > previousCategoryIndex && currentCategoryIndex > -1) {
          const ageCategoryCutoff = dec31PreviousYear;
          console.log(`🔄 Verified dancer moved from ${previousYearCategory} to ${currentCategory} - Age category cutoff: Dec 31, ${currentYear-1}`);
          
          // Use the most restrictive cutoff (earliest date)
          if (earliestExclusionCutoff && earliestExclusionCutoff < ageCategoryCutoff) {
            bestRankCutoffDate = earliestExclusionCutoff;
            console.log(`📅 Using exclusion cutoff (earlier than age category cutoff)`);
          } else {
            bestRankCutoffDate = ageCategoryCutoff;
            console.log(`📅 Using age category cutoff`);
          }
        } else if (earliestExclusionCutoff) {
          bestRankCutoffDate = earliestExclusionCutoff;
          console.log(`📅 Using exclusion cutoff only`);
        }
      } else if (earliestExclusionCutoff) {
        bestRankCutoffDate = earliestExclusionCutoff;
        console.log(`📅 Using exclusion cutoff for non-verified dancer`);
      }
      
      // Calculate rank progression over time WITH cutoff for best rank calculations
      const rankHistoryWithCutoff = calculateRankProgression(allCompetitions, currentDancerName, bestRankCutoffDate);
      
      // Calculate rank progression over time WITHOUT cutoff for current days (always uses full timeline)
      const fullRankHistory = calculateRankProgression(allCompetitions, currentDancerName, null);
      
      // Find best rank and calculate days held (with cutoff consideration)
      const bestRankData = findBestRankAndDays(rankHistoryWithCutoff, bestRankCutoffDate);
      
      // Calculate current rank days using FULL timeline (no cutoff) - works for verified, non-verified, and manual year of birth
      const currentRankDays = calculateCurrentRankDays(fullRankHistory);
      
      return {
        bestRank: bestRankData.bestRank,
        bestDaysHeld: bestRankData.maxDaysHeld, // 📅 Updates daily
        currentDaysHeld: currentRankDays // 📅 Updates daily - uses full timeline for all dancer types
      };
    } catch (error) {
      console.error("❌ Error calculating historical rankings:", error);
      return { bestRank: null, bestDaysHeld: 0, currentDaysHeld: 0 };
    }
  }

  // 📅 Get all competitions in chronological order for rank tracking
  async function getChronologicalCompetitions(discipline, category, dance, competitionsData = null) {
    const competitions = [];
    
    try {
      // Use shared competitions data if available, otherwise query database
      let competitionsToProcess = [];
      
      if (competitionsData) {
        console.log("🚀 Using shared competitions data for getChronologicalCompetitions");
        competitionsToProcess = competitionsData;
      } else {
        console.log("⚠️ No shared data, querying database for getChronologicalCompetitions");
        const oldQuery = query(collection(db, "competitions"));
        const oldSnapshot = await getDocs(oldQuery);
        competitionsToProcess = oldSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data()
        }));
      }
      
      competitionsToProcess.forEach((competition) => {
        const comp = competition.data || competition;
        const docId = competition.id;
        
        if (comp.discipline === discipline && comp.category === category && 
            (!dance || comp.dance === dance) && comp.results && comp.timestamp) {
          
          competitions.push({
            id: docId,
            name: comp.name || docId,
            timestamp: comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp),
            results: comp.results
              .filter(r => !r.invalid) // 🚫 Exclude invalid results from historical calculations
              .map(r => ({
                dancerName: r.dancerName,
                points: Math.round((r.points || 0) * (comp.multiplier || 1)),
                place: r.place
              })),
            multiplier: comp.multiplier || 1
          });
        }
      });

    } catch (error) {
      console.error("❌ Error fetching chronological competitions:", error);
    }

    // Sort by timestamp (oldest first)
    const sorted = competitions.sort((a, b) => a.timestamp - b.timestamp);
    
    return sorted;
  }

  // 📊 Calculate rank progression over time
  function calculateRankProgression(competitions, targetDancer, cutoffDate = null) {
    const rankHistory = [];
    const dancerData = new Map(); // Track cumulative results for each dancer
    
    for (const competition of competitions) {
      const compDate = competition.timestamp instanceof Timestamp ? competition.timestamp.toDate() : new Date(competition.timestamp);
      
      // Skip competitions after cutoff date for best rank calculations (verified age group changes)
      if (cutoffDate && compDate > cutoffDate) {
        console.log(`⏰ Skipping competition ${competition.name} (${compDate.toDateString()}) - after cutoff ${cutoffDate.toDateString()}`);
        continue;
      }
      
      // Update each dancer's results with this competition
      for (const result of competition.results) {
        if (!dancerData.has(result.dancerName)) {
          dancerData.set(result.dancerName, []);
        }
        dancerData.get(result.dancerName).push({
          points: result.points,
          timestamp: competition.timestamp,
          compId: competition.id
        });
      }
      
      // Calculate rankings after this competition
      const allDancers = Array.from(dancerData.entries()).map(([name, results]) => {
        // Only count results from last 365 days from this competition date
        const cutoffDate = new Date(competition.timestamp);
        cutoffDate.setDate(cutoffDate.getDate() - 365);
        
        const validResults = results.filter(r => r.timestamp >= cutoffDate);
        const sortedResults = validResults.sort((a, b) => b.points - a.points);
        const top6Results = sortedResults.slice(0, 6);
        const totalPoints = top6Results.reduce((sum, r) => sum + r.points, 0);
        
        return { name, totalPoints, resultCount: validResults.length };
      });
      
      // Sort by points to get rankings
      allDancers.sort((a, b) => b.totalPoints - a.totalPoints);
      
      // Find target dancer's rank
      const targetDancerIndex = allDancers.findIndex(d => d.name === targetDancer);
      if (targetDancerIndex !== -1) {
        const rank = targetDancerIndex + 1;
        const totalPoints = allDancers[targetDancerIndex].totalPoints;
        
        rankHistory.push({
          date: competition.timestamp,
          rank: rank,
          totalPoints: totalPoints,
          competitionId: competition.id,
          totalDancers: allDancers.filter(d => d.totalPoints > 0).length
        });
      }
    }
    
    return rankHistory;
  }

  // 🏆 Find best rank and calculate maximum consecutive days held
  function findBestRankAndDays(rankHistory, cutoffDate = null) {
    if (rankHistory.length === 0) {
      return { bestRank: null, maxDaysHeld: 0 };
    }
    
    let bestRank = Math.min(...rankHistory.map(r => r.rank));
    let maxDaysHeld = 0;
    let streakStartDate = null;
    
    console.log(`🔍 Analyzing best rank periods for rank #${bestRank}${cutoffDate ? ` (up to ${cutoffDate.toDateString()})` : ''}`);
    
    for (let i = 0; i < rankHistory.length; i++) {
      const current = rankHistory[i];
      
      if (current.rank === bestRank) {
        if (streakStartDate === null) {
          // Start of a new streak at best rank
          streakStartDate = current.date;
          console.log(`🏁 Started best rank period on: ${current.date.toDateString()}`);
        }
        // Continue the streak - we stay at best rank
        // (Don't calculate days yet, wait until streak ends)
      } else {
        // Rank changed - end current streak if we were in one
        if (streakStartDate !== null) {
          // Calculate total days from start of streak to the current competition (where rank changed)
          const currentCompetition = rankHistory[i]; // The competition where rank changed
          const streakDays = Math.ceil((currentCompetition.date - streakStartDate) / (1000 * 60 * 60 * 24));
          
          // If start and current are on the same day, they held it for 1 day minimum
          const adjustedDays = Math.max(streakDays, 1);
          
          console.log(`🏁 Ended best rank period on: ${currentCompetition.date.toDateString()} (held for ${adjustedDays} days)`);
          maxDaysHeld = Math.max(maxDaysHeld, adjustedDays);
          streakStartDate = null;
        }
      }
    }
    
    // Handle case where streak continues to the end (still at best rank)
    if (streakStartDate !== null) {
      const lastCompetition = rankHistory[rankHistory.length - 1];
      // For cutoff calculations (verified age group changes), use cutoff date instead of today
      const endDate = cutoffDate || new Date();
      const ongoingDays = Math.ceil((endDate - streakStartDate) / (1000 * 60 * 60 * 24));
      
      if (cutoffDate) {
        console.log(`🏁 Best rank period up to cutoff ${cutoffDate.toDateString()}: ${ongoingDays} days`);
      } else {
        console.log(`🏁 Best rank period continues to today: ${ongoingDays} days (📅 updates daily)`);
      }
      
      maxDaysHeld = Math.max(maxDaysHeld, Math.max(ongoingDays, 1)); // Minimum 1 day
    }
    
    console.log(`✅ Maximum consecutive days at best rank #${bestRank}: ${maxDaysHeld}${!cutoffDate ? ' (📅 updates daily)' : ''}`);
    return { bestRank, maxDaysHeld };
  }

  // 📅 Calculate current rank consecutive days
  function calculateCurrentRankDays(rankHistory) {
    if (rankHistory.length === 0) {
      return 0;
    }
    
    const currentRank = rankHistory[rankHistory.length - 1].rank;
    let streakStartDate = null;
    
    console.log(`🔍 Calculating current rank #${currentRank} consecutive days`);
    
    // Go backwards from the most recent competition to find where current rank started
    for (let i = rankHistory.length - 1; i >= 0; i--) {
      const competition = rankHistory[i];
      
      if (competition.rank === currentRank) {
        // Still the same rank, continue going back
        streakStartDate = competition.date;
      } else {
        // Different rank found, stop here
        break;
      }
    }
    
    // Calculate actual days from when current rank started to today
    if (streakStartDate !== null) {
      const today = new Date();
      // Convert Timestamp to Date if necessary
      const startDate = streakStartDate instanceof Timestamp ? streakStartDate.toDate() : new Date(streakStartDate);
      const consecutiveDays = Math.ceil((today - startDate) / (1000 * 60 * 60 * 24));
      console.log(`📅 Current rank #${currentRank} started on: ${startDate.toDateString()} (${consecutiveDays} days ago)`);
      return consecutiveDays; // Return actual days held, not minimum
    }
    
    return 0;
  }

  // 📊 Get all-time dancers data for historical comparison
  async function getAllDancersInCategoryAllTime(discipline, category, dance, competitionsData = null) {
    // Similar to getAllDancersInCategory but without the 365-day filter
    // This would be used for calculating historical best ranks
    const dancersMap = new Map();
    
    try {
      // Use shared competitions data if available, otherwise query database
      let competitionsToProcess = [];
      
      if (competitionsData) {
        console.log("🚀 Using shared competitions data for getAllDancersInCategoryAllTime");
        competitionsToProcess = competitionsData;
      } else {
        console.log("⚠️ No shared data, querying database for getAllDancersInCategoryAllTime");
        const oldQuery = query(collection(db, "competitions"));
        const oldSnapshot = await getDocs(oldQuery);
        competitionsToProcess = oldSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data()
        }));
      }
      
      competitionsToProcess.forEach((competition) => {
        const comp = competition.data || competition;
        const docId = competition.id;
        
        if (comp.discipline === discipline && comp.category === category && 
            (!dance || comp.dance === dance) && comp.results) {
          
          comp.results.forEach((result) => {
            const dancerName = result.dancerName;
            if (!dancersMap.has(dancerName)) {
              dancersMap.set(dancerName, []);
            }
            
            const score = Math.round((result.points || 0) * (comp.multiplier || 1));
            dancersMap.get(dancerName).push({
              points: score,
              timestamp: comp.timestamp,
              compId: docId
            });
          });
        }
      });
      
      // Calculate total points (best 6 results) for each dancer
      const dancersWithPoints = Array.from(dancersMap.entries()).map(([name, results]) => {
        const sortedResults = results.sort((a, b) => b.points - a.points);
        const top6Results = sortedResults.slice(0, 6);
        const totalPoints = top6Results.reduce((sum, r) => sum + r.points, 0);
        
        return {
          name,
          totalPoints,
          resultCount: results.length,
          top6Results
        };
      });
      
      return dancersWithPoints.sort((a, b) => b.totalPoints - a.totalPoints);
    } catch (error) {
      console.error("❌ Error fetching all-time dancers data:", error);
      return [];
    }
  }

  // � Load transfer history and determine current studio
  const loadTransferHistory = async (competitionsData = null, cachedStudio = null, existingProfileData = null) => {
    try {
      // Get transfer history for this dancer
      const transfersQuery = query(
        collection(db, 'studioTransfers'),
        where('dancerName', '==', name),
        where('status', '==', 'active')
      );
      
      const transfersSnapshot = await getDocs(transfersQuery);
      const transfers = transfersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        transferDate: doc.data().transferDate.toDate()
      }));
      
      // Sort by transfer date (most recent first)
      transfers.sort((a, b) => b.transferDate - a.transferDate);
      setTransferHistory(transfers);
      
      // Load discipline-specific studios from dancer profile
      // 🚀 Use already-loaded profile data if available to avoid duplicate Firestore read
      try {
        const cached = existingProfileData || loadedProfileDataRef.current;
        if (cached) {
          if (cached.disciplineStudios) {
            setDisciplineStudios(cached.disciplineStudios);
          }
        } else {
          const profileDoc = await getDoc(doc(db, 'dancerProfiles', name));
          if (profileDoc.exists() && profileDoc.data().disciplineStudios) {
            setDisciplineStudios(profileDoc.data().disciplineStudios);
          }
        }
      } catch (error) {
        console.error('Error loading discipline studios:', error);
      }
      
      // Determine current studio
      let currentStudioName = null;
      
      if (transfers.length > 0) {
        // Use the "toStudio" of the most recent transfer
        currentStudioName = transfers[0].toStudio;
      } else {
        // No transfers, get studio from most recent competition
        // Use shared competitions data if available, otherwise query database
        if (competitionsData) {
          console.log("🚀 Using shared competitions data for current studio lookup");
          let mostRecentStudio = null;
          let mostRecentDate = null;
          
          competitionsData.forEach((competition) => {
            const comp = competition.data || competition;
            if (comp.results) {
              comp.results.forEach(result => {
                if (result.name === name && result.studio) {
                  const competitionDate = new Date(comp.date);
                  if (!mostRecentDate || competitionDate > mostRecentDate) {
                    mostRecentDate = competitionDate;
                    mostRecentStudio = result.studio;
                  }
                }
              });
            }
          });
          
          currentStudioName = mostRecentStudio;
        } else if (cachedStudio) {
          // 🚀 Use studio from rankings cache (updated daily) to avoid expensive full competitions scan
          console.log("🚀 Using cached studio from rankings cache");
          currentStudioName = cachedStudio;
        } else {
          console.log("⚠️ No shared data or cached studio, querying database for current studio lookup");
          const competitionsSnapshot = await getDocs(collection(db, 'competitions'));
          let mostRecentStudio = null;
          let mostRecentDate = null;
          
          competitionsSnapshot.forEach((doc) => {
            const competition = doc.data();
            if (competition.results) {
              competition.results.forEach(result => {
                if (result.name === name && result.studio) {
                  const competitionDate = new Date(competition.date);
                  if (!mostRecentDate || competitionDate > mostRecentDate) {
                    mostRecentDate = competitionDate;
                    mostRecentStudio = result.studio;
                  }
                }
              });
            }
          });
          
          currentStudioName = mostRecentStudio;
        }
      }
      
      setCurrentStudio(currentStudioName);
      setStudioLoading(false); // 🚀 Studio data loaded
      
      // Debug logging for studio information
      console.log(`🏢 Studio information loaded:`, {
        currentStudio: currentStudioName,
        transfersCount: transfers.length,
        hasTransfers: transfers.length > 0
      });
    } catch (error) {
      console.error('Error loading transfer history:', error);
      setStudioLoading(false);
    }
  };

  // 🏢 Get studio name for a specific competition date
  const getStudioForDate = (competitionDate) => {
    if (!transferHistory || transferHistory.length === 0) {
      return currentStudio;
    }
    
    // Find the transfer that applies to this date
    for (const transfer of transferHistory) {
      if (competitionDate >= transfer.transferDate) {
        return transfer.toStudio;
      }
    }
    
    // If no transfer applies, use the "fromStudio" of the earliest transfer or current studio
    if (transferHistory.length > 0) {
      return transferHistory[transferHistory.length - 1].fromStudio;
    }
    
    return currentStudio;
  };

  // �📋 Load additional profile data (bio, achievements, etc.)
  const loadProfileData = async () => {
    try {
      if (profileData && profileData.sanitizedName === name) {
        console.log('✅ Profile data already loaded for:', name);
        return;
      }
      
      console.log('🔍 Loading profile data for name:', name);
      // Load from dancerProfiles collection
      const profileRef = doc(db, 'dancerProfiles', name);
      const profileSnap = await getDoc(profileRef);
      
      if (profileSnap.exists()) {
        const data = profileSnap.data();
        loadedProfileDataRef.current = data; // 🚀 Store immediately for other functions (avoids re-read)
        setProfileData(data);
        console.log('✅ Loaded profile data for:', name);
        
        // 🎂 Load manual year of birth if exists
        if (data.manualYearOfBirth) {
          setManualYearOfBirth(data.manualYearOfBirth);
          console.log('📅 Loaded manual year of birth:', data.manualYearOfBirth);
        }
        
        // Load profile picture from dancerProfiles if it exists
        if (data.profilePicture) {
          // Immediately set the profile picture URL - optimistic loading
          setProfilePictureUrl(data.profilePicture);
          console.log('✅ Profile picture set immediately (optimistic):', name);
          
          // If it's not already a HTTP URL, resolve it asynchronously to improve the URL
          if (!data.profilePicture.startsWith('http')) {
            setProfilePictureLoading(true);
            try {
              console.log('🖼️ Improving profile picture URL from dancerProfiles:', data.profilePicture);
            
            const resolveStorageUrl = async (value) => {
              // Handle gs:// URLs
              if (value.startsWith('gs://')) {
                console.log('🔗 Detected gs:// URL, extracting path:', value);
                const withoutScheme = value.replace(/^gs:\/\/[^/]+\//, '');
                console.log('📂 Extracted storage path:', withoutScheme);
                try {
                  const pictureRef = ref(storage, withoutScheme);
                  const pictureUrl = await getDownloadURL(pictureRef);
                  console.log('✅ Generated download URL from gs:// path:', pictureUrl);
                  return pictureUrl;
                } catch (e) {
                  console.warn('⚠️ Could not getDownloadURL for gs:// path, falling back to raw value:', e);
                  return value;
                }
              }

              // Handle Firebase storage download URLs
              if (value.includes('/o/')) {
                console.log('🔗 Detected Firebase download URL, extracting storage path');
                try {
                  const m = value.match(/\/o\/(.+?)\?/);
                  if (m && m[1]) {
                    const decoded = decodeURIComponent(m[1]);
                    console.log('📂 Extracted storage path from URL:', decoded);
                    try {
                      const pictureRef = ref(storage, decoded);
                      const pictureUrl = await getDownloadURL(pictureRef);
                      console.log('✅ Refreshed download URL for:', name);
                      return pictureUrl;
                    } catch (e) {
                      console.warn('⚠️ Could not refresh download URL, using original:', e);
                      return value;
                    }
                  }
                } catch (e) {
                  console.warn('⚠️ Error extracting storage path from download URL:', e);
                }
              }

              // Check if it's a storage path without prefix
              if (!value.startsWith('http') && !value.startsWith('gs://')) {
                try {
                  const pictureRef = ref(storage, value);
                  const pictureUrl = await getDownloadURL(pictureRef);
                  console.log('✅ Generated download URL from storage path for:', name);
                  return pictureUrl;
                } catch (e) {
                  console.warn('⚠️ Could not getDownloadURL for storage path:', e);
                  return value;
                }
              }

              // Otherwise treat as URL
              return value;
            };

            const resolved = await resolveStorageUrl(data.profilePicture);
            setProfilePictureUrl(resolved);
            setProfilePictureLoading(false);
            console.log('✅ Profile picture URL improved for:', name);
            } catch (error) {
              console.log('❌ Could not improve profile picture URL, keeping original:', error);
              // Keep the original URL that was set optimistically
              setProfilePictureLoading(false);
            }
          }
        } else {
          console.log('📷 No profile picture found in dancerProfiles');
          setProfilePictureUrl(null);
        }
      } else {
        console.log('📄 No profile document found');
        setProfileData(null);
        setProfilePictureUrl(null);
        
        // Check if there might be profile updates in profileUpdates collection
        try {
          const updateRef = doc(db, 'profileUpdates', name);
          const updateSnap = await getDoc(updateRef);
          console.log('🔄 Profile updates document exists:', updateSnap.exists());
          
          if (updateSnap.exists()) {
            const updateData = updateSnap.data();
            console.log('🔄 Profile updates data:', updateData);
            setProfileData(updateData);
          }
        } catch (error) {
          console.log('❌ Error checking profile updates:', error);
        }
      }

      // Also check dancers collection for profile picture as fallback
      const dancerRef = doc(db, 'dancers', name);
      const dancerSnap = await getDoc(dancerRef);
      
      if (dancerSnap.exists()) {
        const dancerData = dancerSnap.data();
        console.log('👤 Loaded dancer data:', dancerData);
        
        // Only use dancer collection profile picture if we don't have one from dancerProfiles and not currently loading
        if (!profilePictureUrl && !profilePictureLoading && dancerData.profilePicture) {
          try {
            console.log('🖼️ Loading fallback profile picture from dancers collection:', dancerData.profilePicture);
            const pictureRef = ref(storage, dancerData.profilePicture);
            const pictureUrl = await getDownloadURL(pictureRef);
            setProfilePictureUrl(pictureUrl);
            console.log('✅ Fallback profile picture loaded for:', name);
          } catch (error) {
            console.log('❌ Could not load fallback profile picture:', error);
          }
        }
      } else {
        console.log('👤 No dancer document found');
      }

      // Load age category from profile data (publicly readable)
      try {
        console.log('🎂 Loading age category for dancer:', name);
        
        // First check if we have dateOfBirth from profileData (publicly readable)
        if (profileData && profileData.dateOfBirth) {
          console.log('🎂 Birth date found in profile:', profileData.dateOfBirth);
          const category = calculateAgeCategory(profileData.dateOfBirth);
          console.log('🎂 Calculated age category:', category);
          setAgeCategory(category);
          console.log('🎂 Age category set in state:', category);
        } 
        // Fallback: try profileClaims (requires authentication)
        else {
          try {
            const claimQuery = query(
              collection(db, 'profileClaims'), 
              where('dancerName', '==', name),
              where('status', '==', 'approved')
            );
            const claimSnapshot = await getDocs(claimQuery);
            
            console.log('🎂 Found claim documents:', claimSnapshot.size);
            
            if (!claimSnapshot.empty) {
              const claimData = claimSnapshot.docs[0].data();
              console.log('🎂 Claim data:', claimData);
              
              if (claimData.birthDate) {
                console.log('🎂 Birth date found in claim:', claimData.birthDate);
                const category = calculateAgeCategory(claimData.birthDate);
                console.log('🎂 Calculated age category:', category);
                setAgeCategory(category);
                console.log('🎂 Age category set in state:', category);
              } else {
                setAgeCategory(null);
                console.log('🎂 No birth date found in claim profile');
              }
            } else {
              setAgeCategory(null);
              console.log('🎂 No approved claim profile found');
            }
          } catch (claimError) {
            console.log('ℹ️ Could not load from profileClaims (may require authentication):', claimError.message);
            setAgeCategory(null);
          }
        }
      } catch (error) {
        console.error('❌ Error loading age category:', error);
        setAgeCategory(null);
      }
    } catch (error) {
      console.error('❌ Error loading profile data:', error);
      setProfileData(null);
      setProfilePictureUrl(null);
      setAgeCategory(null);
    }
  };

  // 🔄 Handle profile update
  const handleProfileUpdated = async () => {
    // Force reload by clearing current profile data first
    setProfileData(null);
    setProfilePictureUrl(null);
    setProfilePictureLoading(false);
    
    // Reload profile data
    await loadProfileData();
    
    // Switch to overview tab to show the updated profile
    setActiveTab('overview');
  };

  // 🔄 Handle claim submitted
  const handleClaimSubmitted = () => {
    // The useProfileClaimed hook should automatically refresh
  };

  // ✅ Check if user can edit this profile
  const canEditProfile = () => {
    if (!user) return false;
    if (isAdmin) return true;
    return isProfileClaimed && claimedBy?.userId === user.uid;
  };

  // 🔒 Check if social media editing is allowed based on age category
  const canEditSocialMedia = () => {
    if (!canEditProfile()) return false;
    
    // Check if dancer is in allowed age categories: Junior II, Youth, Adult
    // This applies to EVERYONE, including admins, based on the dancer's actual age
    const allowedCategories = ['Junior II', 'Youth', 'Adult'];
    
    // If we have an age category calculated from the dancer's profile
    if (ageCategory) {
      return allowedCategories.includes(ageCategory);
    }
    
    // If no age category is available, calculate it from available data
    // Try to get current age category using the priority system
    const currentAgeCategory = calculateAgeCategoryFromPriority();
    if (currentAgeCategory) {
      return allowedCategories.includes(currentAgeCategory);
    }
    
    // If no age information is available, restrict social media access for safety
    return false;
  };

  // 🎂 Save manual year of birth (admin only)
  const saveManualYearOfBirth = async (year) => {
    if (!isAdmin) {
      console.warn('❌ Only admins can set manual year of birth');
      return;
    }
    
    setYearOfBirthLoading(true);
    try {
      const profileRef = doc(db, 'dancerProfiles', name);
      
      // Update or merge the document
      await setDoc(profileRef, {
        manualYearOfBirth: year ? parseInt(year) : null,
        lastUpdated: new Date()
      }, { merge: true });
      
      // Also update local state
      setManualYearOfBirth(year ? parseInt(year) : null);
      setEditingYearOfBirth(false);
      
      console.log('✅ Saved manual year of birth:', year);
    } catch (error) {
      console.error('❌ Failed to save manual year of birth:', error);
    } finally {
      setYearOfBirthLoading(false);
    }
  };

  // 🔓 Unclaim profile function (admin only with double-confirm)
  const unclaimProfile = async () => {
    if (!isAdmin) {
      console.warn('❌ Only admins can unclaim profiles');
      return;
    }

    if (unclaimConfirmStep === 0) {
      // First click - show warning
      setUnclaimConfirmStep(1);
      // Reset after 5 seconds if not confirmed
      setTimeout(() => {
        setUnclaimConfirmStep(0);
      }, 5000);
      return;
    }

    if (unclaimConfirmStep === 1) {
      // Second click - proceed with unclaim
      setUnclaimLoading(true);
      try {
        console.log('🔓 Starting unclaim process for:', name);
        
        // 1. Remove claim from dancers collection
        const dancerRef = doc(db, 'dancers', name);
        const dancerSnap = await getDoc(dancerRef);
        if (dancerSnap.exists()) {
          await updateDoc(dancerRef, {
            claimedBy: null,
            profileClaimed: false,
            lastUpdated: new Date()
          });
          console.log('✅ Removed claim from dancers collection');
        }

        // 2. Remove claim from dancerProfiles collection
        const profileRef = doc(db, 'dancerProfiles', name);
        const profileSnap = await getDoc(profileRef);
        if (profileSnap.exists()) {
          await updateDoc(profileRef, {
            claimedBy: null,
            profileClaimed: false,
            lastUpdated: new Date()
          });
          console.log('✅ Removed claim from dancerProfiles collection');
        }

        // 3. Reset local states
        setProfileData(prev => prev ? { ...prev, claimedBy: null, profileClaimed: false } : null);
        setUnclaimConfirmStep(0);
        
        console.log('✅ Profile unclaimed successfully! User can re-claim with same or different data.');
        alert('✅ Profile has been unclaimed successfully!\n\nThe dancer can now claim this profile again (admin must still approve any new claim).');
        
        // Reload the page to refresh claim status
        window.location.reload();
        
      } catch (error) {
        console.error('❌ Failed to unclaim profile:', error);
        alert('❌ Error unclaiming profile: ' + error.message);
      } finally {
        setUnclaimLoading(false);
      }
    }
  };

  // ✏️ Rename Dancer (Admin only) — renames across all competition docs, roundData, and profile collections
  const renameDancer = async () => {
    if (!isAdmin) return;
    const newName = newDancerNameInput.trim();
    if (!newName || newName === name) {
      setRenameStatus('❌ Please enter a different name.');
      return;
    }
    setRenameLoading(true);
    setRenameStatus('⏳ Renaming across all competition documents…');
    try {
      // ── 1. Rename in all competition docs (results[] and roundData[]) ──────
      const allCollections = [
        getDocs(collection(db, 'competitions')),
      ];
      const [mainSnap] = await Promise.all(allCollections);

      const batchArr = [];
      let cur = writeBatch(db);
      let opCount = 0;
      let docsUpdated = 0;

      const applyToSnap = (snap) => {
        snap.forEach(docSnap => {
          const data = docSnap.data();
          let changed = false;
          const update = {};

          if (data.results) {
            const newResults = data.results.map(r => {
              if (r.dancerName === name) { changed = true; return { ...r, dancerName: newName }; }
              return r;
            });
            if (changed) update.results = newResults;
          }

          if (data.roundData) {
            const newRD = data.roundData.map(r => {
              if (r.dancerName === name) { changed = true; return { ...r, dancerName: newName }; }
              return r;
            });
            if (changed) update.roundData = newRD;
          }

          if (changed) {
            cur.update(docSnap.ref, update);
            opCount++;
            docsUpdated++;
            if (opCount >= 490) { batchArr.push(cur); cur = writeBatch(db); opCount = 0; }
          }
        });
      };

      applyToSnap(mainSnap);

      // disciplines subcollection competitions
      const disciplinesSnap = await getDocs(collection(db, 'disciplines'));
      for (const discDoc of disciplinesSnap.docs) {
        const catSnap = await getDocs(collection(db, 'disciplines', discDoc.id, 'categories'));
        for (const catDoc of catSnap.docs) {
          try {
            const compSnap = await getDocs(collection(db, 'disciplines', discDoc.id, 'categories', catDoc.id, 'competitions'));
            applyToSnap(compSnap);
          } catch (_) {}
          try {
            const danceSnap = await getDocs(collection(db, 'disciplines', discDoc.id, 'categories', catDoc.id, 'dances'));
            for (const danceDoc of danceSnap.docs) {
              try {
                const dCompSnap = await getDocs(collection(db, 'disciplines', discDoc.id, 'categories', catDoc.id, 'dances', danceDoc.id, 'competitions'));
                applyToSnap(dCompSnap);
              } catch (_) {}
            }
          } catch (_) {}
        }
      }

      if (opCount > 0) batchArr.push(cur);
      for (const b of batchArr) await b.commit();
      setRenameStatus(`⏳ Updated ${docsUpdated} competition docs. Migrating profile…`);

      // ── 2. Migrate dancerProfiles and dancers docs ────────────────────────
      for (const colName of ['dancerProfiles', 'dancers']) {
        const oldRef = doc(db, colName, name);
        const oldSnap = await getDoc(oldRef);
        if (oldSnap.exists()) {
          // Write to new doc ID, preserving all data (including claimedBy)
          await setDoc(doc(db, colName, newName), { ...oldSnap.data(), name: newName });
          await deleteDoc(oldRef);
        }
      }

      setRenameStatus(`✅ Renamed to "${newName}" successfully! Redirecting…`);
      setEditingDancerName(false);
      setNewDancerNameInput('');

      // Navigate to new profile URL
      setTimeout(() => {
        navigate(`/dancer/${encodeURIComponent(newName)}`, { replace: true });
        window.location.reload();
      }, 1200);

    } catch (error) {
      console.error('❌ Rename failed:', error);
      setRenameStatus('❌ Error: ' + error.message);
    } finally {
      setRenameLoading(false);
    }
  };

  // 📊 Load best days visibility setting
  useEffect(() => {
    const loadBestDaysVisibilitySetting = async () => {
      try {
        const settingsRef = doc(db, "adminSettings", "bestDaysVisibility");
        const settingsSnap = await getDoc(settingsRef);
        
        if (settingsSnap.exists()) {
          setHideBestDaysFromPublic(settingsSnap.data().hideFromPublic || false);
        }
        setBestDaysSettingLoaded(true);
      } catch (error) {
        console.error("Error loading best days visibility setting:", error);
        setBestDaysSettingLoaded(true);
      }
    };
    
    loadBestDaysVisibilitySetting();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (!name) return;
      
      console.log(`🔍 Loading dancer profile for: ${name}`);
      
      // Track profile visit (fire-and-forget — does NOT block profile loading)
      try {
        const functions = getFunctions();
        const trackProfileVisit = httpsCallable(functions, 'trackProfileVisit');
        trackProfileVisit({ profileName: name, profileType: 'dancer' })
          .then(result => {
            if (result.data && result.data.totalVisits !== undefined) {
              setVisitorCount(result.data.totalVisits);
            }
          })
          .catch(error => {
            console.log('Visit tracking error (non-critical):', error.message);
          });
      } catch (error) {
        console.log('Visit tracking setup error (non-critical):', error.message);
      }
      
      // 🚀 IMMEDIATE FEEDBACK: Set basic profile structure instantly
      setDancerData({
        name: name,
        studio: "Loading...",
        results: [],
        disciplines: [],
        categories: [],
        disciplineOverview: [],
        needsCompetitionResults: false // Keep false at mount — loadData will set true if needed AFTER it finishes.
        // This ensures the lazy load only fires after loadData completes and sets loading:false,
        // so the profile renders from cache first (~2s LCP) and competition results load silently afterward.
      });
      setLoading(true); // Show loading state with basic info
      
      // Reset auto-exclusions flag for new dancer
      autoExclusionsCalculated.current = false;
      // Reset loaded profile ref for new dancer (prevents stale data from previous dancer)
      loadedProfileDataRef.current = null;
      
      // 🎂 CRITICAL: Load profile data FIRST to ensure manual birth year is available for cache decision
      console.log('🔍 Loading profile data before cache check...');
      await loadProfileData();
      
      // 🚀 PERFORMANCE: Load cache first — avoids the expensive full competitions scan for studio lookup
      console.log('🏢 Loading cache data first for studio lookup...');
      const cachedRankings = await getGlobalCachedDancerRankings(name);
      const cachedStudio = cachedRankings?.studio || null;
      
      // 🚀 CRITICAL: Load transfer history and current studio FIRST
      // Pass cached studio as fallback so we skip the full competitions scan when no transfers exist
      console.log('🏢 Loading transfer history and current studio...');
      await loadTransferHistory(null, cachedStudio);
      
      // Also try legacy cache if global cache not found
      let finalCachedRankings = cachedRankings;
      if (!cachedRankings || !cachedRankings.disciplineOverview) {
        console.log(`⚠️ No global cache found for: ${name}, trying legacy cache...`);
        finalCachedRankings = await getCachedDancerRankings(name);
        console.log(`🔍 Legacy cache lookup result:`, finalCachedRankings ? 'Found' : 'Not found');
      }
      
      console.log(`🔍 Final cache lookup result:`, finalCachedRankings ? 'Found' : 'Not found');
      
      let shouldRefreshCache = false;
      let useStaleCache = false;
      
      if (finalCachedRankings && finalCachedRankings.disciplineOverview) {
        // Check if cache is fresh (less than 24 hours old)
        const cacheAge = finalCachedRankings.lastCalculated ? Date.now() - finalCachedRankings.lastCalculated.toMillis() : Infinity;
        const twentyFourHours = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
        
        // 🎂 Force recalculation if manual birth year exists (to ensure historical validation is applied)
        const hasManualBirthYear = profileData && profileData.manualYearOfBirth;
        
        // 🎂 Also force recalculation for claimed profiles with birth dates (same reason)
        const isClaimedWithBirthDate = profileData && profileData.profileClaimed && profileData.dateOfBirth;
        
        console.log(`🔍 Cache decision check - profileData:`, profileData ? 'Found' : 'Not found');
        console.log(`🔍 Manual birth year check:`, hasManualBirthYear ? `Yes (${profileData.manualYearOfBirth})` : 'No');
        console.log(`🔍 Claimed profile check:`, isClaimedWithBirthDate ? 'Yes (claimed with birth date)' : 'No');
        
        if (cacheAge < twentyFourHours && !hasManualBirthYear && !isClaimedWithBirthDate) {
          console.log("✅ Found fresh cached rankings (< 24h), will use for instant loading");
        } else if (hasManualBirthYear) {
          console.log("🎂 Manual birth year detected - forcing recalculation to apply historical validation");
          shouldRefreshCache = true;
          useStaleCache = true; // Still use stale cache while recalculating
        } else if (isClaimedWithBirthDate) {
          console.log("🎂 Claimed profile with birth date detected - forcing recalculation to detect wrong age category results");
          shouldRefreshCache = true;
          useStaleCache = true; // Still use stale cache while recalculating
        } else {
          const cacheAgeHours = Math.round(cacheAge / (60 * 60 * 1000));
          console.log(`⚠️ Cached rankings are stale (${cacheAgeHours}h old), will use as fallback and refresh in background`);
          shouldRefreshCache = true;
          useStaleCache = true; // Use stale cache as fallback
        }
        
        if (!shouldRefreshCache || useStaleCache) {
          
          // 🚀 SUPER FAST LOAD: Use cached competition results if available
          let cachedResults = [];
          if (finalCachedRankings.results && Array.isArray(finalCachedRankings.results)) {
            cachedResults = finalCachedRankings.results;
            console.log(`✅ Found ${cachedResults.length} cached competition results`);
          } else {
            console.log("⚠️ No cached results found, will mark as needs loading");
          }
          
          // 🚀 FAST LOAD: Store cached ranking data for immediate overview display
          setCachedRankings(finalCachedRankings);
          
          // Set complete dancerData structure with cached results.
          // Use functional update so we can see if the lazy load already ran while loadProfileData was awaiting.
          // If results are already loaded, preserve them and do NOT re-trigger the lazy load.
          setDancerData(prevData => {
            const alreadyLoadedResults = prevData?.results?.length > 0 ? prevData.results : null;
            return {
              studio: currentStudio || finalCachedRankings.studio || "Studio not specified",
              results: alreadyLoadedResults ?? cachedResults, // keep lazy-load results if they arrived first
              disciplines: finalCachedRankings.disciplines || [],
              categories: finalCachedRankings.categories || [],
              disciplineOverview: finalCachedRankings.disciplineOverview || [],
              needsCompetitionResults: alreadyLoadedResults ? false : (cachedResults.length === 0)
            };
          });
          
          setLoading(false); // Fast load complete
          
          if (cachedResults.length > 0) {
            console.log("✅ Profile loaded instantly from cache with full competition results!");
          } else {
            console.log("✅ Profile overview loaded from cache, competition results will load on demand");
          }
          
          // If cache is stale, show message to user
          if (useStaleCache && shouldRefreshCache) {
            const cacheAge = finalCachedRankings.lastCalculated ? Date.now() - finalCachedRankings.lastCalculated.toMillis() : Infinity;
            const cacheAgeHours = Math.round(cacheAge / (60 * 60 * 1000));
            console.log(`ℹ️ Using stale cache (${cacheAgeHours}h old) - please recalculate for latest data`);
          }
        }
      } else {
        console.log("⚠️ No cached rankings found at all");
        shouldRefreshCache = true;
      }
      
      // 🔄 CONDITIONAL REFRESH: Only show "no data" if there's truly no cache at all
      if (shouldRefreshCache && !useStaleCache && !finalCachedRankings) {
        console.log("🔍 No cache available - showing fallback state...");
        
        // Note: The expensive full calculation was moved to the background
        // For now, use basic fallback data
        setDancerData(prevData => {
          const alreadyLoadedResults = prevData?.results?.length > 0 ? prevData.results : null;
          return {
            name: name,
            studio: currentStudio || "Studio not specified",
            results: alreadyLoadedResults ?? [],
            disciplines: [],
            categories: [],
            disciplineOverview: [],
            needsCompetitionResults: !alreadyLoadedResults // trigger lazy load only if not already loaded
          };
        });
        
        setLoading(false);
        console.log("⚠️ No cache available - using basic fallback, full calculation needed");
      }
    };

    loadData();
  }, [name]); // Only re-run when name changes (profileData is loaded inside the effect)

  // 🚀 LAZY LOADING: Load competition results when needed for competitions tab OR overview historical categories
  useEffect(() => {
    const loadCompetitionResults = async () => {
      // Load results if:
      // 1. User is on competitions tab AND needs competition results AND no results exist
      // 2. User is on overview tab AND we need results for historical categories AND don't have them yet
      // 3. ADMIN SPECIAL CASE: For competitions tab, always load fresh if user is admin to ensure latest toggle states
      const hasExistingResults = dancerData?.results && dancerData.results.length > 0;
      
      const needsForCompetitions = (activeTab === "competitions" && 
                                   (dancerData?.needsCompetitionResults || (isAdmin && !hasExistingResults)));
      
      const needsForOverview = (activeTab === "overview" && 
                               dancerData?.needsCompetitionResults && 
                               !hasExistingResults);
      
      if (needsForCompetitions || needsForOverview) {
        console.log(`🔍 Loading competition results for ${activeTab} tab...${isAdmin && activeTab === "competitions" ? " (ADMIN - ensuring fresh data)" : ""}`);
        
        try {
          // Only show full-page spinner if nothing is displayed yet (no cache loaded).
          // When the profile is already visible (cachedRankings is set), results load silently
          // in the background — no spinner re-show, no double-reload.
          if (!cachedRankings) {
            setLoading(true);
          }
          
          const oneYearAgo = new Date();
          oneYearAgo.setDate(oneYearAgo.getDate() - 365);
          
          // For ranking calculation: exactly 365 days from today
          const rankingCutoff = new Date();
          rankingCutoff.setDate(rankingCutoff.getDate() - 365);

          // 🚀 PERFORMANCE: Load competition results for display
          // Load competitions data once and store for reuse
          let competitionsData = sharedCompetitionsData.current;
          
          if (!competitionsData) {
            console.log("🔍 Loading competitions data for the first time...");
            const oldQuery = query(collection(db, "competitions"));
            const oldSnapshot = await getDocs(oldQuery);
            console.log("🔍 Old structure results:", oldSnapshot.size, "documents");
            
            // Store competitions data for reuse
            competitionsData = oldSnapshot.docs.map(doc => ({
              id: doc.id,
              data: doc.data()
            }));
            sharedCompetitionsData.current = competitionsData;
            console.log("✅ Competitions data loaded and cached for reuse");
          } else {
            console.log("🚀 Using cached competitions data");
          }

          const results = [];
          const compNameMapTemp = {};
          let studio = currentStudio; // Initialize with studio from transfers
          
          // Get dancer's current age category once for efficiency (same as Leaderboard logic)
          const currentAgeCategory = await getDancerCurrentAgeCategory(name);
          console.log(`🔍 Current age category for ${name}: ${currentAgeCategory}`);
          
          // Get dancer's birth date for historical age category calculations
          const birthDatesMap = await preloadBirthDates();
          const dancerBirthDate = getDancerBirthDate(name, birthDatesMap);
          console.log(`🔍 Birth date lookup result for ${name}:`, dancerBirthDate ? 'Found' : 'Not found');
          
          // Process each competition document
          for (const competition of competitionsData) {
            const comp = competition.data;
            const compId = competition.id;
            const compName = comp.name || compId;
            compNameMapTemp[compId] = compName;
            const multiplier = comp.multiplier || 1;

            if (comp.results) {
              for (const r of comp.results) {
                if (r.dancerName === name) {
                  const score = Math.round((r.points || 0) * multiplier);
                  
                  // Determine if this result counts for ranking (within 365 days)
                  const resultDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
                  const isWithin365Days = resultDate >= rankingCutoff;
                  
                  // Check age category validity using historical validation
                  let isValidAgeCategory = true;
                  let ageCategoryInCompetitionYear = null;
                  
                  console.log(`🔍 Validating result for ${name}: ${comp.category} from ${resultDate.getFullYear()}`);
                  
                  if (dancerBirthDate) {
                    console.log(`🔍 Using historical validation with birth date`);
                    const competitionYear = resultDate.getFullYear();
                    // Convert Date object to string format expected by calculateAgeCategoryForYear
                    const birthDateString = typeof dancerBirthDate === 'string' 
                      ? dancerBirthDate 
                      : `${dancerBirthDate.getFullYear()}-${String(dancerBirthDate.getMonth() + 1).padStart(2, '0')}-${String(dancerBirthDate.getDate()).padStart(2, '0')}`;
                    console.log(`🔍 Birth date string: ${birthDateString}`);
                    ageCategoryInCompetitionYear = calculateAgeCategoryForYear(birthDateString, competitionYear);
                    isValidAgeCategory = shouldResultCountForRankingsInYear(comp.category, ageCategoryInCompetitionYear);
                    console.log(`🔍 Competition year: ${competitionYear}, Age category then: ${ageCategoryInCompetitionYear}, Valid: ${isValidAgeCategory}`);
                  } else {
                    console.log(`🔍 Using fallback validation with current age category`);
                    // Fallback to current age category if no birth date (backwards compatibility)
                    isValidAgeCategory = shouldResultCountForRankings(comp.category, currentAgeCategory);
                  }
                  
                  // Determine if result is historical due to age category change
                  const isHistoricalDueToAgeChange = dancerBirthDate && currentAgeCategory && ageCategoryInCompetitionYear &&
                    ageCategoryInCompetitionYear !== currentAgeCategory;
                  
                  // Result counts for ranking if within 365 days AND not manually excluded AND not auto-excluded AND not invalid AND valid age category
                  const countsForRanking = isWithin365Days && !r.excluded && !r.autoExcluded && !r.invalid && isValidAgeCategory;
                  
                  // 🔍 DEBUG: Log age category exclusions and historical results
                  if (!isValidAgeCategory && isWithin365Days && !r.excluded && !r.autoExcluded && !r.invalid) {
                    const competitionYear = resultDate.getFullYear();
                    console.log(`❌ Invalid result: ${name}'s ${comp.category} result from ${competitionYear} (was ${ageCategoryInCompetitionYear || 'unknown'}, competed in wrong category)`);
                  }
                  if (isHistoricalDueToAgeChange && isValidAgeCategory) {
                    const competitionYear = resultDate.getFullYear();
                    console.log(`📅 Historical result: ${name}'s ${comp.category} result from ${competitionYear} (was ${ageCategoryInCompetitionYear}, now ${currentAgeCategory})`);
                  }
                  if (dancerBirthDate && isValidAgeCategory) {
                    const competitionYear = resultDate.getFullYear();
                    console.log(`✅ Valid result: ${name}'s ${comp.category} result from ${competitionYear} (was ${ageCategoryInCompetitionYear}, valid for that age category)`);
                  }
                  
                  // 🐛 DEBUG: Log database values for excluded/invalid flags
                  if (r.excluded || r.invalid) {
                    console.log(`🔍 Found flagged result in database:`, {
                      dancer: r.dancerName,
                      compId,
                      discipline: comp.discipline,
                      category: comp.category,
                      dance: comp.dance,
                      excluded: r.excluded,
                      invalid: r.invalid
                    });
                  }
                  
                  results.push({
                    compId,
                    points: score,
                    place: r.place,
                    multiplier,
                    timestamp: comp.timestamp,
                    studio: getStudioForDate(new Date(comp.date)) || r.studio || "-",
                    originalStudio: r.studio || "-",
                    discipline: comp.discipline || "Latin",
                    category: comp.category || "-",
                    dance: comp.dance || null,
                    participants: comp.participants || 0,
                    isWithin365Days,
                    countsForRanking,
                    excluded: r.excluded || false,
                    invalid: r.invalid || false,
                    autoExcluded: false,
                    ageExcluded: !isValidAgeCategory, // Track age category exclusions separately
                    isHistoricalDueToAgeChange, // Track if result is historical due to age category change
                    ageCategoryInCompetitionYear, // Store the age category dancer was in during competition
                    exclusionReason: r.excluded ? "manual" : null,
                    needsAutoExclusionCheck: isWithin365Days,
                    withdrawn: r.withdrawn || r.withdraw === "true" || r.withdraw === true,
                    // 📤 Upload time for the 24h share feature
                    uploadedAt: comp.uploadedAt || null,
                  });
                  studio = r.studio || studio;
                }
              }
            }
          }

          console.log(`✅ Loaded ${results.length} competition results from old structure only`);
          
          // � DEBUG: Show database values for excluded/invalid before preservation
          const flaggedResults = results.filter(r => r.excluded || r.invalid);
          if (flaggedResults.length > 0) {
            console.log(`🔍 Found ${flaggedResults.length} results with flags from database:`, flaggedResults.map(r => ({
              compId: r.compId,
              discipline: r.discipline,
              category: r.category,
              dance: r.dance,
              excluded: r.excluded,
              invalid: r.invalid
            })));
          }
          
          // �🔄 PRESERVE LOCAL CHANGES: Merge loaded results with existing local modifications
          setDancerData(prevData => {
            let mergedResults = results;
            
            // If we already have results, preserve local changes to excluded/invalid flags
            if (prevData?.results && prevData.results.length > 0) {
              console.log('🔄 Merging local changes with fresh database data...');
              
              mergedResults = results.map(newResult => {
                // Find corresponding existing result that might have local changes
                const existingResult = prevData.results.find(existing => 
                  existing.compId === newResult.compId &&
                  existing.discipline === newResult.discipline &&
                  existing.category === newResult.category &&
                  (newResult.dance ? existing.dance === newResult.dance : true)
                );
                
                if (existingResult) {
                  // Preserve local modifications to excluded and invalid flags
                  return {
                    ...newResult,
                    excluded: existingResult.excluded,
                    invalid: existingResult.invalid,
                    autoExcluded: existingResult.autoExcluded,
                    exclusionReason: existingResult.exclusionReason,
                    // Recalculate countsForRanking with preserved flags
                    countsForRanking: newResult.isWithin365Days && 
                                     !existingResult.excluded && 
                                     !existingResult.autoExcluded && 
                                     !existingResult.invalid
                  };
                }
                
                // If no existing result found, use new result as-is
                return newResult;
              });
              
              console.log(`✅ Preserved local changes for ${mergedResults.filter((r, i) => 
                r.excluded !== results[i]?.excluded || r.invalid !== results[i]?.invalid
              ).length} modified results`);
            }
            
            return {
              ...prevData,
              results: mergedResults,
              needsCompetitionResults: false // Mark as loaded
            };
          });
          
          setCompNameMap(compNameMapTemp);
          setLoading(false);
          
        } catch (error) {
          console.error("❌ Error loading competition results:", error);
          setLoading(false);
        }
      }
    };

    loadCompetitionResults();
  }, [activeTab, dancerData?.needsCompetitionResults, name]);

  // 🔄 RECALCULATE OVERVIEW: When results change (exclude/include, valid/invalid), recalculate overview
  useEffect(() => {
    const recalculateOverview = async () => {
      // Don't recalculate during initial loading or if no results
      if (!dancerData?.results || dancerData.results.length === 0 || loading) return;
      
      console.log('🔄 Recalculating discipline overview after result modifications...');
      
      // Filter out invalid results from ALL calculations (including age-excluded results)
      const validResults = dancerData.results.filter(r => !r.invalid && !r.ageExcluded);
      
      console.log(`🔍 Filtering results: Total ${dancerData.results.length}, Valid ${validResults.length}, Invalid ${dancerData.results.filter(r => r.invalid).length}, Age-excluded ${dancerData.results.filter(r => r.ageExcluded).length}`);
      
      // Group by category-dance only (ignore discipline field to prevent duplicates)
      // Some results have discipline field, some don't - this creates duplicate rows
      const groupedResults = {};
      validResults.forEach(result => {
        // Normalize dance field - treat null, undefined, empty string, and discipline-matching values as the same
        let normalizedDance = result.dance;
        
        // Extract discipline from category first to use for comparison
        const categoryLower = (result.category || '').toLowerCase();
        let extractedDiscipline = result.discipline;
        if (!extractedDiscipline || extractedDiscipline === 'Latin') {
          if (categoryLower.includes('hip hop')) {
            extractedDiscipline = 'Hip Hop';
          } else if (categoryLower.includes('contemporary')) {
            extractedDiscipline = 'Contemporary';
          } else if (categoryLower.includes('ballet')) {
            extractedDiscipline = 'Ballet';
          } else {
            extractedDiscipline = result.discipline || 'Latin';
          }
        }
        
        // If dance value matches the discipline, treat it as null (redundant)
        if (normalizedDance === extractedDiscipline) {
          normalizedDance = null;
        }
        
        const key = `${result.category}${normalizedDance ? `-${normalizedDance}` : ''}`;
        if (!groupedResults[key]) {
          groupedResults[key] = {
            discipline: extractedDiscipline,
            category: result.category,
            dance: normalizedDance, // Use normalized dance (null if redundant with discipline)
            results: []
          };
        }
        groupedResults[key].results.push(result);
      });

      // Calculate updated discipline overview
      const updatedDisciplineOverview = Object.values(groupedResults).map(group => {
        // Only use valid results from last 365 days for ranking calculation
        const rankingGroupResults = group.results.filter(r => r.countsForRanking);
        const sortedResults = rankingGroupResults.sort((a, b) => b.points - a.points);
        const top6Results = sortedResults.slice(0, 6);
        const totalPoints = top6Results.reduce((sum, r) => sum + r.points, 0);
        
        // 🎂 Check if we have manual birth year - if so, don't preserve old overview metrics
        const hasManualBirthYear = profileData && profileData.manualYearOfBirth;
        
        // Debug logging for this category/dance combination
        console.log(`🔍 Overview calc for ${group.discipline} ${group.category}${group.dance ? ` ${group.dance}` : ''}:`, {
          totalResults: group.results.length,
          rankingResults: rankingGroupResults.length,
          totalPoints,
          hasManualBirthYear
        });
        
        // 🎂 For manual birth year, check if this category is still valid for current year
        let shouldResetRankingData = hasManualBirthYear;
        if (hasManualBirthYear && profileData.manualYearOfBirth) {
          const currentYear = new Date().getFullYear();
          const currentAgeCategory = calculateAgeCategoryForYear(`${profileData.manualYearOfBirth}-01-01`, currentYear);
          
          // Extract the age category from the full category name (e.g., "Solo Junior II" -> "Junior II")
          const categoryAgeGroup = group.category.replace(/^(Solo |Couple )?/, ''); // Remove prefix
          
          // Define age category hierarchy (youngest to oldest)
          const ageCategoryHierarchy = ['Junior I', 'Junior II', 'Youth', 'Adult'];
          const currentIndex = ageCategoryHierarchy.indexOf(currentAgeCategory);
          const categoryIndex = ageCategoryHierarchy.indexOf(categoryAgeGroup);
          
          console.log(`🔍 Category analysis for ${group.category}:`);
          console.log(`  - Full category: ${group.category}`);
          console.log(`  - Extracted age group: ${categoryAgeGroup}`);
          console.log(`  - Current age category: ${currentAgeCategory}`);
          console.log(`  - Current index: ${currentIndex}, Category index: ${categoryIndex}`);
          
          // Allow current category and one category higher (dancers can compete up one level)
          const isCurrentCategoryValid = categoryAgeGroup === currentAgeCategory;
          const isOneCategoryHigher = categoryIndex === currentIndex + 1;
          const isCategoryAllowed = isCurrentCategoryValid || isOneCategoryHigher;
          
          console.log(`  - Is current category: ${isCurrentCategoryValid}`);
          console.log(`  - Is one category higher: ${isOneCategoryHigher}`);
          console.log(`  - Is category allowed: ${isCategoryAllowed}`);
          
          if (!isCategoryAllowed) {
            console.log(`🎂 Category ${group.category} (${categoryAgeGroup}) is no longer valid (current: ${currentAgeCategory}, can compete up to: ${ageCategoryHierarchy[currentIndex + 1] || 'Adult'}) - resetting ranking data`);
            shouldResetRankingData = true;
          } else {
            const reason = isCurrentCategoryValid ? 'current category' : 'one category higher (allowed)';
            console.log(`🎂 Category ${group.category} (${categoryAgeGroup}) is still valid for current year (${reason}) - preserving active ranking data`);
            shouldResetRankingData = false; // Don't reset if it's allowed
          }
        }
        
        // Find existing overview data for both preserving current data and historical data
        const existingOverview = dancerData.disciplineOverview?.find(d => 
          d.discipline === group.discipline && 
          d.category === group.category && 
          d.dance === group.dance
        );
        
        if (shouldResetRankingData) {
          console.log(`🎂 Manual birth year detected - not preserving cached overview for ${group.discipline} ${group.category}${group.dance ? ` ${group.dance}` : ''}`);
        }
        
        // For invalid categories, calculate historical best rank and days for the eligible period
        let historicalBestRank = null;
        let historicalBestDays = 0;
        
        if (shouldResetRankingData && profileData?.manualYearOfBirth) {
          // Extract the age category from the full category name (e.g., "Solo Junior II" -> "Junior II")
          const categoryAgeGroup = group.category.replace(/^(Solo |Couple )?/, ''); // Remove prefix
          
          // Find the actual last year they were eligible for this specific category
          let actualLastEligibleYear = null;
          const currentYear = new Date().getFullYear();
          
          // Check backward from current year to find the last year they were in this category
          for (let year = currentYear; year >= 2020; year--) {
            const yearAgeCategory = calculateAgeCategoryForYear(`${profileData.manualYearOfBirth}-01-01`, year);
            if (yearAgeCategory === categoryAgeGroup) {
              actualLastEligibleYear = year;
              break; // Found the most recent year they were eligible
            }
          }
          
          if (actualLastEligibleYear) {
            // Calculate historical best rank and days for results until end of last eligible year
            const eligibleEndDate = new Date(actualLastEligibleYear, 11, 31, 23, 59, 59); // End of December 31st
            
            // Filter results for this category that occurred during the eligible period
            const eligibleResults = group.results.filter(result => {
              const resultDate = result.timestamp instanceof Timestamp ? result.timestamp.toDate() : new Date(result.timestamp);
              return resultDate <= eligibleEndDate && result.place && result.place !== 'DQ' && result.place !== 'DNS' && !result.withdrawn;
            });
            
            if (eligibleResults.length > 0) {
              // Sort results chronologically
              const sortedResults = eligibleResults.sort((a, b) => {
                const dateA = a.timestamp instanceof Timestamp ? a.timestamp.toDate() : new Date(a.timestamp);
                const dateB = b.timestamp instanceof Timestamp ? b.timestamp.toDate() : new Date(b.timestamp);
                return dateA - dateB;
              });
              
              // Find best place achieved during eligible period
              const validPlaces = sortedResults
                .map(r => parseInt(r.place))
                .filter(place => !isNaN(place));
              
              if (validPlaces.length > 0) {
                historicalBestRank = Math.min(...validPlaces);
                
                // Calculate maximum consecutive days at best rank during eligible period
                let currentRank = null;
                let currentStart = null;
                let maxDays = 0;
                
                sortedResults.forEach((result, index) => {
                  const place = parseInt(result.place);
                  const resultDate = result.timestamp instanceof Timestamp ? result.timestamp.toDate() : new Date(result.timestamp);
                  
                  if (!isNaN(place) && place === historicalBestRank) {
                    if (currentRank !== historicalBestRank) {
                      currentRank = historicalBestRank;
                      currentStart = resultDate;
                    }
                  } else if (currentRank === historicalBestRank) {
                    // Rank changed, calculate days held
                    const days = Math.floor((resultDate - currentStart) / (1000 * 60 * 60 * 24));
                    maxDays = Math.max(maxDays, days);
                    currentRank = null;
                    currentStart = null;
                  }
                });
                
                // If still holding best rank at end of eligible period
                if (currentRank === historicalBestRank && currentStart) {
                  const days = Math.floor((eligibleEndDate - currentStart) / (1000 * 60 * 60 * 24));
                  maxDays = Math.max(maxDays, days);
                }
                
                historicalBestDays = Math.max(maxDays, 1); // Ensure at least 1 day if we have a valid rank
              }
            }
            
            console.log(`🎂 Historical calculation for ${group.category}: Best Rank #${historicalBestRank}, Best Days: ${historicalBestDays} (eligible until ${actualLastEligibleYear})`);
          } else {
            console.log(`🎂 No eligible period found for ${group.category} - no historical data to preserve`);
          }
        }
        
        // For invalid categories, reset current ranking data but preserve/calculate historical best data
        const finalTotalPoints = shouldResetRankingData ? 0 : totalPoints;
        const finalHasCurrentRank = shouldResetRankingData ? false : totalPoints > 0;
        
        // Debug log the final values before returning
        if (shouldResetRankingData) {
          console.log(`🎂 Final values for ${group.category}: historicalBestRank=${historicalBestRank}, historicalBestDays=${historicalBestDays}`);
        }
        
        return {
          discipline: group.discipline,
          category: group.category,
          dance: group.dance,
          totalPoints: finalTotalPoints,
          competitionCount: group.results.length,
          rankingCompetitionCount: shouldResetRankingData ? 0 : rankingGroupResults.length,
          hasResults: group.results.length > 0,
          currentRank: shouldResetRankingData ? null : (existingOverview?.currentRank || null),
          bestRank: shouldResetRankingData ? historicalBestRank : (existingOverview?.bestRank || null),
          daysHeld: shouldResetRankingData ? 0 : (existingOverview?.daysHeld || 0),
          bestDaysHeld: shouldResetRankingData ? historicalBestDays : (existingOverview?.bestDaysHeld || 0),
          hasCurrentRank: finalHasCurrentRank
        };
      }).filter(overview => overview.hasResults)
      .sort((a, b) => {
        // Sort by age category first (highest to lowest)
        const ageCategoryOrder = ['Adult', 'Youth', 'Junior II', 'Junior I', 'Juvenile II', 'Juvenile I'];
        const aIndex = ageCategoryOrder.indexOf(a.category);
        const bIndex = ageCategoryOrder.indexOf(b.category);
        
        if (aIndex !== bIndex) {
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        }
        
        if (a.category === b.category && a.discipline === 'Latin' && b.discipline === 'Latin') {
          const latinDanceOrder = ['Samba', 'ChaChaCha', 'Rumba', 'Pasodoble', 'Jive'];
          const aDanceIndex = latinDanceOrder.indexOf(a.dance);
          const bDanceIndex = latinDanceOrder.indexOf(b.dance);
          
          if (aDanceIndex !== bDanceIndex) {
            if (aDanceIndex === -1) return 1;
            if (bDanceIndex === -1) return -1;
            return aDanceIndex - bDanceIndex;
          }
        }
        
        return 0;
      });

      // Calculate real rankings for the updated overview
      const disciplineOverviewWithRankings = await calculateRealRankings(updatedDisciplineOverview, dancerName, sharedCompetitionsData.current);
      
      // Update the dancer data with new overview and rankings
      setDancerData(prevData => ({
        ...prevData,
        disciplineOverview: disciplineOverviewWithRankings
      }));
      
      console.log('✅ Overview recalculated with updated exclusions/validity and rankings');
    };

    // Immediate recalculation for fast response
    const timeoutId = setTimeout(recalculateOverview, 0);
    return () => clearTimeout(timeoutId);
  }, [dancerData?.results, loading]);

  // 🏆 Calculate Achievement Badges
  useEffect(() => {
    // First, check if badges are available from cache
    if (cachedRankings?.achievementBadges && Array.isArray(cachedRankings.achievementBadges)) {
      console.log('🏆 Using cached achievement badges:', cachedRankings.achievementBadges.length);
      console.log('🏆 Badge IDs:', cachedRankings.achievementBadges.map(b => b.id));
      
      // Filter out Active badge only - keep all other badges including eternal-cup
      let badges = cachedRankings.achievementBadges.filter(badge => badge.id !== 'active');
      
      // LEGACY: Add Eternal Cup Winner badges from old isEternalCupWinner structure (if it exists)
      // This is for backwards compatibility with old cache data
      if (cachedRankings?.isEternalCupWinner?.isWinner && cachedRankings?.isEternalCupWinner?.wins) {
        const wins = cachedRankings.isEternalCupWinner.wins;
        
        // Format category text: remove underscores and fix discipline naming
        const formatCategory = (category) => {
          let formatted = category;
          
          // Remove underscores first
          formatted = formatted.replace(/_/g, ' ');
          
          // Fix Hip Hop duplication: "Hip Hop Solo Hip Hop Adult" → "Hip Hop Solo Adult"
          formatted = formatted.replace('Hip Hop Solo Hip Hop ', 'Hip Hop Solo ');
          
          // Fix Contemporary duplication: "Contemporary Solo Contemporary Adult" → "Contemporary Solo Adult"
          formatted = formatted.replace('Contemporary Solo Contemporary ', 'Contemporary Solo ');
          
          // Fix word order if needed: "Solo Contemporary" → "Contemporary Solo"
          formatted = formatted.replace('Solo Contemporary ', 'Contemporary Solo ');
          formatted = formatted.replace('Solo Hip Hop ', 'Hip Hop Solo ');
          
          return formatted;
        };
        
        // Group wins by category
        const winsByCategory = new Map();
        wins.forEach(win => {
          const formattedCategory = formatCategory(win.category);
          if (!winsByCategory.has(formattedCategory)) {
            winsByCategory.set(formattedCategory, []);
          }
          winsByCategory.get(formattedCategory).push(win.year);
        });
        
        // Create one badge per category, with all years listed
        const eternalCupBadges = Array.from(winsByCategory.entries()).map(([category, years], index) => {
          // Sort years descending (newest first)
          const sortedYears = years.sort((a, b) => b - a);
          const yearsText = sortedYears.join(', ');
          
          return {
            id: `eternal-cup-champion-${index}`,
            icon: '🏆',
            name: 'Eternal Cup Winner',
            description: `${yearsText} - ${category}`,
            years: sortedYears, // Array of years for this category
            category: category,
            color: '#FFD700',
            special: true
          };
        });
        
        // Add at the beginning of the badges array
        badges = [...eternalCupBadges, ...badges];
      }
      
      // Add disciplines list to Diverse badge
      badges = badges.map(badge => {
        if (badge.id === 'diverse' && badge.disciplines && badge.disciplines.length > 0) {
          return {
            ...badge,
            disciplinesList: badge.disciplines // Store for display
          };
        }
        return badge;
      });
      
      setAchievementBadges(badges);
      return;
    }
    
    // Fallback: Calculate badges from results if not cached
    if (!dancerData?.results || dancerData.results.length === 0) {
      console.log('🏆 No results data available for badges');
      setAchievementBadges([]);
      return;
    }

    console.log('🏆 Starting badge calculation with', dancerData.results.length, 'results');
    console.log('🏆 Sample result:', dancerData.results[0]);
    
    const badges = [];
    const results = dancerData.results;
    
    // Note: Eternal Cup Winners are stored in a separate collection and can only be 
    // determined during global cache calculation. They are NOT calculated from competition results.
    // If cache is missing isEternalCupWinner data, the badge won't appear until cache is regenerated.
    
    // 1. Gold Medals - Count 1st place finishes
    const goldMedals = results.filter(r => String(r.place) === '1').length;
    console.log('🥇 Gold medals (place === 1):', goldMedals);
    console.log('🥇 Sample placements:', results.slice(0, 5).map(r => ({ place: r.place, type: typeof r.place })));
    if (goldMedals > 0) {
      badges.push({
        id: 'gold-medal',
        icon: '🥇',
        name: 'Gold Medal',
        count: goldMedals,
        color: '#FFD700'
      });
    }

    // 3. Silver Medals - Count 2nd place finishes
    const silverMedals = results.filter(r => String(r.place) === '2').length;
    console.log('🥈 Silver medals:', silverMedals);
    if (silverMedals > 0) {
      badges.push({
        id: 'silver-medal',
        icon: '🥈',
        name: 'Silver Medal',
        count: silverMedals,
        color: '#C0C0C0'
      });
    }

    // 4. Bronze Medals - Count 3rd place finishes
    const bronzeMedals = results.filter(r => String(r.place) === '3').length;
    console.log('🥉 Bronze medals:', bronzeMedals);
    if (bronzeMedals > 0) {
      badges.push({
        id: 'bronze-medal',
        icon: '🥉',
        name: 'Bronze Medal',
        count: bronzeMedals,
        color: '#CD7F32'
      });
    }

    // 5. Active - Calculate years active
    if (results.length > 0) {
      const dates = results
        .map(r => {
          if (r.date) {
            if (r.date.toDate) return r.date.toDate();
            if (r.date instanceof Date) return r.date;
            return new Date(r.date);
          }
          return null;
        })
        .filter(d => d !== null);
      
      if (dates.length > 0) {
        const oldestDate = new Date(Math.min(...dates.map(d => d.getTime())));
        const yearsActive = Math.floor((new Date() - oldestDate) / (365.25 * 24 * 60 * 60 * 1000));
        
        if (yearsActive >= 1) {
          badges.push({
            id: 'active',
            icon: '⏰',
            name: 'Active',
            count: yearsActive,
            suffix: yearsActive === 1 ? 'year' : 'years',
            color: '#4CAF50'
          });
        }
      }
    }

    // 6. Diverse - Multi-discipline check (only Latin, Contemporary, Hip Hop, Commercial count as separate disciplines)
    const disciplines = [...new Set(results.map(r => r.discipline).filter(Boolean))];
    const validDisciplines = ['Latin', 'Contemporary', 'Hip Hop', 'Commercial'];
    const competedDisciplines = disciplines.filter(d => 
      validDisciplines.some(valid => d.toLowerCase().includes(valid.toLowerCase()))
    );
    
    console.log('🎭 Diverse check:', { 
      allDisciplines: disciplines, 
      competedDisciplines,
      count: competedDisciplines.length 
    });
    
    if (competedDisciplines.length >= 2) {
      const diverseDescription = `Competes in ${competedDisciplines.length} disciplines: ${competedDisciplines.join(', ')}`;
      
      badges.push({
        id: 'diverse',
        icon: '🎭',
        name: 'Diverse',
        description: diverseDescription,
        disciplines: competedDisciplines, // Store disciplines array
        color: '#9C27B0'
      });
    }

    // 7. Rising Star - Has 4th-6th places but no podiums
    const hasPodium = results.some(r => ['1', '2', '3'].includes(String(r.place)));
    const hasTopSix = results.some(r => ['4', '5', '6'].includes(String(r.place)));
    
    if (!hasPodium && hasTopSix) {
      badges.push({
        id: 'rising-star',
        icon: '⭐',
        name: 'Rising Star',
        description: 'Consistently placing 4th-6th',
        color: '#FFC107'
      });
    }

    setAchievementBadges(badges);
    console.log('🏆 Achievement Badges calculated:', badges.length, badges);
  }, [dancerData?.results, cachedRankings]);

  // Auto-scroll carousel for badges on mobile/tablet
  useEffect(() => {
    // Wait for DOM to be ready
    const timer = setTimeout(() => {
      const container = document.querySelector('.badges-scroll-container');
      if (!container || achievementBadges.length <= 1) return;
      
      // Check if on mobile or tablet
      const isMobileOrTablet = window.innerWidth <= 1024;
      if (!isMobileOrTablet) return;
      
      // Check if content is scrollable
      if (container.scrollWidth <= container.clientWidth) return;
      
      let scrollDirection = 1; // 1 for right, -1 for left
      let animationId;
      let isPaused = false;
      let userInteracting = false;
      
      const scroll = () => {
        if (isPaused || userInteracting) {
          animationId = requestAnimationFrame(scroll);
          return;
        }
        
        const currentScroll = container.scrollLeft;
        const maxScroll = container.scrollWidth - container.clientWidth;
        
        // Scroll slowly
        const newScroll = currentScroll + (scrollDirection * 1);
        container.scrollLeft = newScroll;
        
        // Change direction when reaching the end
        if (newScroll >= maxScroll - 1) {
          scrollDirection = -1;
        } else if (newScroll <= 1) {
          scrollDirection = 1;
        }
        
        animationId = requestAnimationFrame(scroll);
      };
      
      // Pause on touch/mouse down
      const handleInteractionStart = () => {
        userInteracting = true;
        isPaused = true;
      };
      
      // Resume after touch/mouse up (with delay)
      const handleInteractionEnd = () => {
        userInteracting = false;
        setTimeout(() => {
          if (!userInteracting) {
            isPaused = false;
          }
        }, 3000); // Resume after 3 seconds of no interaction
      };
      
      // Update on manual scroll
      const handleScroll = () => {
        if (userInteracting) {
          isPaused = true;
        }
      };
      
      // Start animation
      animationId = requestAnimationFrame(scroll);
      
      // Add event listeners
      container.addEventListener('touchstart', handleInteractionStart, { passive: true });
      container.addEventListener('touchend', handleInteractionEnd, { passive: true });
      container.addEventListener('mousedown', handleInteractionStart);
      container.addEventListener('mouseup', handleInteractionEnd);
      container.addEventListener('scroll', handleScroll, { passive: true });
      
      return () => {
        cancelAnimationFrame(animationId);
        if (container) {
          container.removeEventListener('touchstart', handleInteractionStart);
          container.removeEventListener('touchend', handleInteractionEnd);
          container.removeEventListener('mousedown', handleInteractionStart);
          container.removeEventListener('mouseup', handleInteractionEnd);
          container.removeEventListener('scroll', handleScroll);
        }
      };
    }, 100); // Small delay to ensure DOM is ready
    
    return () => clearTimeout(timer);
  }, [achievementBadges]);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (activeBadgeTooltip) {
        setActiveBadgeTooltip(null);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeBadgeTooltip]);

  // 🏆 Compute Eternal Cup Rankings for this dancer (current year, live from Firestore)
  useEffect(() => {
    const computeEternalCupRankings = async () => {
      const overview = cachedRankings?.disciplineOverview || dancerData?.disciplineOverview;
      if (!overview || overview.length === 0 || !name) return;

      // Deduplicate to unique (discipline, category) — Latin groups all dances together
      const seen = new Set();
      const uniqueCombos = [];
      for (const entry of overview) {
        const key = `${entry.discipline}|${entry.category}`;
        if (!seen.has(key)) {
          seen.add(key);
          uniqueCombos.push({ discipline: entry.discipline, category: entry.category });
        }
      }

      if (uniqueCombos.length === 0) return;

      setEternalCupLoading(true);
      try {
        const currentYear = new Date().getFullYear();
        const yearStart = new Date(currentYear, 0, 1);
        const yearEnd = new Date(currentYear + 1, 0, 1);
        const topN = currentYear >= 2026 ? 6 : 4;

        // Load all old-structure competitions once
        const oldSnapshot = await getDocs(query(collection(db, "competitions")));

        const results = [];

        for (const { discipline, category } of uniqueCombos) {
          const dancersMap = new Map();

          oldSnapshot.forEach((docSnap) => {
            const comp = docSnap.data();
            if (!comp.results || !comp.timestamp) return;
            const compDate = comp.timestamp instanceof Timestamp
              ? comp.timestamp.toDate()
              : new Date(comp.timestamp);
            if (compDate < yearStart || compDate >= yearEnd) return;
            if (comp.discipline !== discipline || comp.category !== category) return;

            comp.results.forEach((result) => {
              if (result.autoExcluded || result.invalid) return;
              // For current year, skip manually excluded results
              if (result.excluded) return;

              const dn = result.dancerName;
              if (!dancersMap.has(dn)) {
                dancersMap.set(dn, { results: [], danceResults: {} });
              }
              const entry = dancersMap.get(dn);
              const score = Math.round((result.points || 0) * (comp.multiplier || 1));

              if (discipline === "Latin") {
                const dance = comp.dance || "Unknown";
                if (!entry.danceResults[dance]) entry.danceResults[dance] = [];
                entry.danceResults[dance].push(score);
              } else {
                entry.results.push(score);
              }
            });
          });

          // Calculate total points per dancer
          const dancers = Array.from(dancersMap.entries()).map(([dn, data]) => {
            let totalPoints = 0;
            if (discipline === "Latin") {
              Object.values(data.danceResults).forEach((scores) => {
                const top = scores.sort((a, b) => b - a).slice(0, topN);
                totalPoints += top.reduce((s, v) => s + v, 0);
              });
            } else {
              const top = data.results.sort((a, b) => b - a).slice(0, topN);
              totalPoints = top.reduce((s, v) => s + v, 0);
            }
            return { dn, totalPoints };
          }).filter((d) => d.totalPoints > 0)
            .sort((a, b) => b.totalPoints - a.totalPoints);

          // Assign ranks with tie handling
          let lastPoints = null;
          let lastRank = 0;
          let tieCount = 0;
          let myRank = null;
          let myPoints = 0;

          for (const dancer of dancers) {
            let rank;
            if (lastPoints === dancer.totalPoints) {
              tieCount++;
              rank = lastRank;
            } else {
              rank = lastRank + tieCount + 1;
              lastRank = rank;
              lastPoints = dancer.totalPoints;
              tieCount = 0;
            }
            if (dancer.dn === name) {
              myRank = rank;
              myPoints = dancer.totalPoints;
            }
          }

          if (myRank !== null) {
            results.push({
              discipline,
              category,
              rank: myRank,
              totalPoints: myPoints,
              totalDancers: dancers.length,
            });
          }
        }

        setEternalCupRankings(results);
      } catch (err) {
        console.error("❌ Error computing Eternal Cup Rankings:", err);
        setEternalCupRankings([]);
      } finally {
        setEternalCupLoading(false);
      }
    };

    computeEternalCupRankings();
  }, [cachedRankings?.disciplineOverview, dancerData?.disciplineOverview, name]);

  // Compute EC rankings (rank + total dancers) for any selected year, used by the competitions tab toggle
  const computeEcYearRankings = useCallback(async (year) => {
    if (!dancerData?.results || !name) return;
    setEcYearRankingsLoading(true);
    try {
      const yearStart = new Date(year, 0, 1);
      const yearEnd = new Date(year + 1, 0, 1);
      const topN = year >= 2026 ? 6 : 4;

      const toDate = (ts) => {
        if (!ts) return null;
        if (ts instanceof Timestamp) return ts.toDate();
        if (ts instanceof Date) return ts;
        return new Date(ts);
      };

      const yearResults = dancerData.results.filter(r => {
        if (r.invalid || r.excluded || r.autoExcluded || r.ageExcluded) return false;
        const d = toDate(r.timestamp);
        return d && d >= yearStart && d < yearEnd;
      });

      const seen = new Set();
      const uniqueCombos = [];
      yearResults.forEach(r => {
        const key = `${r.discipline}|${r.category}`;
        if (!seen.has(key)) { seen.add(key); uniqueCombos.push({ discipline: r.discipline, category: r.category }); }
      });

      if (uniqueCombos.length === 0) { setEcYearRankings([]); return; }

      const oldSnapshot = await getDocs(collection(db, "competitions"));
      const results = [];

      for (const { discipline, category } of uniqueCombos) {
        const dancersMap = new Map();

        oldSnapshot.forEach(docSnap => {
          const comp = docSnap.data();
          if (!comp.results || !comp.timestamp) return;
          const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
          if (compDate < yearStart || compDate >= yearEnd) return;
          if (comp.discipline !== discipline || comp.category !== category) return;

          comp.results.forEach(result => {
            if (result.autoExcluded || result.invalid || result.excluded) return;
            const dn = result.dancerName;
            if (!dancersMap.has(dn)) dancersMap.set(dn, { results: [], danceResults: {} });
            const entry = dancersMap.get(dn);
            const score = Math.round((result.points || 0) * (comp.multiplier || 1));
            if (discipline === "Latin") {
              const dance = comp.dance || "Unknown";
              if (!entry.danceResults[dance]) entry.danceResults[dance] = [];
              entry.danceResults[dance].push(score);
            } else {
              entry.results.push(score);
            }
          });
        });

        const dancers = Array.from(dancersMap.entries()).map(([dn, data]) => {
          let totalPoints = 0;
          if (discipline === "Latin") {
            Object.values(data.danceResults).forEach(scores => {
              const top = [...scores].sort((a, b) => b - a).slice(0, topN);
              totalPoints += top.reduce((s, v) => s + v, 0);
            });
          } else {
            const top = [...data.results].sort((a, b) => b - a).slice(0, topN);
            totalPoints = top.reduce((s, v) => s + v, 0);
          }
          return { dn, totalPoints };
        }).filter(d => d.totalPoints > 0).sort((a, b) => b.totalPoints - a.totalPoints);

        let lastPoints = null, lastRank = 0, tieCount = 0, myRank = null;
        for (const dancer of dancers) {
          let rank;
          if (lastPoints === dancer.totalPoints) { tieCount++; rank = lastRank; }
          else { rank = lastRank + tieCount + 1; lastRank = rank; lastPoints = dancer.totalPoints; tieCount = 0; }
          if (dancer.dn === name) myRank = rank;
        }

        if (myRank !== null) {
          results.push({ discipline, category, rank: myRank, totalDancers: dancers.length });
        }
      }

      setEcYearRankings(results);
    } catch (err) {
      console.error("❌ Error computing EC year rankings:", err);
      setEcYearRankings([]);
    } finally {
      setEcYearRankingsLoading(false);
    }
  }, [dancerData?.results, name]);

  // Trigger rank computation when the EC toggle is opened or the year changes
  useEffect(() => {
    if (showEternalCupTable && dancerData?.results) {
      setEcYearRankings(null);
      computeEcYearRankings(ecSelectedYear);
    }
  }, [showEternalCupTable, ecSelectedYear, computeEcYearRankings, dancerData?.results]);

  // Reset age-category filter when year changes
  useEffect(() => {
    setEcSelectedCategory("");
  }, [ecSelectedYear]);

  // Memoize expensive calculations to prevent re-computation (moved before early returns)
  const allResults = useMemo(() => {
    if (!dancerData?.results) return [];
    
    // Define Latin dance order
    const latinDanceOrder = ['Samba', 'ChaChaCha', 'Rumba', 'Pasodoble', 'Jive'];
    
    // Helper function to get dance order index
    const getDanceOrderIndex = (dance) => {
      const index = latinDanceOrder.indexOf(dance);
      return index === -1 ? 999 : index; // Unknown dances go to the end
    };
    
    return dancerData.results
      .sort((a, b) => {
        // First sort by timestamp (newest first)
        const dateA = a.timestamp instanceof Timestamp ? a.timestamp.toDate() : new Date(a.timestamp);
        const dateB = b.timestamp instanceof Timestamp ? b.timestamp.toDate() : new Date(b.timestamp);
        const dateDiff = dateB - dateA;
        
        // Get competition names for grouping
        const compNameA = compNameMap[a.compId] || a.compId;
        const compNameB = compNameMap[b.compId] || b.compId;
        
        // If same competition (same competition name) and both are Latin, apply Latin dance order
        if (compNameA === compNameB && a.discipline === 'Latin' && b.discipline === 'Latin') {
          return getDanceOrderIndex(a.dance) - getDanceOrderIndex(b.dance);
        }
        
        return dateDiff;
      });
  }, [dancerData?.results, compNameMap]);

  // 📈 ENHANCED OVERVIEW: Include historical categories where dancer participated but has no active points
  const enhancedOverview = useMemo(() => {
    // Always prefer freshly calculated dancerData.disciplineOverview over cache when available
    // Cache is only for initial fast display; once fresh data is calculated, use it
    const baseOverview = (dancerData?.disciplineOverview && dancerData.disciplineOverview.length > 0)
      ? dancerData.disciplineOverview 
      : (cachedRankings?.disciplineOverview) || [];
    
    // If we don't have results loaded, return base overview
    if (!dancerData?.results || dancerData.results.length === 0) {
      return baseOverview;
    }
    
    // Find all unique categories from ALL results (including historical)
    // Use the same normalization logic as the overview calculation
    const allCategoriesFromResults = new Set();
    dancerData.results.forEach(result => {
      // Normalize discipline extraction (same logic as grouping)
      const categoryLower = (result.category || '').toLowerCase();
      let normalizedDiscipline = result.discipline;
      if (!normalizedDiscipline || normalizedDiscipline === 'Latin') {
        if (categoryLower.includes('hip hop')) {
          normalizedDiscipline = 'Hip Hop';
        } else if (categoryLower.includes('contemporary')) {
          normalizedDiscipline = 'Contemporary';
        } else if (categoryLower.includes('ballet')) {
          normalizedDiscipline = 'Ballet';
        } else {
          normalizedDiscipline = result.discipline || 'Latin';
        }
      }
      
      // Normalize dance - if it matches discipline, treat as null
      let normalizedDance = result.dance;
      if (normalizedDance === normalizedDiscipline) {
        normalizedDance = null;
      }
      
      const key = `${normalizedDiscipline}_${result.category}${normalizedDance ? `_${normalizedDance}` : ''}`;
      allCategoriesFromResults.add(key);
    });
    
    // Find categories in base overview
    const existingCategoryKeys = new Set();
    baseOverview.forEach(overview => {
      const key = `${overview.discipline}_${overview.category}${overview.dance ? `_${overview.dance}` : ''}`;
      existingCategoryKeys.add(key);
    });
    
    // Find historical categories not in base overview
    const historicalCategories = [];
    allCategoriesFromResults.forEach(categoryKey => {
      if (!existingCategoryKeys.has(categoryKey)) {
        // This is a historical category - find its details
        const [discipline, category, dance] = categoryKey.split('_');
        
        // Find all results for this category using normalized matching
        const categoryResults = dancerData.results.filter(r => {
          // Normalize discipline for comparison
          const categoryLower = (r.category || '').toLowerCase();
          let rNormalizedDiscipline = r.discipline;
          if (!rNormalizedDiscipline || rNormalizedDiscipline === 'Latin') {
            if (categoryLower.includes('hip hop')) {
              rNormalizedDiscipline = 'Hip Hop';
            } else if (categoryLower.includes('contemporary')) {
              rNormalizedDiscipline = 'Contemporary';
            } else if (categoryLower.includes('ballet')) {
              rNormalizedDiscipline = 'Ballet';
            } else {
              rNormalizedDiscipline = r.discipline || 'Latin';
            }
          }
          
          // Normalize dance for comparison
          let rNormalizedDance = r.dance;
          if (rNormalizedDance === rNormalizedDiscipline) {
            rNormalizedDance = null;
          }
          
          return rNormalizedDiscipline === discipline && 
                 r.category === category && 
                 (dance ? rNormalizedDance === dance : !rNormalizedDance);
        });
        
        // Check if this category has any active points
        const activeResults = categoryResults.filter(r => r.countsForRanking);
        const totalActivePoints = activeResults.reduce((sum, r) => sum + r.points, 0);
        
        // Only include if no active points (historical only)
        if (totalActivePoints === 0 && categoryResults.length > 0) {
          // Calculate best rank and best days historically
          const allResults = categoryResults.sort((a, b) => {
            const dateA = a.timestamp instanceof Timestamp ? a.timestamp.toDate() : new Date(a.timestamp);
            const dateB = b.timestamp instanceof Timestamp ? b.timestamp.toDate() : new Date(b.timestamp);
            return dateA - dateB; // Chronological order
          });
          
          // Find best place achieved
          const validPlaces = allResults
            .filter(r => r.place && r.place !== 'DQ' && r.place !== 'DNS' && !r.withdrawn)
            .map(r => parseInt(r.place))
            .filter(place => !isNaN(place));
          
          const bestRank = validPlaces.length > 0 ? Math.min(...validPlaces) : null;
          
          // Calculate best days held (simplified calculation)
          let bestDaysHeld = 0;
          if (bestRank && validPlaces.length > 1) {
            // Find periods where this rank was held
            let currentRank = null;
            let currentStart = null;
            let maxDays = 0;
            
            allResults.forEach((result, index) => {
              const place = parseInt(result.place);
              const resultDate = result.timestamp instanceof Timestamp ? result.timestamp.toDate() : new Date(result.timestamp);
              
              if (!isNaN(place) && place === bestRank) {
                if (currentRank !== bestRank) {
                  currentRank = bestRank;
                  currentStart = resultDate;
                }
              } else if (currentRank === bestRank) {
                // Rank changed, calculate days held
                const days = Math.floor((resultDate - currentStart) / (1000 * 60 * 60 * 24));
                maxDays = Math.max(maxDays, days);
                currentRank = null;
                currentStart = null;
              }
            });
            
            bestDaysHeld = maxDays;
          }
          
          historicalCategories.push({
            discipline,
            category,
            dance: dance || null,
            totalPoints: 0,
            activePoints: 0,
            currentRank: null,
            daysHeld: 0,
            currentDaysHeld: 0,
            bestRank,
            bestDaysHeld,
            competitionCount: categoryResults.length,
            rankingCompetitionCount: 0,
            hasResults: true,
            isHistoricalOnly: true,
            totalDancers: null
          });
        }
      }
    });
    
    // Merge base overview with historical categories
    const combined = [...baseOverview, ...historicalCategories];
    
    // Define age category order (youngest to oldest)
    const ageCategoryOrder = {
      'Juvenile I': 1,
      'Juvenile II': 2,
      'Junior I': 3,
      'Junior II': 4,
      'Youth': 5,
      'Adult': 6
    };
    
    // Define Latin dance order
    const latinDanceOrder = {
      'Samba': 1,
      'ChaChaCha': 2,
      'Rumba': 3,
      'Pasodoble': 4,
      'Jive': 5
    };
    
    // Sort by complex hierarchy: Active (youngest first), then Historical (oldest first)
    return combined.sort((a, b) => {
      // 1. Age category priority (youngest on top, oldest after)
      const aAgeOrder = ageCategoryOrder[a.category] || 999;
      const bAgeOrder = ageCategoryOrder[b.category] || 999;
      if (aAgeOrder !== bAgeOrder) {
        return aAgeOrder - bAgeOrder;
      }

      // 2. Active/Historical priority (active first)
      const aIsActive = (a.totalPoints || a.activePoints || 0) > 0;
      const bIsActive = (b.totalPoints || b.activePoints || 0) > 0;
      if (aIsActive !== bIsActive) {
        return aIsActive ? -1 : 1;
      }

      // 3. Discipline priority
      const aDisciplineKey = a.discipline || '';
      const bDisciplineKey = b.discipline || '';
      if (aDisciplineKey !== bDisciplineKey) {
        return aDisciplineKey.localeCompare(bDisciplineKey);
      }

      // 4. Dance priority (Latin order for Latin, alphabetical for others)
      if (a.dance && b.dance) {
        if (a.discipline === 'Latin' && b.discipline === 'Latin') {
          const aDanceOrder = latinDanceOrder[a.dance] || 999;
          const bDanceOrder = latinDanceOrder[b.dance] || 999;
          return aDanceOrder - bDanceOrder;
        } else {
          return a.dance.localeCompare(b.dance);
        }
      }

      // 5. Handle cases where one has dance and other doesn't
      if (a.dance && !b.dance) return 1;
      if (!a.dance && b.dance) return -1;

      return 0;
    });
  }, [cachedRankings?.disciplineOverview, dancerData?.disciplineOverview, dancerData?.results, profileData]);

  // Memoize filtered results for competitions tab
  const filteredResults = useMemo(() => {
    if (!allResults) return [];
    
    return allResults.filter((r) => {
      const disciplineMatch = !selectedDiscipline || r.discipline === selectedDiscipline;
      const ageCategoryMatch = !selectedAgeCategory || r.category === selectedAgeCategory;
      const danceMatch = !selectedDance || r.dance === selectedDance;
      return disciplineMatch && ageCategoryMatch && danceMatch;
    });
  }, [allResults, selectedDiscipline, selectedAgeCategory, selectedDance]);

  // Memoize top 6 calculation - 6 best per discipline OR 6 best per dance+category (when Latin dance is selected)
  const filteredTop6Results = useMemo(() => {
    if (!dancerData?.results) return [];
    
    // Filter results that count for ranking (last 365 days)
    const rankingResults = dancerData.results.filter(r => r.countsForRanking);
    
    // If a specific dance is selected (Latin only), get top 6 for that dance in the selected age category
    if (selectedDance && selectedDiscipline === 'Latin') {
      const danceResults = rankingResults.filter(r => {
        const danceMatch = r.dance === selectedDance && r.discipline === 'Latin';
        const categoryMatch = !selectedAgeCategory || r.category === selectedAgeCategory;
        return danceMatch && categoryMatch;
      });
      
      return danceResults
        .sort((a, b) => b.points - a.points)
        .slice(0, 6)
        .map(r => r.compId);
    }
    
    // If Latin discipline is selected but no specific dance (All dances), no green ticks
    if (selectedDiscipline === 'Latin' && !selectedDance) {
      return []; // No green ticks when "All dances" is selected
    }
    
    // If a discipline is selected (non-Latin), get top 6 for that discipline in selected age category
    if (selectedDiscipline) {
      const disciplineResults = rankingResults.filter(r => {
        const disciplineMatch = r.discipline === selectedDiscipline;
        const categoryMatch = !selectedAgeCategory || r.category === selectedAgeCategory;
        return disciplineMatch && categoryMatch;
      });
      
      return disciplineResults
        .sort((a, b) => b.points - a.points)
        .slice(0, 6)
        .map(r => r.compId);
    }
    
    // No filters - return empty (no green ticks when no specific filter)
    return [];
  }, [dancerData?.results, selectedDiscipline, selectedDance, selectedAgeCategory]);

  // Years that have at least one competition result for this dancer
  const ecAvailableYears = useMemo(() => {
    if (!dancerData?.results) return [new Date().getFullYear()];
    const years = new Set();
    dancerData.results.forEach(r => {
      if (!r.timestamp) return;
      const d = r.timestamp instanceof Timestamp ? r.timestamp.toDate() : new Date(r.timestamp);
      if (!isNaN(d.getTime())) years.add(d.getFullYear());
    });
    const sorted = Array.from(years).sort((a, b) => b - a);
    return sorted.length > 0 ? sorted : [new Date().getFullYear()];
  }, [dancerData?.results]);

  // Eternal Cup best N competitions per discipline+category for the selected year
  // For Latin: best N per dance combined; for others: best N overall
  const eternalCupBestResults = useMemo(() => {
    if (!dancerData?.results) return [];

    const yearStart = new Date(ecSelectedYear, 0, 1);
    const yearEnd = new Date(ecSelectedYear + 1, 0, 1);
    const topN = ecSelectedYear >= 2026 ? 6 : 4;

    const toDate = (ts) => {
      if (!ts) return null;
      if (ts instanceof Timestamp) return ts.toDate();
      if (ts instanceof Date) return ts;
      return new Date(ts);
    };

    const yearResults = dancerData.results.filter(r => {
      if (r.invalid || r.excluded || r.autoExcluded || r.ageExcluded) return false;
      const date = toDate(r.timestamp);
      return date && date >= yearStart && date < yearEnd;
    });

    if (yearResults.length === 0) return [];

    const groups = {};
    yearResults.forEach(r => {
      const key = `${r.discipline}__${r.category}`;
      if (!groups[key]) groups[key] = { discipline: r.discipline, category: r.category, results: [] };
      groups[key].results.push(r);
    });

    const latinDanceOrder = ['Samba', 'ChaChaCha', 'Rumba', 'Pasodoble', 'Jive'];

    return Object.values(groups).map(group => {
      if (group.discipline === 'Latin') {
        const byDance = {};
        group.results.forEach(r => {
          const dance = r.dance || 'Unknown';
          if (!byDance[dance]) byDance[dance] = [];
          byDance[dance].push(r);
        });

        const countingIds = new Set();
        const danceBreakdown = Object.entries(byDance)
          .sort(([a], [b]) => {
            const ai = latinDanceOrder.indexOf(a);
            const bi = latinDanceOrder.indexOf(b);
            return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
          })
          .map(([dance, results]) => {
            const sorted = [...results].sort((a, b) => b.points - a.points);
            const top = sorted.slice(0, topN);
            top.forEach(r => countingIds.add(r.compId));
            const danceTotal = top.reduce((s, r) => s + r.points, 0);
            return { dance, all: sorted, top, danceTotal };
          });

        const totalPoints = danceBreakdown.reduce((sum, { danceTotal }) => sum + danceTotal, 0);
        return { discipline: group.discipline, category: group.category, isLatin: true, danceBreakdown, countingIds, totalPoints };
      } else {
        const sorted = [...group.results].sort((a, b) => b.points - a.points);
        const top = sorted.slice(0, topN);
        const countingIds = new Set(top.map(r => r.compId));
        const totalPoints = top.reduce((sum, r) => sum + r.points, 0);
        return { discipline: group.discipline, category: group.category, isLatin: false, allResults: sorted, top, countingIds, totalPoints };
      }
    }).sort((a, b) => {
      const disciplineOrder = ['Latin', 'Contemporary', 'Hip Hop', 'Commercial'];
      const ai = disciplineOrder.indexOf(a.discipline);
      const bi = disciplineOrder.indexOf(b.discipline);
      if (ai !== bi) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      return a.category.localeCompare(b.category);
    });
  }, [dancerData?.results, ecSelectedYear]);

  // Age categories available for the selected EC year (for the category filter)
  const ecAvailableCategories = useMemo(() => {
    const cats = [...new Set(eternalCupBestResults.map(g => g.category))];
    const categoryOrder = ['Adult', 'Youth', 'Junior II', 'Junior I', 'Juvenile II', 'Juvenile I'];
    return cats.sort((a, b) => {
      const ai = categoryOrder.indexOf(a);
      const bi = categoryOrder.indexOf(b);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
  }, [eternalCupBestResults]);

  // eternalCupBestResults filtered by selected age category
  const ecFilteredBestResults = useMemo(() => {
    if (!ecSelectedCategory) return eternalCupBestResults;
    return eternalCupBestResults.filter(g => g.category === ecSelectedCategory);
  }, [eternalCupBestResults, ecSelectedCategory]);

  // Get unique age categories from results filtered by discipline
  const availableAgeCategories = useMemo(() => {
    if (!allResults) return [];
    
    // Filter results by selected discipline first
    const disciplineFilteredResults = selectedDiscipline 
      ? allResults.filter(r => r.discipline === selectedDiscipline)
      : allResults;
    
    const categories = [...new Set(disciplineFilteredResults.map(r => r.category))];
    
    // Sort from highest to lowest: Adult, Youth, Junior II, Junior I, Juvenile II, Juvenile I
    const categoryOrder = ['Adult', 'Youth', 'Junior II', 'Junior I', 'Juvenile II', 'Juvenile I'];
    return categories.sort((a, b) => {
      const indexA = categoryOrder.indexOf(a);
      const indexB = categoryOrder.indexOf(b);
      // If category not found in order, put it at the end
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  }, [allResults, selectedDiscipline]);

  // Get unique dances for the selected discipline
  const availableDances = useMemo(() => {
    if (!allResults || selectedDiscipline !== 'Latin') return [];
    const dances = [...new Set(allResults
      .filter(r => r.discipline === 'Latin')
      .map(r => r.dance)
    )];
    
    // Sort in Latin dance order: Samba, ChaChaCha, Rumba, Pasodoble, Jive
    const danceOrder = ['Samba', 'ChaChaCha', 'Rumba', 'Pasodoble', 'Jive'];
    return dances.sort((a, b) => {
      const indexA = danceOrder.indexOf(a);
      const indexB = danceOrder.indexOf(b);
      // If dance not found in order, put it at the end
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  }, [allResults, selectedDiscipline]);

  // 📤 Compute results uploaded within the last 48 hours (for share feature)
  // Uses uploadedAt (server timestamp) if present, otherwise falls back to timestamp (competition date)
  const recentShareableResults = useMemo(() => {
    if (!dancerData?.results || dancerData.results.length === 0) return [];
    const cutoff = new Date(Date.now() - 48 * 60 * 60 * 1000); // 48h ago

    const toDate = (ts) => {
      if (!ts) return null;
      if (ts instanceof Timestamp) return ts.toDate();
      if (ts instanceof Date) return ts;
      return new Date(ts);
    };

    return dancerData.results
      .filter((r) => {
        if (r.invalid) return false;
        // Prefer uploadedAt (actual server-side upload time) over competition date
        const uploadTime = toDate(r.uploadedAt) || toDate(r.timestamp);
        return uploadTime && uploadTime >= cutoff;
      })
      .map((r) => ({
        ...r,
        competitionName: compNameMap[r.compId] || r.compId || "Competition",
      }))
      .sort((a, b) => {
        const toDateFn = (ts) => ts instanceof Timestamp ? ts.toDate() : new Date(ts);
        const ta = toDate(a.uploadedAt) || toDateFn(a.timestamp);
        const tb = toDate(b.uploadedAt) || toDateFn(b.timestamp);
        return tb - ta; // newest first
      });
  }, [dancerData?.results, compNameMap]);

  if (loading) return <p>Loading profile...</p>;
  
  // Check if we have either dancer data or cached rankings
  if (!dancerData && !cachedRankings) return <p>Dancer not found.</p>;

  const displayName = name.replace(/\s+/g, "").toLowerCase();

  // Style constants for competition detail modal table
  const cmThStyle = { padding: "4px 6px", border: "1px solid #ccc", textAlign: "left", fontWeight: "600", whiteSpace: "nowrap", fontSize: "0.72rem" };
  const cmTdStyle = { padding: "3px 6px", border: "1px solid #e0e0e0", whiteSpace: "nowrap", fontSize: "0.72rem" };

  return (
    <div className="profile-container" style={{ 
      maxWidth: "60vw", 
      width: "100%", 
      margin: "0 auto",
      padding: "0.75rem"
    }}>
      <div className="profile-header">
        <div className="profile-title-section">
          <h2>
            {name}
            {isProfileClaimed && (
              <span className="verified-badge" title="Verified Profile">
                ✅ Verified
              </span>
            )}
          </h2>
          {/* Visitor Count - Visible to everyone */}
          {visitorCount !== null && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginLeft: '1rem',
              padding: '0.25rem 0.75rem',
              backgroundColor: '#f0f0f0',
              borderRadius: '20px',
              fontSize: '0.85rem',
              color: '#666'
            }}>
              <span>👁️</span>
              <span>{visitorCount.toLocaleString()} {visitorCount === 1 ? 'visitor' : 'visitors'}</span>
            </div>
          )}
        </div>
        
        <div className="profile-actions">
          {/* Other actions live in the tabs below */}
          <FollowButton type="dancer" name={name} />
        </div>
      </div>

      <PredictionPoints dancerName={name} />

      {profileData && (
        <div className="profile-info-section">
          {profileData.favoriteStyle && (
            <div className="profile-info-item">
              <strong>Favorite Style:</strong> {profileData.favoriteStyle}
            </div>
          )}
          {profileData.yearsExperience && (
            <div className="profile-info-item">
              <strong>Experience:</strong> {profileData.yearsExperience} years
            </div>
          )}
          {profileData.currentStudio && (
            <div className="profile-info-item">
              <strong>Studio:</strong> {profileData.currentStudio}
            </div>
          )}
          {profileData.achievements && (
            <div className="profile-info-item">
              <strong>Achievements:</strong> {profileData.achievements}
            </div>
          )}

          {/* 🎂 Birth Year and Age Category Section */}
          {(isAdmin || calculateAgeCategoryFromPriority()) && (
            <div className="profile-info-item">
              {/* Show Age Category (always visible if available) */}
              {calculateAgeCategoryFromPriority() && (
                <div style={{ marginBottom: '8px' }}>
                  <strong>Age Category:</strong> {calculateAgeCategoryFromPriority()}
                </div>
              )}

              {/* 🔧 Admin Controls Toggle (Compact) */}
              {isAdmin && (
                <div style={{ marginTop: '8px' }}>
                  <button
                    onClick={() => setShowAdminControls(!showAdminControls)}
                    style={{
                      padding: '3px 8px',
                      fontSize: '0.7rem',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      opacity: 0.7
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '1'}
                    onMouseLeave={(e) => e.target.style.opacity = '0.7'}
                  >
                    {showAdminControls ? '▲' : '▼'} Admin
                  </button>
                  
                  {/* Compact Admin Section */}
                  {showAdminControls && (
                    <div style={{ 
                      marginTop: '8px',
                      padding: '8px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                      fontSize: '0.85rem',
                      border: '1px solid #dee2e6'
                    }}>
                      {/* Birth Year Controls */}
                      <div style={{ marginBottom: isProfileClaimed ? '12px' : '0', paddingBottom: isProfileClaimed ? '12px' : '0', borderBottom: isProfileClaimed ? '1px solid #dee2e6' : 'none' }}>
                        <div style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: '6px' }}>
                          <strong>Birth Year Source:</strong>{' '}
                          {getEffectiveBirthYear() === null ? (
                            'None'
                          ) : isProfileClaimed && claimedBy?.userId && claimedBy.dateOfBirth ? (
                            '✅ Claimed'
                          ) : manualYearOfBirth ? (
                            '⚙️ Manual'
                          ) : (
                            'Unknown'
                          )}
                        </div>

                        {!editingYearOfBirth ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem' }}>
                            <span><strong>Manual Year:</strong> {manualYearOfBirth || 'Not set'}</span>
                            <button
                              onClick={() => setEditingYearOfBirth(true)}
                              style={{
                                padding: '2px 6px',
                                fontSize: '0.7rem',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '2px',
                                cursor: 'pointer'
                              }}
                            >
                              ✏️
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                            <input
                              type="number"
                              placeholder="Year"
                              defaultValue={manualYearOfBirth || ''}
                              min="1900"
                              max={new Date().getFullYear()}
                              style={{
                                padding: '2px 6px',
                                border: '1px solid #ddd',
                                borderRadius: '2px',
                                width: '80px',
                                fontSize: '0.8rem'
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  const year = e.target.value ? parseInt(e.target.value) : null;
                                  saveManualYearOfBirth(year);
                                } else if (e.key === 'Escape') {
                                  setEditingYearOfBirth(false);
                                }
                              }}
                              autoFocus
                              id="year-of-birth-input"
                            />
                            <button
                              onClick={() => {
                                const input = document.getElementById('year-of-birth-input');
                                const year = input.value ? parseInt(input.value) : null;
                                saveManualYearOfBirth(year);
                              }}
                              disabled={yearOfBirthLoading}
                              style={{
                                padding: '2px 6px',
                                fontSize: '0.7rem',
                                backgroundColor: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '2px',
                                cursor: yearOfBirthLoading ? 'not-allowed' : 'pointer'
                              }}
                            >
                              ✅
                            </button>
                            <button
                              onClick={() => setEditingYearOfBirth(false)}
                              style={{
                                padding: '2px 6px',
                                fontSize: '0.7rem',
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '2px',
                                cursor: 'pointer'
                              }}
                            >
                              ❌
                            </button>
                          </div>
                        )}
                      </div>

                      {/* ✏️ Rename Dancer */}
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #dee2e6' }}>
                        <div style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: '6px', fontWeight: 'bold' }}>
                          ✏️ Rename Dancer
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#6c757d', marginBottom: '6px', lineHeight: '1.3' }}>
                          Changes name across all competition results. Claimed profile stays claimed.
                        </div>
                        {!editingDancerName ? (
                          <button
                            onClick={() => { setEditingDancerName(true); setNewDancerNameInput(name); setRenameStatus(''); }}
                            style={{ padding: '3px 8px', fontSize: '0.75rem', backgroundColor: '#fd7e14', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                          >
                            ✏️ Rename
                          </button>
                        ) : (
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', marginBottom: '4px' }}>
                              <input
                                type="text"
                                value={newDancerNameInput}
                                onChange={e => setNewDancerNameInput(e.target.value)}
                                disabled={renameLoading}
                                style={{ padding: '3px 6px', border: '1px solid #fd7e14', borderRadius: '3px', fontSize: '0.8rem', width: '160px' }}
                                onKeyDown={e => { if (e.key === 'Enter') renameDancer(); else if (e.key === 'Escape') { setEditingDancerName(false); setRenameStatus(''); } }}
                                autoFocus
                              />
                              <button
                                onClick={renameDancer}
                                disabled={renameLoading || !newDancerNameInput.trim() || newDancerNameInput.trim() === name}
                                style={{ padding: '3px 8px', fontSize: '0.75rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '3px', cursor: renameLoading ? 'not-allowed' : 'pointer' }}
                              >
                                {renameLoading ? '⏳' : '✅ Save'}
                              </button>
                              <button
                                onClick={() => { setEditingDancerName(false); setRenameStatus(''); }}
                                disabled={renameLoading}
                                style={{ padding: '3px 8px', fontSize: '0.75rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                              >
                                ❌
                              </button>
                            </div>
                            {renameStatus && (
                              <div style={{ fontSize: '0.7rem', color: renameStatus.startsWith('✅') ? '#28a745' : renameStatus.startsWith('❌') ? '#dc3545' : '#555', marginTop: '4px' }}>
                                {renameStatus}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Unclaim Profile (if claimed) */}
                      {isProfileClaimed && (
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#dc3545', marginBottom: '6px', fontWeight: 'bold' }}>
                            ⚠️ Unclaim Profile
                          </div>
                          <div style={{ fontSize: '0.7rem', color: '#6c757d', marginBottom: '6px', lineHeight: '1.3' }}>
                            Reset to unclaimed state
                          </div>
                          {unclaimConfirmStep === 0 ? (
                            <button
                              onClick={unclaimProfile}
                              disabled={unclaimLoading}
                              style={{
                                padding: '3px 8px',
                                fontSize: '0.75rem',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: unclaimLoading ? 'not-allowed' : 'pointer'
                              }}
                            >
                              🔓 Unclaim
                            </button>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                              <button
                                onClick={unclaimProfile}
                                disabled={unclaimLoading}
                                style={{
                                  padding: '3px 8px',
                                  fontSize: '0.75rem',
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  border: '2px solid #c82333',
                                  borderRadius: '3px',
                                  cursor: unclaimLoading ? 'not-allowed' : 'pointer',
                                  fontWeight: 'bold'
                                }}
                              >
                                {unclaimLoading ? '⏳' : '⚠️ CONFIRM'}
                              </button>
                              <button
                                onClick={() => setUnclaimConfirmStep(0)}
                                disabled={unclaimLoading}
                                style={{
                                  padding: '3px 8px',
                                  fontSize: '0.75rem',
                                  backgroundColor: '#6c757d',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '3px',
                                  cursor: unclaimLoading ? 'not-allowed' : 'pointer'
                                }}
                              >
                                ❌
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="tabs">
        <button
          onClick={() => setActiveTab("overview")}
          className={activeTab === "overview" ? "active" : ""}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab("competitions")}
          className={activeTab === "competitions" ? "active" : ""}
        >
          Competitions
        </button>
        {!isProfileClaimed && (
          <button
            onClick={() => setActiveTab("claim")}
            className={activeTab === "claim" ? "active" : ""}
          >
            🎭 Are you {name}? Claim Profile
          </button>
        )}
        {user && canEditProfile() && (
          <button
            onClick={() => setActiveTab("update")}
            className={activeTab === "update" ? "active" : ""}
          >
            ✏️ Update Profile
          </button>
        )}
      </div>

      {activeTab === "overview" && (
        <div className="overview-tab">
          <div style={{ marginBottom: "1rem" }}>
            {profilePictureUrl ? (
              <img
                src={profilePictureUrl}
                alt={`${name} profile picture`}
                className="profile-pic"
                onError={(e) => {
                  // Prevent infinite loop by checking if we've already tried fallbacks
                  if (e.target.dataset.fallbackAttempted) {
                    return;
                  }
                  
                  // If Firebase Storage image fails, try fallback to static images
                  const currentSrc = e.target.src;
                  
                  // Check if it's a Firebase Storage URL that failed
                  if (currentSrc.includes('firebasestorage.googleapis.com')) {
                    e.target.dataset.fallbackAttempted = 'true';
                    e.target.src = `/images/dancers/${encodeURIComponent(name)}.jpg`;
                    e.target.onerror = (e2) => {
                      // Try .png extension if .jpg fails
                      if (e2.target.src.endsWith('.jpg') && !e2.target.dataset.pngAttempted) {
                        e2.target.dataset.pngAttempted = 'true';
                        e2.target.src = `/images/dancers/${encodeURIComponent(name)}.png`;
                      } else if (!e2.target.dataset.placeholderShown) {
                        // If all fail, show placeholder
                        e2.target.dataset.placeholderShown = 'true';
                        e2.target.style.display = "none";
                        const placeholder = document.createElement('div');
                        placeholder.className = 'profile-pic-placeholder';
                        placeholder.style.cssText = `
                          width: 200px;
                          height: 200px;
                          border-radius: 50%;
                          background: linear-gradient(135deg, #3498db, #2980b9);
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          color: white;
                          font-size: 4rem;
                          font-weight: bold;
                          margin: 1rem auto;
                          position: relative;
                        `;
                        placeholder.innerHTML = name.charAt(0).toUpperCase();
                        if (e2.target.parentNode && !e2.target.parentNode.querySelector('.profile-pic-placeholder')) {
                          e2.target.parentNode.appendChild(placeholder);
                        }
                      }
                    };
                  }
                }}
              />
            ) : (
              // No profile picture URL - show placeholder directly
              <div 
                className="profile-pic-placeholder"
                style={{
                  width: '200px',
                  height: '200px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3498db, #2980b9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '4rem',
                  fontWeight: 'bold',
                  margin: '1rem auto',
                  position: 'relative'
                }}
              >
                {name.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <p>
            <strong>Studio:</strong> 
            {studioLoading ? (
              <span className="loading-skeleton" style={{
                display: 'inline-block',
                backgroundColor: '#f0f0f0',
                borderRadius: '4px',
                width: '120px',
                height: '20px',
                animation: 'pulse 1.5s ease-in-out infinite'
              }}>
                &nbsp;
              </span>
            ) : (
              <>
                {/* Primary/Current Studio */}
                {(currentStudio || dancerData?.studio) ? (
                  <ClickableStudioName name={currentStudio || dancerData.studio} />
                ) : (
                  <span style={{ color: '#666', fontStyle: 'italic' }}>
                    No studio information available
                  </span>
                )}
                
                {/* Discipline-Specific Studios */}
                {Object.keys(disciplineStudios).length > 0 && (
                  <div className="discipline-studios" style={{ 
                    marginTop: '8px', 
                    fontSize: '0.9em'
                  }}>
                    {Object.entries(disciplineStudios).map(([discipline, studio]) => (
                      <div key={discipline} style={{ 
                        marginBottom: '4px',
                        color: '#555'
                      }}>
                        <ClickableStudioName name={studio} /> ({discipline})
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Transfer History */}
                {transferHistory.length > 0 && (
                  <div className="transfer-info" style={{ 
                    marginTop: '5px', 
                    fontSize: '0.9em', 
                    color: '#666',
                    fontStyle: 'italic'
                  }}>
                    {transferHistory.map((transfer, index) => (
                      <div key={transfer.id}>
                        Transferred from <ClickableStudioName name={transfer.fromStudio} /> 
                        to <ClickableStudioName name={transfer.toStudio} /> 
                        on {transfer.transferDate.toLocaleDateString('en-GB', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </p>

          {/* Age Category Section */}
          {ageCategory && (
            <p><strong>Age Category:</strong> {ageCategory}</p>
          )}

          {/* Social Media Section */}
          {(() => {
            const hasInstagram = (profileData?.socialMedia?.instagram && profileData.socialMedia.instagram.trim()) || 
                               (profileData?.instagramUsername && profileData.instagramUsername.trim()) || 
                               (profileData?.instagram && profileData.instagram.trim());
            const hasTikTok = (profileData?.socialMedia?.tiktok && profileData.socialMedia.tiktok.trim()) || 
                            (profileData?.tiktok && profileData.tiktok.trim());
            const hasFacebook = (profileData?.socialMedia?.facebook && profileData.socialMedia.facebook.trim()) || 
                              (profileData?.facebook && profileData.facebook.trim());
            const hasYouTube = (profileData?.socialMedia?.youtube && profileData.socialMedia.youtube.trim()) || 
                             (profileData?.youtube && profileData.youtube.trim());
            const hasSocialMedia = hasInstagram || hasTikTok || hasFacebook || hasYouTube;
            
            // Reduced logging - only log once per session for this profile
            if (!window.socialMediaLogged || window.socialMediaLogged !== name) {
              console.log('🔍 Social media check for:', name, '- Has social media:', hasSocialMedia);
              window.socialMediaLogged = name;
            }
            return hasSocialMedia;
          })() && (
            <div className="profile-info-item" style={{ margin: "1rem 0" }}>
              <p><strong>Social Media:</strong></p>
              <div className="social-links" style={{ marginLeft: "0.5rem" }}>
                {(() => {
                  const instagramValue = profileData.socialMedia?.instagram || profileData.instagramUsername || profileData.instagram;
                  return instagramValue;
                })() && (
                  <p>
                    <a href={
                      (() => {
                        const instagramValue = profileData.socialMedia?.instagram || profileData.instagramUsername || profileData.instagram;
                        return instagramValue?.startsWith('http') ? instagramValue : `https://instagram.com/${instagramValue.replace('@', '')}`;
                      })()
                    } 
                       target="_blank" rel="noopener noreferrer"
                       style={{ color: "#E4405F", textDecoration: "none", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                      Instagram: @{(() => {
                        const instagramValue = profileData.socialMedia?.instagram || profileData.instagramUsername || profileData.instagram;
                        return instagramValue ? instagramValue.replace('@', '') : '';
                      })()}
                    </a>
                  </p>
                )}
                {(() => {
                  const tiktokValue = profileData.socialMedia?.tiktok || profileData.tiktok;
                  return tiktokValue;
                })() && (
                  <p>
                    <a href={
                      (() => {
                        const tiktokValue = profileData.socialMedia?.tiktok || profileData.tiktok;
                        return tiktokValue?.startsWith('http') ? tiktokValue : `https://tiktok.com/@${tiktokValue.replace('@', '')}`;
                      })()
                    } 
                       target="_blank" rel="noopener noreferrer"
                       style={{ color: "#000000", textDecoration: "none", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                      </svg>
                      TikTok: @{(() => {
                        const tiktokValue = profileData.socialMedia?.tiktok || profileData.tiktok;
                        return tiktokValue ? tiktokValue.replace('@', '') : '';
                      })()}
                    </a>
                  </p>
                )}
                {profileData.socialMedia?.facebook && (
                  <p>
                    <a href={normalizeFacebookUrl(profileData.socialMedia.facebook)} 
                       target="_blank" rel="noopener noreferrer"
                       style={{ color: "#1877F2", textDecoration: "none", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      Facebook
                    </a>
                  </p>
                )}
                {profileData.socialMedia?.youtube && (
                  <p>
                    <a href={profileData.socialMedia.youtube.startsWith('http') ? profileData.socialMedia.youtube : `https://youtube.com/${profileData.socialMedia.youtube}`} 
                       target="_blank" rel="noopener noreferrer"
                       style={{ color: "#FF0000", textDecoration: "none", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                      YouTube
                    </a>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Bio Section */}
          {(() => {
            const bioValue = profileData?.bio;
            const bioString = bioValue ? String(bioValue).trim() : '';
            const hasBio = bioString.length > 0;
            
            // Reduced logging - only log once per session for this profile
            if (!window.bioLogged || window.bioLogged !== name) {
              console.log('🔍 Bio check for:', name, '- Has bio:', hasBio);
              window.bioLogged = name;
            }
            return hasBio;
          })() && (
            <div className="profile-info-item" style={{ margin: "1rem 0" }}>
              <p><strong>About:</strong></p>
              <div className="dancer-bio" style={{ 
                marginLeft: "0.5rem", 
                padding: "0.75rem", 
                backgroundColor: "#f8f9fa", 
                borderRadius: "8px",
                borderLeft: "4px solid #007bff"
              }}>
                <p style={{ margin: 0, lineHeight: "1.5" }}>{String(profileData.bio)}</p>
              </div>
            </div>
          )}
          
          {/* Prima Sponsorship Display - After bio and social */}
          {!sponsorshipLoading && isSponsored && (
            <div style={{ margin: "1rem 0" }}>
              <SponsorshipDisplay 
                dancerName={name}
                isSponsored={isSponsored}
                size="small"
                layout="horizontal"
                position="profile"
              />
            </div>
          )}

          {/* 📤 Share Latest Result Banner (visible within 24h of upload) */}
          {recentShareableResults.length > 0 && (
            <div className="share-latest-result-banner">
              <div className="share-latest-result-banner__text">
                <p className="share-latest-result-banner__title">
                  🎉 New result{recentShareableResults.length > 1 ? "s" : ""} just uploaded!
                </p>
                <p className="share-latest-result-banner__sub">
                  Share your latest achievement on social media
                </p>
              </div>
              <button
                className="share-latest-result-btn"
                onClick={() => setShareModalOpen(true)}
              >
                📤 Share Result
                {recentShareableResults.length > 1 && (
                  <span className="share-latest-result-btn__badge">
                    {recentShareableResults.length}
                  </span>
                )}
              </button>
            </div>
          )}
          {(() => {
            console.log('🏆 Badge Display Check:', {
              badgeCount: achievementBadges.length,
              badges: achievementBadges
            });
            return null;
          })()}
          {achievementBadges.length > 0 && (
            <div style={{
              margin: '1.5rem 0',
              padding: '1.25rem',
              background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <h4 style={{
                margin: '0 0 1rem 0',
                fontSize: '1.1rem',
                color: '#333',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                👑 Achievements
              </h4>
              <div 
                className="badges-scroll-container"
                style={{
                  overflowX: 'auto',
                  margin: '0 -1.25rem',
                  padding: '2px 1.25rem',
                  WebkitOverflowScrolling: 'touch' // Smooth scrolling on iOS
                }}>
                <div style={{
                  display: 'flex',
                  flexWrap: 'nowrap',
                  gap: '0.75rem',
                  minWidth: 'min-content'
                }}>
                {achievementBadges.map(badge => (
                  <div
                    key={badge.id}
                    className={`achievement-badge ${badge.sacred ? 'sacred-badge' : ''}`}
                    style={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: badge.sacred ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' : '#fff',
                      background: badge.sacred ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' : '#fff',
                      borderRadius: '24px',
                      boxShadow: badge.sacred 
                        ? '0 0 20px rgba(147, 51, 234, 0.5), 0 0 40px rgba(147, 51, 234, 0.3), 0 4px 12px rgba(0,0,0,0.3)' 
                        : activeBadgeTooltip === badge.id 
                          ? '0 4px 12px rgba(0,0,0,0.15)' 
                          : '0 2px 6px rgba(0,0,0,0.08)',
                      border: badge.sacred ? '2px solid #9333EA' : `2px solid ${badge.color}`,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      cursor: 'pointer',
                      transform: activeBadgeTooltip === badge.id ? 'translateY(-2px)' : 'translateY(0)',
                      animation: badge.sacred ? 'sacredGlow 2s ease-in-out infinite' : 'none'
                    }}
                    title={badge.description || badge.name}
                    onClick={(e) => {
                      // Toggle tooltip on mobile, prevent click from bubbling
                      e.stopPropagation();
                      setActiveBadgeTooltip(activeBadgeTooltip === badge.id ? null : badge.id);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      if (activeBadgeTooltip !== badge.id) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.08)';
                      }
                    }}
                  >
                    <span className="badge-icon" style={badge.sacred ? { filter: 'drop-shadow(0 0 6px rgba(255, 215, 0, 0.8))' } : {}}>
                      {badge.icon}
                    </span>
                    <div className="badge-text-container">
                      <span className="badge-name" style={badge.sacred ? { color: '#FFD700', fontWeight: 'bold', textShadow: '0 0 8px rgba(255, 215, 0, 0.6)' } : {}}>
                        {badge.name}
                      </span>
                      {/* Show years and category for Eternal Cup Winner - each year on separate line */}
                      {badge.years && badge.category && badge.name === 'Eternal Cup Winner' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          {badge.years.map((year, idx) => (
                            <span key={idx} className="badge-details" style={badge.sacred ? { color: '#e0e0e0' } : {}}>
                              {year} {badge.category}
                            </span>
                          ))}
                        </div>
                      )}
                      {/* Show placements for Top 3 Of The Year - each placement on separate line */}
                      {badge.placements && badge.category && badge.name === 'Top 3 Of The Year' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          {badge.placements.map((placement, idx) => {
                            const placeText = placement.rank === 2 ? '2nd' : '3rd';
                            return (
                              <span key={idx} className="badge-details">
                                {placement.year} - {badge.category} - {placeText}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      {/* Show disciplines for Diverse badge */}
                      {badge.id === 'diverse' && badge.disciplines && (
                        <span className="badge-details">
                          {badge.disciplines.join(', ')}
                        </span>
                      )}
                      {/* Show count for medal badges (centered) */}
                      {badge.count !== undefined && !badge.years && !badge.placements && badge.id !== 'diverse' && (
                        <span className="badge-details" style={{ textAlign: 'center' }}>
                          {badge.count} {badge.suffix || ''}
                        </span>
                      )}
                    </div>
                    
                    {/* Mobile Tooltip */}
                    {activeBadgeTooltip === badge.id && badge.description && (
                      <div style={{
                        position: 'absolute',
                        bottom: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        marginBottom: '0.5rem',
                        padding: '0.5rem 0.75rem',
                        backgroundColor: '#333',
                        color: '#fff',
                        borderRadius: '8px',
                        fontSize: '0.75rem',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                        zIndex: 1000,
                        pointerEvents: 'none'
                      }}>
                        {badge.description}
                        {/* Tooltip arrow */}
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: 0,
                          height: 0,
                          borderLeft: '6px solid transparent',
                          borderRight: '6px solid transparent',
                          borderTop: '6px solid #333'
                        }} />
                      </div>
                    )}
                  </div>
                ))}
                </div>
              </div>
            </div>
          )}
          
          <hr />

          {/* 🏆 Eternal Cup Ranking – current year */}
          {(eternalCupLoading || (eternalCupRankings && eternalCupRankings.length > 0)) && (
            <div style={{ margin: '0.75rem 0 1.25rem 0' }}>
              <p style={{ margin: '0 0 0.4rem 0', fontSize: '0.8em', fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                🏆 Eternal Cup {new Date().getFullYear()}
              </p>
              {eternalCupLoading ? (
                <p style={{ margin: 0, color: '#999', fontStyle: 'italic', fontSize: '0.85em' }}>⏳ Loading...</p>
              ) : (
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table style={{ borderCollapse: 'collapse', fontSize: '0.85em', width: '100%' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                        <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: '600', color: '#555', fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>Category</th>
                        <th style={{ textAlign: 'center', padding: '4px 8px', fontWeight: '600', color: '#555', fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>Points</th>
                        <th style={{ textAlign: 'center', padding: '4px 8px', fontWeight: '600', color: '#555', fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>Rank</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eternalCupRankings.map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '5px 8px', color: '#333', whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.discipline} – {row.category}</td>
                          <td style={{ textAlign: 'center', padding: '5px 8px', color: '#444' }}>{row.totalPoints}</td>
                          <td style={{ textAlign: 'center', padding: '5px 8px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
                              <span style={{
                                fontWeight: 'bold',
                                color: row.rank === 1 ? '#d4af37' : row.rank <= 3 ? '#cd7f32' : row.rank <= 10 ? '#007bff' : '#333'
                              }}>#{row.rank}</span>
                              {row.totalDancers > 0 && (
                                <span style={{ fontSize: '0.75em', color: '#999' }}>of {row.totalDancers}</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <h3>⭐ Star Ranking (Current &amp; Past)</h3>

          {/* Use cached rankings for immediate display, fallback to computed data */}
          {((cachedRankings?.disciplineOverview && cachedRankings.disciplineOverview.length > 0) || 
            (dancerData?.disciplineOverview && dancerData.disciplineOverview.length > 0)) ? (
            <div className="ranking-overview">
              <div className="table-container" style={{ overflowX: "auto", margin: "0 -10px" }}>
                <table className="ranking-table">
                  <thead>
                    <tr>
                      <th>Discipline & Category</th>
                      <th style={{ textAlign: "center" }}>Active Points</th>
                      <th style={{ textAlign: "center" }}>Current Rank</th>
                      <th style={{ textAlign: "center" }}>Current Days</th>
                      <th style={{ textAlign: "center" }}>Best Rank</th>
                      <th style={{ textAlign: "center" }}>Best Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Use enhanced overview that includes historical categories */}
                    {enhancedOverview.map((overview, i) => (
                      <tr key={i} style={{ 
                        backgroundColor: (overview.totalPoints || overview.activePoints || 0) > 0 ? "white" : "#f9f9f9",
                        opacity: (overview.totalPoints || overview.activePoints || 0) > 0 ? 1 : 0.85
                      }}>
                        <td>
                          <div style={{ lineHeight: "1.3" }}>
                            <strong>{overview.discipline || 'Unknown'}</strong> – {overview.category || 'Unknown'}
                            {overview.discipline === "Latin" && overview.dance && (
                              <span style={{ fontSize: "0.85em", color: "#666" }}> ({overview.dance})</span>
                            )}
                            {(overview.totalPoints || overview.activePoints || 0) === 0 && (
                              <div style={{ color: "#888", fontSize: "0.75em", marginTop: "2px" }}>Historical Only</div>
                            )}
                          </div>
                        </td>
                        <td style={{ textAlign: "center", fontWeight: "bold" }}>
                          {(overview.activePoints || overview.totalPoints || 0) > 0 ? (overview.activePoints || overview.totalPoints || 0) : 0}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {(overview.activePoints || overview.totalPoints || 0) > 0 ? (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "4px", justifyContent: "center" }}>
                                <div style={{
                                  fontSize: "1.1em",
                                  fontWeight: "bold",
                                  color: overview.currentRank <= 3 ? "#d4af37" : overview.currentRank <= 10 ? "#007bff" : "#333",
                                  textShadow: overview.currentRank <= 3 ? "0 1px 2px rgba(0,0,0,0.3)" : "none"
                                }}>
                                  #{overview.currentRank}
                                </div>
                              </div>
                              {overview.totalDancers && overview.totalDancers > 0 && (
                                <div style={{ 
                                  fontSize: "0.7em", 
                                  color: "#666", 
                                  backgroundColor: "#f8f9fa",
                                  padding: "1px 3px",
                                  borderRadius: "2px",
                                  border: "1px solid #e9ecef"
                                }}>
                                  of {overview.totalDancers}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: "#999" }}>-</span>
                          )}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {(overview.activePoints || overview.totalPoints || 0) > 0 && (overview.daysHeld || overview.currentDaysHeld || 0) > 0 ? `${overview.daysHeld || overview.currentDaysHeld || 0} days` : '-'}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {overview.bestRank ? `#${overview.bestRank}` : '-'}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {/* Show best days if: admin OR (profile claimed AND setting allows public view) */}
                          {isAdmin || (isProfileClaimed && !hideBestDaysFromPublic) ? (
                            (overview.bestDaysHeld || 0) > 0 ? `${overview.bestDaysHeld} days` : '-'
                          ) : (
                            <span style={{ 
                              color: "#666", 
                              fontSize: "0.75em", 
                              fontStyle: "italic",
                              border: "1px solid #ddd",
                              padding: "2px 4px",
                              borderRadius: "3px",
                              backgroundColor: "#f8f9fa"
                            }}>
                              Verified Only
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Beta Warning Message */}
              <div style={{
                marginTop: "1rem",
                padding: "0.5rem",
                textAlign: "center"
              }}>
                <p style={{
                  color: "#dc3545",
                  fontWeight: "bold",
                  fontSize: "0.95em",
                  margin: 0
                }}>
                  This application is in beta version - this table may have invalid data.
                </p>
              </div>

              <div style={{ marginTop: "1rem" }}>
                <h3 style={{ margin: "0 0 0.5rem 0" }}>🏆 Ranking Overview - All Historical &amp; Active Disciplines &amp; Categories</h3>
                <p style={{ fontSize: "0.9em", color: "#666", margin: 0 }}>
                  <strong>Display:</strong> Shows all disciplines &amp; categories where this dancer has competed (both active and historical).<br/>
                  <strong>Points:</strong> Calculated from best 4 results in last 365 days (since {new Date(Date.now() - 365*24*60*60*1000).toLocaleDateString()}).<br/>
                  <strong>Current Rank:</strong> The current place the dancer holds • <strong>Current Days:</strong> How many days the place is held<br/>
                  <strong>Best Rank:</strong> Highest position ever achieved • <strong>Best Days:</strong> How many days the highest position was held
                  {hideBestDaysFromPublic && !isAdmin && !isProfileClaimed && <span style={{color: "#e74c3c"}}> (verified profiles only)</span>}
                </p>
              </div>
            </div>
          ) : null}
          
          {/* Loading state for rankings */}
          {loading && (
            <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
              <p style={{ fontStyle: "italic" }}>⏳ Loading Ranks...</p>
            </div>
          )}
          
        </div>
      )}

      {activeTab === "competitions" && (
        <div className="competitions-tab">
          {/* Filter Controls */}
          <div className="filter-controls" style={{ 
            marginBottom: "1rem", 
            padding: "1rem", 
            backgroundColor: "#f5f5f5", 
            borderRadius: "5px",
            border: "1px solid #ddd"
          }}>
            <h4 style={{ margin: "0 0 0.5rem 0" }}>🎯 Competition Filters</h4>
            <p style={{ fontSize: "0.9em", color: "#666", margin: "0 0 0.75rem 0" }}>
              Filter results by discipline, age category, and dance. Green tick (✅) shows top 6 results for ranking.
            </p>
            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              <label style={{ display: "flex", flexDirection: "column", minWidth: "150px" }}>
                <strong>Discipline:</strong>
                <select 
                  value={selectedDiscipline} 
                  onChange={(e) => {
                    setSelectedDiscipline(e.target.value);
                    // Clear age category and dance filters when discipline changes
                    setSelectedAgeCategory("");
                    if (e.target.value !== 'Latin') {
                      setSelectedDance("");
                    }
                  }}
                  style={{ padding: "0.25rem", marginTop: "0.25rem" }}
                >
                  <option value="">All Disciplines</option>
                  {(dancerData.disciplines || []).map((d, i) => (
                    <option key={i} value={d}>{d}</option>
                  ))}
                </select>
              </label>
              <label style={{ display: "flex", flexDirection: "column", minWidth: "150px" }}>
                <strong>Age Category:</strong>
                <select 
                  value={selectedAgeCategory} 
                  onChange={(e) => setSelectedAgeCategory(e.target.value)}
                  style={{ padding: "0.25rem", marginTop: "0.25rem" }}
                >
                  <option value="">All Age Categories</option>
                  {availableAgeCategories.map((category, i) => {
                    // Check if dancer has active results (within 365 days) in this category
                    const hasActiveResults = allResults && allResults.some(r => 
                      r.category === category && r.countsForRanking
                    );
                    return (
                      <option 
                        key={i} 
                        value={category}
                        style={{ 
                          backgroundColor: hasActiveResults ? "#e8f5e8" : "#ffebee",
                          color: hasActiveResults ? "#2e7d32" : "#d32f2f",
                          fontWeight: "bold"
                        }}
                      >
                        {category}
                      </option>
                    );
                  })}
                </select>
              </label>
              {selectedDiscipline === 'Latin' && (
                <label style={{ display: "flex", flexDirection: "column", minWidth: "150px" }}>
                  <strong>Dance:</strong>
                  <select 
                    value={selectedDance} 
                    onChange={(e) => setSelectedDance(e.target.value)}
                    style={{ padding: "0.25rem", marginTop: "0.25rem" }}
                  >
                    <option value="">All Latin Dances</option>
                    {availableDances.map((dance, i) => (
                      <option key={i} value={dance}>{dance}</option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          </div>

          {/* 🏆 Eternal Cup Competitions toggle */}
          <div style={{ marginBottom: "1rem" }}>
            <button
              onClick={() => setShowEternalCupTable(prev => !prev)}
              style={{
                padding: "0.5rem 1rem",
                backgroundColor: showEternalCupTable ? "#d4af37" : "#f5f5f5",
                color: showEternalCupTable ? "#fff" : "#333",
                border: `1px solid ${showEternalCupTable ? "#c9a227" : "#ccc"}`,
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: "600",
                fontSize: "0.9em",
                display: "flex",
                alignItems: "center",
                gap: "0.4rem"
              }}
            >
              🏆 Eternal Cup Competitions {showEternalCupTable ? "▲" : "▼"}
            </button>

            {showEternalCupTable && (
              <div style={{
                marginTop: "0.75rem",
                padding: "1rem",
                backgroundColor: "#fffdf0",
                border: "1px solid #e6d97a",
                borderRadius: "6px"
              }}>
                {/* Year selector */}
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
                  <strong style={{ fontSize: "0.9em" }}>Year:</strong>
                  {ecAvailableYears.map(yr => (
                    <button
                      key={yr}
                      onClick={() => setEcSelectedYear(yr)}
                      style={{
                        padding: "0.25rem 0.65rem",
                        backgroundColor: ecSelectedYear === yr ? "#d4af37" : "#f0f0f0",
                        color: ecSelectedYear === yr ? "#fff" : "#333",
                        border: `1px solid ${ecSelectedYear === yr ? "#c9a227" : "#ccc"}`,
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontWeight: ecSelectedYear === yr ? "bold" : "normal",
                        fontSize: "0.85em"
                      }}
                    >
                      {yr}{yr === new Date().getFullYear() ? " (current)" : ""}
                    </button>
                  ))}
                </div>

                {/* Age category filter – only shown when dancer has multiple categories in the selected year */}
                {ecAvailableCategories.length > 1 && (
                  <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.85rem", flexWrap: "wrap" }}>
                    <strong style={{ fontSize: "0.9em" }}>Category:</strong>
                    <button
                      onClick={() => setEcSelectedCategory("")}
                      style={{
                        padding: "0.2rem 0.6rem",
                        backgroundColor: !ecSelectedCategory ? "#555" : "#f0f0f0",
                        color: !ecSelectedCategory ? "#fff" : "#333",
                        border: `1px solid ${!ecSelectedCategory ? "#444" : "#ccc"}`,
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "0.82em"
                      }}
                    >
                      All
                    </button>
                    {ecAvailableCategories.map(cat => (
                      <button
                        key={cat}
                        onClick={() => setEcSelectedCategory(cat)}
                        style={{
                          padding: "0.2rem 0.6rem",
                          backgroundColor: ecSelectedCategory === cat ? "#555" : "#f0f0f0",
                          color: ecSelectedCategory === cat ? "#fff" : "#333",
                          border: `1px solid ${ecSelectedCategory === cat ? "#444" : "#ccc"}`,
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "0.82em"
                        }}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                )}

                {eternalCupBestResults.length === 0 ? (
                  <p style={{ color: "#888", fontStyle: "italic", margin: 0, fontSize: "0.9em" }}>
                    No competition results found for {ecSelectedYear}.
                  </p>
                ) : (
                  <>
                    {ecFilteredBestResults.map((group, gi) => {
                      const rankData = ecYearRankings?.find(
                        r => r.discipline === group.discipline && r.category === group.category
                      );
                      return (
                        <div key={gi} style={{ marginBottom: "1.25rem" }}>
                          {/* Group header: discipline – category, total pts, and rank */}
                          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "0.5rem", marginBottom: "0.35rem" }}>
                            <span style={{ fontWeight: "600", fontSize: "0.92em", color: "#333" }}>
                              {group.discipline} – {group.category}
                            </span>
                            <span style={{ color: "#666", fontSize: "0.85em" }}>
                              {group.totalPoints} pts{group.isLatin ? ", all dances combined" : ""}
                            </span>
                            {ecYearRankingsLoading ? (
                              <span style={{ color: "#999", fontSize: "0.8em", fontStyle: "italic" }}>⏳ rank…</span>
                            ) : rankData ? (
                              <span style={{
                                fontWeight: "bold",
                                fontSize: "0.85em",
                                color: rankData.rank === 1 ? "#d4af37" : rankData.rank <= 3 ? "#cd7f32" : rankData.rank <= 10 ? "#007bff" : "#333"
                              }}>
                                #{rankData.rank} <span style={{ fontWeight: "normal", color: "#888" }}>of {rankData.totalDancers}</span>
                              </span>
                            ) : null}
                          </div>

                          {group.isLatin ? (
                            <div>
                              {group.danceBreakdown.map(({ dance, all, top, danceTotal }, di) => (
                                <div key={di} style={{ marginBottom: "0.8rem" }}>
                                  <p style={{ margin: "0 0 0.2rem 0", fontSize: "0.82em", fontWeight: "600", color: "#555" }}>
                                    {dance} — <strong>{danceTotal} pts</strong>
                                    <span style={{ fontWeight: "normal", color: "#888" }}> (top {top.length} of {all.length} result{all.length !== 1 ? "s" : ""})</span>
                                  </p>
                                  <div style={{ overflowX: "auto" }}>
                                    <table style={{ borderCollapse: "collapse", fontSize: "0.82em", width: "100%" }}>
                                      <thead>
                                        <tr style={{ borderBottom: "1px solid #e0e0e0", backgroundColor: "#f9f9f9" }}>
                                          <th style={{ textAlign: "left", padding: "3px 6px", fontWeight: "600", color: "#555", whiteSpace: "nowrap" }}>Competition</th>
                                          <th style={{ textAlign: "center", padding: "3px 6px", fontWeight: "600", color: "#555", whiteSpace: "nowrap" }}>Date</th>
                                          <th style={{ textAlign: "center", padding: "3px 6px", fontWeight: "600", color: "#555", whiteSpace: "nowrap" }}>Points</th>
                                          <th style={{ textAlign: "center", padding: "3px 6px", fontWeight: "600", color: "#555", whiteSpace: "nowrap" }}>EC Top</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {all.map((r, ri) => {
                                          const isCounting = group.countingIds.has(r.compId);
                                          return (
                                            <tr key={ri} style={{ borderBottom: "1px solid #f5f5f5", backgroundColor: isCounting ? "#e8f5e8" : "#ffebee" }}>
                                              <td style={{ padding: "3px 6px", color: "#333", wordBreak: "break-word" }}>{compNameMap[r.compId] || r.compId}</td>
                                              <td style={{ textAlign: "center", padding: "3px 6px", color: "#666", whiteSpace: "nowrap" }}>{formatDate(r.timestamp)}</td>
                                              <td style={{ textAlign: "center", padding: "3px 6px", fontWeight: isCounting ? "bold" : "normal" }}>{r.points}</td>
                                              <td style={{ textAlign: "center", padding: "3px 6px" }}>{isCounting ? "✅" : ""}</td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{ overflowX: "auto" }}>
                              <table style={{ borderCollapse: "collapse", fontSize: "0.82em", width: "100%" }}>
                                <thead>
                                  <tr style={{ borderBottom: "1px solid #e0e0e0", backgroundColor: "#f9f9f9" }}>
                                    <th style={{ textAlign: "left", padding: "3px 6px", fontWeight: "600", color: "#555", whiteSpace: "nowrap" }}>Competition</th>
                                    <th style={{ textAlign: "center", padding: "3px 6px", fontWeight: "600", color: "#555", whiteSpace: "nowrap" }}>Date</th>
                                    <th style={{ textAlign: "center", padding: "3px 6px", fontWeight: "600", color: "#555", whiteSpace: "nowrap" }}>Points</th>
                                    <th style={{ textAlign: "center", padding: "3px 6px", fontWeight: "600", color: "#555", whiteSpace: "nowrap" }}>EC Top</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {group.allResults.map((r, ri) => {
                                    const isCounting = group.countingIds.has(r.compId);
                                    return (
                                      <tr key={ri} style={{ borderBottom: "1px solid #f5f5f5", backgroundColor: isCounting ? "#e8f5e8" : "#ffebee" }}>
                                        <td style={{ padding: "3px 6px", color: "#333", wordBreak: "break-word" }}>{compNameMap[r.compId] || r.compId}</td>
                                        <td style={{ textAlign: "center", padding: "3px 6px", color: "#666", whiteSpace: "nowrap" }}>{formatDate(r.timestamp)}</td>
                                        <td style={{ textAlign: "center", padding: "3px 6px", fontWeight: isCounting ? "bold" : "normal" }}>{r.points}</td>
                                        <td style={{ textAlign: "center", padding: "3px 6px" }}>{isCounting ? "✅" : ""}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    <p style={{ fontSize: "0.78em", color: "#888", margin: "0.25rem 0 0 0" }}>
                      <span style={{ backgroundColor: "#e8f5e8", padding: "1px 4px", borderRadius: "2px" }}>Green</span> = counts for Eternal Cup &nbsp;
                      <span style={{ backgroundColor: "#ffebee", padding: "1px 4px", borderRadius: "2px" }}>Red</span> = not in top {ecSelectedYear >= 2026 ? 6 : 4} for that selection
                    </p>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Loading message for lazy competition results */}
          {dancerData?.needsCompetitionResults && loading && (
            <div style={{ 
              padding: "2rem", 
              textAlign: "center", 
              backgroundColor: "#e3f2fd", 
              borderRadius: "8px", 
              border: "1px solid #2196f3",
              marginBottom: "1rem"
            }}>
              <div style={{ fontSize: "1.2em", marginBottom: "0.5rem" }}>🔄 Loading Competition Results...</div>
              <div style={{ color: "#666" }}>
                Fetching all competition history for this dancer. This may take a moment.
              </div>
            </div>
          )}

          <div className="table-container" style={{ 
            width: "100%", 
            maxWidth: "none", 
            fontSize: "1.0rem"
          }}>
            <p style={{ fontSize: "1.05em", color: "#666", marginBottom: "0.75rem" }}>
              <strong>📊 Results Legend:</strong><br/>
              • <span style={{ color: "#2e7d32", fontWeight: "bold" }}>Green background</span>: Counts for ranking (last 365 days)<br/>
              • <span style={{ color: "#d32f2f", fontWeight: "bold" }}>Red background</span>: Historical result (older than 365 days)<br/>
              • <span style={{ backgroundColor: "#e3f2fd", padding: "2px 4px" }}>Blue highlight</span>: Top 6 results for current filter selection<br/>
              • <strong>Status indicators</strong>: 🟢 Counts for ranking • 🔴 Historical • 📅 Historical (age change) • ❌ Wrong age category • 🚫 Invalid
              {isAdmin && (
                <>
                  <br/>• <strong>Actions column</strong>: Click "✅ Include" to exclude a result from ranking, or "❌ Excluded" to include it back
                </>
              )}
            </p>
            
            {/* Competition Results Table or Empty State */}
            {filteredResults && filteredResults.length > 0 ? (
              <div className="competition-table-container">
                <table className="competition-table" style={{ 
                  width: "100%", 
                  fontSize: "0.95rem"
                }}>
                <thead>
                  <tr>
                    <th className="comp-name-col">Competition</th>
                    <th className="comp-date-col">Date</th>
                    <th className="comp-category-col">Category</th>
                    <th className="comp-points-col">Points</th>
                    <th className="comp-place-col">Place</th>
                    <th className="comp-top6-col">Top 6</th>
                    <th className="comp-status-col">Status</th>
                    {isAdmin && <th className="comp-actions-col">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredResults.map((r, i) => {
                    const isFilteredTop6 = filteredTop6Results.includes(r.compId);
                    const isRanking = r.countsForRanking;
                    let rowClass = "";
                    let backgroundColor = "";
                    
                    if (isFilteredTop6) {
                      rowClass = "highlight-blue";
                    } else if (isRanking) {
                      backgroundColor = "#e8f5e8"; // Light green for ranking results
                    } else {
                      backgroundColor = "#ffebee"; // Light red for historical results
                    }
                    
                    return (
                      <tr key={i} className={rowClass} style={{ backgroundColor: rowClass ? "" : backgroundColor }}>
                        <td className="comp-name-col" style={{ 
                          cursor: "pointer",
                          color: "#007bff",
                          textDecoration: "underline"
                        }} 
                        title={compNameMap[r.compId] || r.compId}
                        onClick={() => handleCompetitionClick(r.compId)}
                        >
                          {compNameMap[r.compId] || r.compId}
                        </td>
                        <td className="comp-date-col" title={formatDate(r.timestamp)}>
                          {formatDate(r.timestamp)}
                        </td>
                        <td className="comp-category-col" title={formatCategoryDisplay(r.category, r.dance, r.discipline)}>
                          {formatCategoryDisplay(r.category, r.dance, r.discipline)}
                        </td>
                        <td className="comp-points-col">{r.points}</td>
                        <td className="comp-place-col">
                          {formatPlaceDisplay(r.place, r.participants, r.withdrawn)}
                        </td>
                        <td className="comp-top6-col">{isFilteredTop6 ? "✅" : ""}</td>
                        <td className="comp-status-col" style={{ fontSize: "0.9em", color: "#666" }}>
                          {r.invalid ? (isAdmin ? "🚫 Invalid" : "🚫 Invalid") :
                           r.ageExcluded ? (isAdmin ? "❌ Wrong Age Category" : "❌ Invalid") :
                           r.isHistoricalDueToAgeChange ? (isAdmin ? "📅 Historical (Age Change)" : "🔴 Historical") :
                           r.autoExcluded && r.isHistorical ? (isAdmin ? "🔶 Historical (Auto)" : "🔴 Historical") :
                           r.autoExcluded ? (isAdmin ? "🔶 Auto-excluded" : "🔴 Excluded") :
                           r.excluded && r.exclusionReason === "manual" ? (isAdmin ? "❌ Excluded (Manual)" : "🔴 Historical") :
                           isRanking ? "🟢 Counts" : "🔴 Historical"}
                        </td>
                        {isAdmin && (
                          <td className="comp-actions-col">
                            {r.autoExcluded ? (
                              <span style={{
                                padding: "0.25rem 0.5rem",
                                fontSize: "0.8rem",
                                color: "#666",
                                backgroundColor: "#f0f0f0",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                display: "inline-block"
                              }}>
                                🔶 Auto-excluded
                              </span>
                            ) : (
                              <div style={{ display: "flex", gap: "0.25rem", flexDirection: "column" }}>
                                <button 
                                  style={{ 
                                    padding: "0.25rem 0.5rem", 
                                    fontSize: "0.8rem",
                                    cursor: "pointer",
                                    border: "1px solid #ccc",
                                    borderRadius: "4px",
                                    backgroundColor: r.excluded ? "#ffcdd2" : "#c8e6c9"
                                  }}
                                  onClick={() => toggleResultExclusion(r, r.excluded)}
                                  title={r.excluded ? "Click to include this result in rankings" : "Click to exclude this result from rankings"}
                                >
                                  {r.excluded ? "🔄 Include" : "❌ Exclude"}
                                </button>
                                <button 
                                  style={{ 
                                    padding: "0.25rem 0.5rem", 
                                    fontSize: "0.8rem",
                                    cursor: "pointer",
                                    border: "1px solid #666",
                                    borderRadius: "4px",
                                    backgroundColor: r.invalid ? "#ffebee" : "#f5f5f5",
                                    color: r.invalid ? "#d32f2f" : "#333"
                                  }}
                                  onClick={() => toggleResultInvalid(r, r.invalid)}
                                  title={r.invalid ? "Mark as valid - will count in all calculations" : "Mark as invalid - will be completely excluded from all calculations"}
                                >
                                  {r.invalid ? "🚫 Invalid" : "✅ Valid"}
                                </button>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="comp-name-col"><strong>Total (Filtered)</strong></td>
                    <td className="comp-date-col"></td>
                    <td className="comp-category-col"></td>
                    <td className="comp-points-col"><strong>{(selectedDiscipline || selectedAgeCategory || selectedDance) ? filteredTop6Results.reduce((sum, compId) => {
                      const result = dancerData.results.find(r => r.compId === compId);
                      return sum + (result ? result.points : 0);
                    }, 0) : 0}</strong></td>
                    <td className="comp-place-col"></td>
                    <td className="comp-top6-col"></td>
                    <td className="comp-status-col"></td>
                    {isAdmin && <td className="comp-actions-col"></td>}
                  </tr>
                </tfoot>
              </table>
              </div>
            ) : (
              /* Empty State - Show when no competition results or still loading */
              <div style={{ 
                padding: "2rem", 
                textAlign: "center", 
                backgroundColor: "#f9f9f9", 
                borderRadius: "8px", 
                border: "1px solid #ddd",
                color: "#666"
              }}>
                {dancerData?.needsCompetitionResults ? (
                  /* Competition results need to be loaded */
                  <div>
                    <div style={{ fontSize: "1.1em", marginBottom: "0.5rem" }}>⏳ Click on this tab to load competition results</div>
                    <div style={{ fontSize: "0.9em" }}>
                      Competition results will be loaded when you access this tab for faster performance.
                    </div>
                  </div>
                ) : (
                  /* No results found after loading */
                  <div>
                    <div style={{ fontSize: "1.1em", marginBottom: "0.5rem" }}>📋 No competition results found</div>
                    <div style={{ fontSize: "0.9em" }}>
                      {(selectedDiscipline || selectedAgeCategory || selectedDance) 
                        ? "Try adjusting the filters above to see more results."
                        : "This dancer may not have competed yet, or results may not be uploaded."}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "claim" && !isProfileClaimed && (
        <div className="claim-tab">
          {user ? (
            <ClaimProfile 
              dancerName={name} 
              onClaimSubmitted={handleClaimSubmitted}
            />
          ) : (
            <LoginPrompt type="dancer profile" />
          )}
        </div>
      )}

      {activeTab === "update" && user && canEditProfile() && (
        <div className="update-tab">
          <SimpleProfileEditor
            dancerName={name}
            onSuccess={handleProfileUpdated}
            canEditSocialMedia={canEditSocialMedia()}
            currentAgeCategory={ageCategory || calculateAgeCategoryFromPriority()}
          />
        </div>
      )}

      {/* Competition Details Modal */}
      {competitionModal.isOpen && (
        <div
          className="cm-modal-overlay"
          style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.6)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 9999, paddingTop: "80px", paddingLeft: "0.5rem", paddingRight: "0.5rem", paddingBottom: "1rem", overflow: "auto" }}
          onClick={(e) => { if (e.target === e.currentTarget) closeCompetitionModal(); }}
        >
          <div
            className="cm-modal-box"
            style={{ backgroundColor: "white", borderRadius: "10px", maxWidth: "900px", width: "100%", maxHeight: "calc(100vh - 100px)", display: "flex", flexDirection: "column", boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }}
          >
            {/* Modal Header - Fixed at top */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0.6rem 0.75rem",
              borderBottom: "2px solid #007aff",
              backgroundColor: "#007aff",
              borderRadius: "10px 10px 0 0",
              flexShrink: 0,
              minHeight: "44px"
            }}>
              <h2 style={{ margin: 0, fontSize: "0.95rem", color: "white", flex: 1, paddingRight: "0.5rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                🏆 {competitionModal.competitionData?.name || competitionModal.competitionId}
              </h2>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  closeCompetitionModal();
                }}
                style={{
                  background: "white",
                  border: "none",
                  fontSize: "1.1rem",
                  cursor: "pointer",
                  color: "#007aff",
                  padding: "0.4rem 0.7rem",
                  borderRadius: "6px",
                  lineHeight: 1,
                  fontWeight: "bold",
                  minWidth: "40px",
                  minHeight: "36px"
                }}
                title="Close"
              >
                ✕
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="cm-modal-body">
              {competitionModal.loading ? (
                <div style={{ textAlign: "center", padding: "1.5rem" }}>
                  <div>Loading...</div>
                </div>
              ) : competitionModal.error ? (
                <div style={{ textAlign: "center", padding: "1.5rem", color: "#dc3545" }}>
                  {competitionModal.error}
                </div>
              ) : competitionModal.competitionData ? (
                (() => {
                  const cd = competitionModal.competitionData;
                  // Derive judgeIds: use stored field, or fall back to order from adjudicators array
                  const jIds = (cd.judgeIds && cd.judgeIds.length > 0)
                    ? cd.judgeIds
                    : (cd.adjudicators || []).map(a => a.id);
                  const dances = (cd.dances && cd.dances.length > 0)
                    ? cd.dances
                    : (cd.dance ? [cd.dance] : []);
                  const hasRoundData = cd.results?.some(r => r.rounds?.length > 0);
                  const mThStyle = { padding: "5px 8px", border: "1px solid #ccc", textAlign: "left", fontWeight: "600", whiteSpace: "nowrap", fontSize: "0.82rem" };
                  const mTdStyle = { padding: "4px 8px", border: "1px solid #e0e0e0", whiteSpace: "nowrap", fontSize: "0.82rem" };
                  const hovJ = cmHoveredJudge;
                  const selJ = cmSelectedJudges;
                  const hasSelJ = selJ.size > 0;
                  // judge highlight helper: selected beats hovered
                  const jStyle = (jId) => {
                    const hov = hovJ === jId;
                    const sel = selJ.has(jId);
                    if (sel && hov) return { bg: "#ffb347", fg: "#111", fw: "bold" };
                    if (sel)        return { bg: "#bbdefb", fg: "#0d47a1", fw: "bold" };
                    if (hov)        return { bg: "#ffe066", fg: "#111",   fw: "bold" };
                    return { bg: "transparent", fg: "inherit", fw: "normal" };
                  };
                  // Compute star count for the info block
                  const _allRoundLabels = new Set();
                  for (const _r of (cd.results || [])) for (const _rd of (_r.rounds || [])) _allRoundLabels.add(_rd.round);
                  const _multipleRounds = _allRoundLabels.size > 1;
                  function _rsk(r) { if (r === "F") return 10000; if (r === "Preliminary") return -1; const f = { "1/32 Final": 10, "1/16 Final": 20, "1/8 Final": 30, "1/4 Final": 40, "1/2 Final": 50 }; if (f[r] != null) return f[r]; const n = parseInt(r); return isNaN(n) ? 5 : n; }
                  const _earliestRound = _multipleRounds ? [..._allRoundLabels].sort((a, b) => _rsk(a) - _rsk(b))[0] : null;
                  const infoStarCount = (_multipleRounds && _earliestRound != null)
                    ? (cd.results || []).filter(r => !(r.rounds || []).some(rd => rd.round === _earliestRound)).length
                    : 0;
                  return (
                    <>
                      {/* Info block */}
                      <div style={{ background: "#f0f8ff", border: "1px solid #b8d4e8", borderRadius: "6px", padding: "0.75rem 1rem", marginBottom: "0.75rem", fontSize: "0.85rem" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.3rem 1.5rem" }}>
                          <div><strong>Competition:</strong> {cd.name}</div>
                          <div><strong>Category:</strong> {cd.category}</div>
                          <div><strong>Discipline:</strong> {cd.discipline}</div>
                          <div><strong>Dance:</strong> {cd.dance}</div>
                          <div><strong>Participants:</strong> {cd.participants}{infoStarCount > 0 ? <span style={{ color: "#e6a000", fontWeight: "600" }}> (★ {infoStarCount})</span> : ""}</div>
                          <div><strong>Date:</strong> {
                            cd.timestamp instanceof Timestamp
                              ? cd.timestamp.toDate().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                              : cd.timestamp?.seconds
                                ? new Date(cd.timestamp.seconds * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                                : "—"
                          }</div>
                          {cd.adjudicators?.length > 0 && (
                            <div style={{ gridColumn: "1 / -1" }}>
                              <strong>Adjudicators:</strong>{" "}
                              <span style={{ display: "inline-flex", flexWrap: "wrap", gap: "6px", marginTop: "4px" }}>
                                {cd.adjudicators.map(a => {
                                  const sel = selJ.has(a.id);
                                  const hov = hovJ === a.id;
                                  return (
                                  <span key={a.id}
                                    onMouseEnter={() => setCmHoveredJudge(a.id)}
                                    onMouseLeave={() => setCmHoveredJudge(null)}
                                    onClick={() => toggleCmJudge(a.id)}
                                    style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "2px 8px", borderRadius: "12px", fontSize: "0.82rem", cursor: "pointer", transition: "all 0.15s", userSelect: "none",
                                      background: sel ? "#d0e8ff" : hov ? "#ffe066" : "#e8eaf6",
                                      border: sel ? "1px solid #2979ff" : hov ? "1px solid #f0c000" : "1px solid #c5cae9",
                                      fontWeight: (sel || hov) ? "700" : "normal",
                                      boxShadow: sel ? "0 0 0 2px #2979ff55" : "none" }}>
                                    <span style={{ fontWeight: "bold", fontSize: "0.75rem", background: sel ? "#2979ff" : hov ? "#f0a000" : "#3a3a5c", color: "#fff", borderRadius: "8px", padding: "0 5px", minWidth: "18px", textAlign: "center", display: "inline-block", transition: "background 0.15s" }}>{a.id}</span>
                                    {a.name}{a.country ? ` (${a.country})` : ""}
                                  </span>
                                  );
                                })}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Results table */}
                      {hasSelJ && (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "0.4rem", flexWrap: "wrap" }}>
                          <span style={{ fontSize: "0.82rem", color: "#0d47a1" }}>
                            <strong>{selJ.size}</strong> judge{selJ.size > 1 ? "s" : ""} selected:
                            {[...selJ].map(jId => {
                              const adj = cd.adjudicators?.find(a => a.id === jId);
                              return (
                                <span key={jId} onClick={() => toggleCmJudge(jId)}
                                  style={{ display: "inline-flex", alignItems: "center", gap: "3px", marginLeft: "5px", background: "#bbdefb", border: "1px solid #2979ff", borderRadius: "10px", padding: "1px 7px", cursor: "pointer", fontSize: "0.78rem", fontWeight: "bold" }}>
                                  <span style={{ background: "#2979ff", color: "#fff", borderRadius: "6px", padding: "0 4px", fontSize: "0.72rem" }}>{jId}</span>
                                  {adj?.name || jId} ✕
                                </span>
                              );
                            })}
                          </span>
                          <button onClick={() => setCmSelectedJudges(new Set())}
                            style={{ background: "none", border: "1px solid #2979ff", color: "#2979ff", borderRadius: "8px", padding: "1px 8px", cursor: "pointer", fontSize: "0.78rem", fontWeight: "bold" }}>✕ Clear all</button>
                        </div>
                      )}
                      <div className="cm-table-wrap">
                        {hasRoundData ? (
                          <table className="cm-results-table" style={{ width: "max-content", minWidth: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                            <thead>
                              <tr style={{ background: "#e8e8e8" }}>
                                <th style={mThStyle} rowSpan={2}>Rank</th>
                                <th style={mThStyle} rowSpan={2}>No.</th>
                                <th style={mThStyle} rowSpan={2}>Name</th>
                                <th style={mThStyle} rowSpan={2}>Studio</th>
                                <th style={mThStyle} rowSpan={2}>Rnd.</th>
                                {dances.map(d => (
                                  <th key={d} colSpan={jIds.length + 1} style={{ ...mThStyle, background: "#3a3a5c", color: "#fff", textAlign: "center" }}>
                                    {d}
                                  </th>
                                ))}
                                <th style={mThStyle} rowSpan={2}>Points</th>
                              </tr>
                              <tr style={{ background: "#3a3a5c" }}>
                                {dances.map(d => (
                                  <React.Fragment key={d}>
                                    {jIds.map(jId => {
                                      const adj = cd.adjudicators?.find(a => a.id === jId);
                                      return (
                                        <th key={jId}
                                          title={adj ? `${adj.id}. ${adj.name}${adj.country ? ` (${adj.country})` : ""}` : jId}
                                          onMouseEnter={() => setCmHoveredJudge(jId)}
                                          onMouseLeave={() => setCmHoveredJudge(null)}
                                          onClick={() => toggleCmJudge(jId)}
                                          style={{ ...mThStyle, textAlign: "center", cursor: "pointer", padding: "3px 10px", minWidth: "2.5em", userSelect: "none", transition: "all 0.1s",
                                            background: selJ.has(jId) ? "rgba(41,121,255,0.5)" : hovJ === jId ? "rgba(255,224,102,0.25)" : "#3a3a5c",
                                            color: selJ.has(jId) ? "#7ec8ff" : hovJ === jId ? "#ffe066" : "#fff",
                                            fontWeight: selJ.has(jId) ? "bold" : "600" }}>
                                          {jId}
                                        </th>
                                      );
                                    })}
                                    <th style={{ ...mThStyle, background: "#2a2a4c", color: "#fff", textAlign: "center", padding: "3px 10px" }}>∑</th>
                                  </React.Fragment>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                // Determine the "first round" of the competition (lowest round label present across ALL dancers)
                                // Round order: numeric rounds first (1 < 2 ...), then fractions (1/32 < 1/16 < 1/8 < 1/4 < 1/2), then "F"
                                function roundSortKey(r) {
                                  if (r === "F") return 10000;
                                  if (r === "Preliminary") return -1;
                                  const frac = { "1/32 Final": 10, "1/16 Final": 20, "1/8 Final": 30, "1/4 Final": 40, "1/2 Final": 50 };
                                  if (frac[r] != null) return frac[r];
                                  const n = parseInt(r);
                                  return isNaN(n) ? 5 : n;
                                }
                                const allRoundLabels = new Set();
                                for (const r of (cd.results || [])) {
                                  for (const rd of (r.rounds || [])) allRoundLabels.add(rd.round);
                                }
                                const multipleRounds = allRoundLabels.size > 1;
                                // The earliest (lowest sort key) round label across all dancers
                                const earliestRound = multipleRounds
                                  ? [...allRoundLabels].sort((a, b) => roundSortKey(a) - roundSortKey(b))[0]
                                  : null;
                                // Which dancers are "stars" — they don't have the earliest round
                                const starDancers = new Set();
                                if (multipleRounds && earliestRound != null) {
                                  for (const r of (cd.results || [])) {
                                    const hasEarliest = (r.rounds || []).some(rd => rd.round === earliestRound);
                                    if (!hasEarliest) starDancers.add(r.dancerName);
                                  }
                                }
                                const hasAnyStars = starDancers.size > 0;

                                return (cd.results || []).map((r, idx) => {
                                  const bgBase = idx % 2 === 0 ? "#fff" : "#fafafa";
                                  const isCurrentDancer = r.dancerName === name;
                                  const isStar = hasAnyStars && starDancers.has(r.dancerName);
                                  // Sort rounds: Final first, then 1/2 Final, 1/4 Final, 1/8 Final, ... (descending sort key)
                                  // Filter out spurious Final rows: a Final with no judge data when the dancer
                                  // also has preliminary round(s) means they were eliminated before the Final.
                                  const hasPrelimRound = (r.rounds || []).some(rd => {
                                    const dd = rd.danceData && Object.values(rd.danceData)[0];
                                    return dd && dd.type !== "final";
                                  });
                                  const rounds = [...(r.rounds || [])]
                                    .filter(rd => {
                                      const dd = rd.danceData && Object.values(rd.danceData)[0];
                                      if (!dd || dd.type !== "final") return true;
                                      const hasJudgeData =
                                        Object.values(dd.judgeMarks || {}).some(v => v != null) ||
                                        Object.values(dd.judgeRecalls || {}).some(v => v);
                                      return hasJudgeData || !hasPrelimRound;
                                    })
                                    .sort((a, b) => roundSortKey(b.round) - roundSortKey(a.round));
                                  // Star dancers get a synthetic earliest-round row appended at the bottom
                                  const syntheticStarRow = (isStar && earliestRound)
                                    ? [{ round: earliestRound, rank: null, danceData: null, _isSyntheticStar: true }]
                                    : [];
                                  const allRows = [...rounds, ...syntheticStarRow];
                                  if (allRows.length === 0) return null;
                                  return allRows.map((rd, rdIdx) => {
                                    const isFirstRow = rdIdx === 0;
                                    const isFinalRound = rd.round === "F";
                                    const isStarRow = rd._isSyntheticStar === true;
                                    const rowBg = isCurrentDancer
                                      ? (isFinalRound ? "#fff0b3" : "#fffbe6")
                                      : (isFinalRound ? "#f0fff0" : bgBase);
                                    const starRowBg = isCurrentDancer ? "#fffbe6" : bgBase;
                                    if (isStarRow) {
                                      return (
                                        <tr key={`${idx}-star`} style={{ background: starRowBg }}>
                                          <td style={{ ...mTdStyle, fontWeight: "normal", color: "#888", background: starRowBg }}>
                                            {earliestRound === "F" ? "Final" : earliestRound}
                                          </td>
                                          {dances.map(d => (
                                            <React.Fragment key={d}>
                                              {jIds.map(jId => {
                                                const { bg } = jStyle(jId);
                                                return (
                                                  <td key={jId}
                                                    onMouseEnter={() => setCmHoveredJudge(jId)}
                                                    onMouseLeave={() => setCmHoveredJudge(null)}
                                                    onClick={() => toggleCmJudge(jId)}
                                                    title={(cd.adjudicators || []).find(a => a.id === jId)?.name || jId}
                                                    style={{ ...mTdStyle, textAlign: "center", cursor: "pointer", fontSize: "0.85rem",
                                                      background: bg !== "transparent" ? bg : starRowBg,
                                                      color: selJ.has(jId) ? "#c47000" : hovJ === jId ? "#111" : "#e6a000",
                                                      fontWeight: "bold" }}>
                                                    ★
                                                  </td>
                                                );
                                              })}
                                              <td style={{ ...mTdStyle, color: "#bbb", background: starRowBg, textAlign: "center" }}>—</td>
                                            </React.Fragment>
                                          ))}
                                        </tr>
                                      );
                                    }
                                    return (
                                      <tr key={`${idx}-${rdIdx}`} style={{ background: rowBg, borderTop: isFirstRow ? "2px solid #ccc" : undefined }}>
                                        {isFirstRow && (
                                          <>
                                            <td style={{ ...mTdStyle, fontWeight: "bold" }} rowSpan={allRows.length}>{r.place}</td>
                                            <td style={mTdStyle} rowSpan={allRows.length}>{r.number ?? ""}</td>
                                            <td style={{ ...mTdStyle, whiteSpace: "normal", minWidth: "100px" }} rowSpan={allRows.length}>
                                              <span style={{ cursor: isCurrentDancer ? "default" : "pointer", color: isCurrentDancer ? "#333" : "#007bff", fontWeight: isCurrentDancer ? "700" : "normal", textDecoration: isCurrentDancer ? "none" : "underline" }}
                                                onClick={() => { if (!isCurrentDancer) { closeCompetitionModal(); navigate(`/dancer/${encodeURIComponent(r.dancerName)}`); } }}>
                                                {r.dancerName}
                                              </span>
                                              {isStar && <span title="Seeded — skipped opening round" style={{ marginLeft: "4px", fontSize: "0.7rem", color: "#e6a000" }}>★</span>}
                                            </td>
                                            <td style={{ ...mTdStyle, fontSize: "0.75rem", color: "#555" }} rowSpan={allRows.length}>{r.studio || ""}</td>
                                          </>
                                        )}
                                        <td style={{ ...mTdStyle, fontWeight: isFinalRound ? "bold" : "normal", color: isFinalRound ? "#1a7a1a" : "#555", background: rowBg }}>
                                          {rd.round === "F" ? "Final" : rd.round}
                                        </td>
                                        {dances.map(d => {
                                          // Try exact dance key, then fallback to first available key in danceData
                                          const dd = rd.danceData?.[d] || (rd.danceData && Object.values(rd.danceData)[0]) || null;
                                          if (!dd) return (
                                            <React.Fragment key={d}>
                                              {jIds.map(jId => <td key={jId} style={{ ...mTdStyle, color: "#bbb", background: rowBg, textAlign: "center" }}>—</td>)}
                                              <td style={{ ...mTdStyle, color: "#bbb", background: rowBg, textAlign: "center" }}>—</td>
                                            </React.Fragment>
                                          );
                                          const isFinalType = dd.type === "final";
                                          const isSkatingFinal = isFinalType && (dd.isSkating || (dd.skatingCols && Object.keys(dd.skatingCols).length > 0));
                                          const _jmValues = isFinalType && !isSkatingFinal && dd.judgeMarks ? Object.values(dd.judgeMarks).filter(v => v != null) : [];
                                          const _computedSum = _jmValues.length > 0 ? _jmValues.reduce((a, b) => a + b, 0) : null;
                                          const sumDisplay = isSkatingFinal
                                            ? ""
                                            : isFinalType
                                              ? (dd.sum != null ? String(dd.sum) : _computedSum != null ? String(_computedSum) : "")
                                              : (dd.totalCrosses != null ? String(dd.totalCrosses) : "0");
                                          return (
                                            <React.Fragment key={d}>
                                              {jIds.map(jId => {
                                                let mark;
                                                if (isFinalType) {
                                                  const m = dd.judgeMarks?.[jId];
                                                  mark = m != null ? String(m) : "\u00b7";
                                                } else {
                                                  const rc = dd.judgeRecalls?.[jId];
                                                  mark = rc === true ? "x" : "\u00b7";
                                                }
                                                const { bg, fg, fw } = jStyle(jId);
                                                return (
                                                  <td key={jId}
                                                    onMouseEnter={() => setCmHoveredJudge(jId)}
                                                    onMouseLeave={() => setCmHoveredJudge(null)}
                                                    onClick={() => toggleCmJudge(jId)}
                                                    title={(cd.adjudicators || []).find(a => a.id === jId)?.name || jId}
                                                    style={{ ...mTdStyle, textAlign: "center", cursor: "pointer", fontFamily: "monospace",
                                                      background: bg !== "transparent" ? bg : rowBg,
                                                      color: fg !== "inherit" ? fg : "inherit",
                                                      fontWeight: fw }}>
                                                    {mark}
                                                  </td>
                                                );
                                              })}
                                              <td style={{ ...mTdStyle, fontFamily: "monospace", fontWeight: "bold", background: rowBg, textAlign: "center" }}>
                                                {sumDisplay}
                                              </td>
                                            </React.Fragment>
                                          );
                                        })}
                                        {isFirstRow && (
                                          <td style={{ ...mTdStyle, fontFamily: "monospace", fontWeight: "600", color: "#28a745" }} rowSpan={allRows.length}>
                                            {Math.round((r.points || 0) * (cd.multiplier || 1))}
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  });
                                });
                              })()}
                            </tbody>
                          </table>
                        ) : (
                          /* Fallback for non-Contaste competitions (no round data) */
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                            <thead>
                              <tr style={{ background: "#e8e8e8" }}>
                                <th style={mThStyle}>Rank</th>
                                <th style={mThStyle}>Dancer</th>
                                <th style={mThStyle}>Studio</th>
                                <th style={mThStyle}>Points</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(cd.results || []).map((r, idx) => {
                                const isCurrentDancer = r.dancerName === name;
                                return (
                                  <tr key={idx} style={{ background: isCurrentDancer ? "#fff3cd" : idx % 2 === 0 ? "#fff" : "#fafafa", borderBottom: "1px solid #eee", fontWeight: isCurrentDancer ? "600" : "normal" }}>
                                    <td style={{ ...mTdStyle, fontWeight: "bold", textAlign: "center" }}>{r.place}</td>
                                    <td style={mTdStyle}>
                                      <span style={{ cursor: isCurrentDancer ? "default" : "pointer", color: isCurrentDancer ? "#333" : "#007bff", textDecoration: isCurrentDancer ? "none" : "underline" }}
                                        onClick={() => { if (!isCurrentDancer) { closeCompetitionModal(); navigate(`/dancer/${encodeURIComponent(r.dancerName)}`); } }}>
                                        {r.dancerName}
                                      </span>
                                    </td>
                                    <td style={{ ...mTdStyle, color: "#666" }}>{r.studio || "—"}</td>
                                    <td style={{ ...mTdStyle, fontFamily: "monospace", fontWeight: "600", color: "#28a745", textAlign: "center" }}>
                                      {Math.round((r.points || 0) * (cd.multiplier || 1))}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>

                      {/* View Full Page button */}
                      <div style={{ textAlign: "center" }}>
                        <button
                          onClick={() => { closeCompetitionModal(); navigate(`/competition/${encodeURIComponent(competitionModal.competitionId)}`); }}
                          style={{ backgroundColor: "#007aff", color: "white", border: "none", borderRadius: "6px", padding: "0.5rem 1rem", cursor: "pointer", fontSize: "0.82rem" }}
                        >
                          View Full Page →
                        </button>
                      </div>
                    </>
                  );
                })()
              ) : null}
            </div>
          </div>
        </div>
      )}


      {/* Share Result Modal */}
      <ShareResultModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        recentResults={recentShareableResults}
        dancerName={name}
        canGenerate={isAdmin || (isProfileClaimed && claimedBy?.userId === user?.uid)}
        profilePictureUrl={profilePictureUrl}
        rankingOverview={dancerData?.disciplineOverview || []}
      />
    </div>
  );
}
